import * as client_state from './alan_api';
import * as application from '../../libraries/gui_definition_lib/libraries/model_lib/types/application/alan_api';
import * as client_bindings from '../../libraries/gui_definition_lib/libraries/client_lib/types/client_bindings/alan_api';
import * as gui_model from '../../libraries/gui_definition_lib/types/gui_model/alan_api';
import * as parameters from '../../libraries/gui_definition_lib/types/parameters/alan_api';
import * as settings from '../../libraries/gui_definition_lib/types/settings/alan_api';
import * as translations from '../../libraries/gui_definition_lib/types/translations/alan_api';
import * as views from '../../libraries/gui_definition_lib/types/views/alan_api';
import * as widget from '../../libraries/gui_definition_lib/types/widget/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};

type Mutable<T> = { -readonly[P in keyof T]: T[P] };
export type Subscription = {
	unsubscribe:() => void;
}

export type DestructionObserver = () => void;
export interface IDestructable {
	readonly destroyed: boolean;
	destroy(): void;
	onDestroy(observer: DestructionObserver): Subscription;
}
export interface ValueObserver {
	on_update: (value:string|number|undefined, subscription: Subscription) => void;
}
export interface TextObserver extends ValueObserver {
	on_update: (value:string, subscription: Subscription) => void;
}
export interface NumberObserver extends ValueObserver {
	on_update: (value:number, subscription: Subscription) => void;
}
export interface StateObserver extends ValueObserver {
	on_update: (value:undefined, subscription: Subscription) => void;
}
export interface DictionaryObserver<T> {
	on_add?: (entry:T) => void,
	on_remove?: (key:string) => void
}
export interface SetObserver<T> {
	on_add?: (entry:T) => void,
	on_remove?: () => void
}
type RefCountable = {
	__ref_count: number;
	__ref_set: Set<any>;
};
function isRefCountable(obj:any): RefCountable|undefined {
	if(obj instanceof AlanNode &&
		obj.entity instanceof AlanDictionaryEntry &&
		obj.entity["__ref_count"] !== undefined) {
		return obj.entity
	}
	return undefined;}

function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	return (detach = false) => {
		return callback(detach);
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	return (detach = false) => {
		return callback(detach);
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject implements IDestructable {
	public abstract get path(): string;
	private destruction_observers: Set<() => void> | undefined = undefined;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
	public destroy(): void {
		this._destroyed = true;
		if (this.destruction_observers) {
			for (const observer of this.destruction_observers) {
				(<any>this).root?.defer_destruction(() => observer());
			}
			this.destruction_observers.clear();
		}
	}
	onDestroy(observer: DestructionObserver): Subscription {
		if (this.destroyed) {
			observer();
		} else {
			if (!this.destruction_observers) {
				this.destruction_observers = new Set();
			}
			this.destruction_observers.add(observer);
		}
		return {
			unsubscribe: () => this.destruction_observers?.delete(observer),
		};
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	private referenced_entry:T|undefined;
	private counted_entry:RefCountable|undefined;
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { 
		super();
	}
	public get ref() {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.referenced_entry !== undefined) { return this.referenced_entry; }
		this.referenced_entry = this.resolve();
		this.counted_entry = isRefCountable(this.referenced_entry)
		if (this.counted_entry !== undefined) {
			this.counted_entry.__ref_count += 1
			if (debug) {
				if (this.counted_entry.__ref_set === undefined) { this.counted_entry.__ref_set = new Set<any>(); }
				this.counted_entry.__ref_set.add(this);
			}
		}
		return this.referenced_entry
	}
	public unlink(): void {
		if (this.counted_entry !== undefined) {
			this.counted_entry.__ref_count -= 1;
			this.counted_entry.__ref_set?.delete(this);
			this.counted_entry = undefined;
		}
		this.referenced_entry = undefined;
	}
	public destroy(): void {
		super.destroy();
		this.unlink();
	}

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries: Map<string, T['node']>;
	protected observers:Set<DictionaryObserver<T['node']>> = new Set();

	constructor (entries:{[key:string]:T['init']},	protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, this.initialize(parent, entry_key, entries[entry_key])]));
	}

	protected abstract initialize(parent:P, key:string, obj:T['init']): T['node'];
	public destroy(): void {
		super.destroy();
		for (const entry of this._entries.values()) {
			entry.destroy();
		}
	}

	get size() { return this._entries.size; }
	[Symbol.iterator]():IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	entries(): IterableIterator<[string,T['node']]> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.entries();
	}
	values(): IterableIterator<T['node']> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.values();
	}
	keys(): IterableIterator<string> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return Array.from(this.entries());
	}
	map<RT>(callback:(value:T['node']) => RT): Record<string, RT> {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node']|undefined {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		return this._entries.get(key);
	}
	has(key:string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key:string, onExists:(($: T['node']) => RT)|Exclude<RT, Function>, onNotExists:(() => RT)|Exclude<RT, Function>): RT {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry) : onExists;
		}
	}
	create(key:string, init:T['init']): T['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this._entries.has(key)) return this._entries.get(key)!;
		const entry = this.initialize(this.parent, key, init);
		this._entries.set(key, entry);
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_add?.(entry)), this);
		return entry;
	}

	delete(key:string): boolean {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const entry = this._entries.get(key);
		if (entry === undefined) return false;
		this._entries.delete(key);
		this.parent.root.transact(() => entry.destroy());
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_remove?.(key)), this);
		return true;
	}
	subscribe(observer:DictionaryObserver<T['node']>): Subscription {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		this.observers.add(observer);
		const subscription:Subscription = {
			unsubscribe: () => this.observers.delete(observer)
		};
		return subscription;
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	private observers:Set<SetObserver<T['node']>> = new Set();
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	public destroy():void {
		super.destroy();
		this._entries.forEach(entry => entry.destroy());
	}

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
	create(init:T['init']): T['node'] {
		const entry = this.initialize(this.parent, init);
		this._entries.add(entry);
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_add?.(entry)), this);
		return entry;
	}
	delete(entry:T['node']):boolean {
		if (!this._entries.has(entry)) return false;
		this._entries.delete(entry);
		this.parent.root.transact(() => entry.destroy());
		this.parent.root.defer(() => this.observers.forEach(obs => obs.on_remove?.()), this);
		return true;
	}
	subscribe(observer:SetObserver<T['node']>):Subscription {
		const $this = this;
		$this.observers.add(observer);
		const subscription:Subscription = {
			unsubscribe: () => $this.observers.delete(observer)
		};
		return subscription;
	}
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cclient_state
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}

	set<S extends T['name']>(state_name:S, init:Extract<T, {name:S}>['init']): Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const parent = this.state.node.parent;
		const current_state = this.state;
		this.state = {
			name: state_name as T['name'],
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
		this.root.transact(() => current_state.node.destroy());
		(this.parent as any).notify(this.name);
		return this.state.node;
	}
	ensure<S extends T['name']>(state_name:S, init:Extract<T, {name:S}>['init']): Extract<T, {name:S}>['node'] {
		if (this.state.name !== state_name) {
			this.set(state_name, init);
		}
		return this.state.node;
	}
	public subscribe(observer: StateObserver): Subscription {
		const subscription = (this.parent as any)._subscribe_property(this.name, observer) as Subscription;
		observer.on_update(undefined, subscription);
		return subscription;
	}
	public destroy(): void {
		super.destroy();
		this.state.node.destroy();
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cclient_state|undefined;
	public abstract get root():Cclient_state;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
	private property_observers: Map<string, Set<[ValueObserver, Subscription]>> | undefined = undefined;
	protected _subscribe_property(key:string, obs:ValueObserver):Subscription {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.property_observers === undefined) {
			this.property_observers = new Map()
		}
		if (!this.property_observers.has(key)) {
			this.property_observers.set(key, new Set());
		}

		const subscription = {
			unsubscribe: () => this.property_observers?.get(key)?.delete(property_observer)
		};
		const property_observer:[ValueObserver, Subscription] = [obs, subscription];
		this.property_observers.get(key)!.add(property_observer);
		return subscription;
	}
	protected notify(key:string, value?:(string|number)) {
		const prop = this.property_observers?.get(key);
		if (prop !== undefined) {
			for (const [observer, subscription] of prop) {
				this.root.defer(() => observer.on_update?.(value, subscription), this);
			}
		}
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
	public __ref_count:number = 0;
	public __ref_set:Set<any>;public abstract parent:AlanNode;
	public destroy(): void {
		super.destroy();
		this.parent.root.defer(() => {
			if (this.__ref_count > 0) {
				const refs = (debug) ? ' ' + [...this.__ref_set].map(ref => ref.path).join('\n') : ''
				throw new Error(`Entry is still referenced! refcount: ${this.__ref_count} ${this.path}${refs}`);
			}
		})
	}
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
type Vwhere_rules_definer = { name: 'state', definition: client_state.Cstates__state_group__type__property}|{ name: 'reference', definition: client_state.Cyes__navigable}
export class Cwhere_rules_definer extends AlanStruct {
	constructor(
		public readonly variant:Vwhere_rules_definer) { super(); }
	public cast<K extends Vwhere_rules_definer['name']>(_variant:K):Extract<Vwhere_rules_definer, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vwhere_rules_definer['name']]:(($:Extract<Vwhere_rules_definer, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'where rules definer'`; }
	public is(other:Cwhere_rules_definer):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vresolvable = { name: 'rule', definition: client_state.Crules}|{ name: 'reference', definition: client_state.Cyes__navigable}
export class Cresolvable extends AlanStruct {
	constructor(
		public readonly variant:Vresolvable) { super(); }
	public cast<K extends Vresolvable['name']>(_variant:K):Extract<Vresolvable, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vresolvable['name']]:(($:Extract<Vresolvable, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'resolvable'`; }
	public is(other:Cresolvable):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Twhere_rules = {
	'rules':Record<string, Trules>;
};
export class Cwhere_rules extends AlanNode {
	public readonly properties:{
		readonly rules:Cwhere_rules.Drules
	};
	constructor(init:Twhere_rules, public location:AlanNode, public input: {
		definer: () => client_state.Cwhere_rules_definer,
		definition: () => application.Cwhere_rules_definition
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			rules: new Cwhere_rules.Drules(init['rules'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.rules.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Krules extends Reference<application.Crules, string> {
	constructor(key:string, $this:Crules) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.definition())
			.then(context => {
				const entry = context?.properties.rules.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rules'::key`; }
}
export type Trules = {
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
	'resolve status':Tresolve_status;
};
export class Crules extends AlanDictionaryEntry {
	public key:Krules;
	public get key_value() { return this.key.entry; }
	public definitions:{
		resolvable: Cresolvable;
	} = {
		resolvable: new Cresolvable({name:'rule', definition: this})
	}
	public readonly properties:{
		readonly is_valid:Crules.Dis_valid,
		readonly resolve_status:Cresolve_status
	};
	constructor(key:string, init:Trules, public parent:Cwhere_rules) {
		super();
		const $this = this;
		this.key = new Krules(key, $this);
		this.properties = {
			is_valid: new Crules.Dis_valid(init['is valid'], $this),
			resolve_status: new Crules.Dresolve_status(init['resolve status'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.is_valid.destroy();
		this.properties.resolve_status.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rules'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__is_valid__rules = {
};
export class Cno__is_valid__rules extends AlanNode {
	constructor(init:Tno__is_valid__rules, public parent:Crules) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__rules = {
};
export class Cyes__is_valid__rules extends AlanNode {
	constructor(init:Tyes__is_valid__rules, public parent:Crules) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstyle = {
	'initialized':'no'|['no', {}]|['yes', Tyes__initialized__style];
};
export class Cstyle extends AlanNode {
	public readonly properties:{
		readonly initialized:Cstyle.Dinitialized
	};
	constructor(init:Tstyle, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initialized: new Cstyle.Dinitialized(init['initialized'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.initialized.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__initialized__style = {
};
export class Cno__initialized__style extends AlanNode {
	constructor(init:Tno__initialized__style, public parent:Cstyle) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__initialized__style = {
	'has style':'no'|['no', {}]|['yes', Tyes__has_style];
};
export class Cyes__initialized__style extends AlanNode {
	public readonly properties:{
		readonly has_style:Cyes__initialized__style.Dhas_style
	};
	constructor(init:Tyes__initialized__style, public parent:Cstyle) {
		super();
		const $this = this;
		this.properties = {
			has_style: new Cyes__initialized__style.Dhas_style(init['has style'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_style.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_style = {
};
export class Cno__has_style extends AlanNode {
	constructor(init:Tno__has_style, public parent:Cyes__initialized__style) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has style'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_style = {
	'type':['color', Tcolor]|['fixed', Tfixed];
};
export class Cyes__has_style extends AlanNode {
	public readonly properties:{
		readonly type:Cyes__has_style.Dtype
	};
	constructor(init:Tyes__has_style, public parent:Cyes__initialized__style) {
		super();
		const $this = this;
		this.properties = {
			type: new Cyes__has_style.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has style'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcolor = {
	'value':string;
};
export class Ccolor extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tcolor, public parent:Cyes__has_style) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_value(value:string):void {
		(this.properties as Mutable<Ccolor['properties']>).value = value;
		super.notify('value', value);
	}
	public subscribe_value(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'color'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfixed = {
	'style':'accent'|['accent', {}]|'background'|['background', {}]|'brand'|['brand', {}]|'error'|['error', {}]|'foreground'|['foreground', {}]|'link'|['link', {}]|'success'|['success', {}]|'warning'|['warning', {}];
};
export class Cfixed extends AlanNode {
	public readonly properties:{
		readonly style:Cfixed.Dstyle
	};
	constructor(init:Tfixed, public parent:Cyes__has_style) {
		super();
		const $this = this;
		this.properties = {
			style: new Cfixed.Dstyle(init['style'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.style.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'fixed'`; }
	public get entity() { return this.parent.entity; }
}
export type Taccent = {
};
export class Caccent extends AlanNode {
	constructor(init:Taccent, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'accent'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbackground = {
};
export class Cbackground extends AlanNode {
	constructor(init:Tbackground, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'background'`; }
	public get entity() { return this.parent.entity; }
}
export type Tbrand = {
};
export class Cbrand extends AlanNode {
	constructor(init:Tbrand, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'brand'`; }
	public get entity() { return this.parent.entity; }
}
export type Terror__style = {
};
export class Cerror__style extends AlanNode {
	constructor(init:Terror__style, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'error'`; }
	public get entity() { return this.parent.entity; }
}
export type Tforeground = {
};
export class Cforeground extends AlanNode {
	constructor(init:Tforeground, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'foreground'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlink = {
};
export class Clink extends AlanNode {
	constructor(init:Tlink, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'link'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__style = {
};
export class Csuccess__style extends AlanNode {
	constructor(init:Tsuccess__style, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Twarning__style = {
};
export class Cwarning__style extends AlanNode {
	constructor(init:Twarning__style, public parent:Cfixed) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'warning'`; }
	public get entity() { return this.parent.entity; }
}
type Vnode_position = { name: 'root', definition: client_state.Croot_position}|{ name: 'state', definition: client_state.Cstates__state_group__type__property}|{ name: 'group', definition: client_state.Cgroup__type__property}|{ name: 'entity', definition: client_state.Centity}
export class Cnode_position extends AlanStruct {
	constructor(
		public readonly variant:Vnode_position, public input: {
			parent: () => client_state.Cnode_parent,
			root: () => client_state.Croot_position
		}) { super(); }
	public readonly output:{
		parent: () => client_state.Cnode_parent;
		root: () => client_state.Croot_position;
	} = {
		parent: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent()).result!),
		root: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.root()).result!)
	};
	public cast<K extends Vnode_position['name']>(_variant:K):Extract<Vnode_position, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_position['name']]:(($:Extract<Vnode_position, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node position'`; }
	public is(other:Cnode_position):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vroot_position = { name: 'operation', definition: client_state.Cyes__started}|{ name: 'root', definition: client_state.Cclient_state}
export class Croot_position extends AlanStruct {
	constructor(
		public readonly variant:Vroot_position) { super(); }
	public definitions:{
		node_position: Cnode_position;
	} = {
		node_position: new Cnode_position({name:'root', definition: this}, {
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => {
					switch (context?.variant.name) {
						case 'operation': {
							const client_state__root_position_var___node_position_in___parent___operation_nval = context.cast('operation');
							return resolve(context)
								.then(context => client_state__root_position_var___node_position_in___parent___operation_nval)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.definitions.node_parent).result;
						}
						case 'root': {
							return resolve(context)
								.then(() => this)
								.then(() => client_state.Cnode_parent.Pnone).result;
						}
						case undefined: return undefined;
						default: throw new Error(`Unexpected subtype '${(<any>context).variant.name}'`);
					};
				}).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this).result!)
		})
	}
	public cast<K extends Vroot_position['name']>(_variant:K):Extract<Vroot_position, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vroot_position['name']]:(($:Extract<Vroot_position, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'root position'`; }
	public is(other:Croot_position):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tresolve_status = {
	'status':'failed to resolve'|['failed to resolve', {}]|'is busy resolving'|['is busy resolving', {}]|'resolved'|['resolved', {}]|'unresolved'|['unresolved', {}];
	'validation result':'failure'|['failure', {}]|'success'|['success', {}];
};
export class Cresolve_status extends AlanNode {
	public readonly properties:{
		readonly status:Cresolve_status.Dstatus,
		readonly validation_result:Cresolve_status.Dvalidation_result
	};
	constructor(init:Tresolve_status, public location:AlanNode, public input: {
		resolvable: () => client_state.Cresolvable
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			status: new Cresolve_status.Dstatus(init['status'], $this),
			validation_result: new Cresolve_status.Dvalidation_result(init['validation result'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.status.destroy();
		this.properties.validation_result.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tfailed_to_resolve = {
};
export class Cfailed_to_resolve extends AlanNode {
	constructor(init:Tfailed_to_resolve, public parent:Cresolve_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'status'?'failed to resolve'`; }
	public get entity() { return this.parent.entity; }
}
export type Tis_busy_resolving = {
};
export class Cis_busy_resolving extends AlanNode {
	constructor(init:Tis_busy_resolving, public parent:Cresolve_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'status'?'is busy resolving'`; }
	public get entity() { return this.parent.entity; }
}
export type Tresolved__status = {
};
export class Cresolved__status extends AlanNode {
	constructor(init:Tresolved__status, public parent:Cresolve_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'status'?'resolved'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunresolved__status = {
};
export class Cunresolved__status extends AlanNode {
	constructor(init:Tunresolved__status, public parent:Cresolve_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'status'?'unresolved'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__validation_result__resolve_status = {
};
export class Cfailure__validation_result__resolve_status extends AlanNode {
	constructor(init:Tfailure__validation_result__resolve_status, public parent:Cresolve_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation result'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__validation_result__resolve_status = {
};
export class Csuccess__validation_result__resolve_status extends AlanNode {
	constructor(init:Tsuccess__validation_result__resolve_status, public parent:Cresolve_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation result'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Treport_parameters = {
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
	'parameters':Record<string, Tparameters>;
};
export class Creport_parameters extends AlanNode {
	public readonly properties:{
		readonly is_valid:Creport_parameters.Dis_valid,
		readonly parameters:Creport_parameters.Dparameters
	};
	constructor(init:Treport_parameters, public location:AlanNode, public input: {
		report_type: () => parameters.Cparameters
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			is_valid: new Creport_parameters.Dis_valid(init['is valid'], $this),
			parameters: new Creport_parameters.Dparameters(init['parameters'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_valid.destroy();
		this.properties.parameters.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__is_valid__report_parameters = {
};
export class Cno__is_valid__report_parameters extends AlanNode {
	constructor(init:Tno__is_valid__report_parameters, public parent:Creport_parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__report_parameters = {
};
export class Cyes__is_valid__report_parameters extends AlanNode {
	constructor(init:Tyes__is_valid__report_parameters, public parent:Creport_parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kparameters extends Reference<parameters.Cparameters__, string> {
	constructor(key:string, $this:Cparameters) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.report_type())
			.then(context => {
				const entry = context?.properties.parameters.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'parameters'::key`; }
}
export type Tparameters = {
	'type':['number', Tnumber__type__parameters]|['text', Ttext__type__parameters];
};
export class Cparameters extends AlanDictionaryEntry {
	public key:Kparameters;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cparameters.Dtype
	};
	constructor(key:string, init:Tparameters, public parent:Creport_parameters) {
		super();
		const $this = this;
		this.key = new Kparameters(key, $this);
		this.properties = {
			type: new Cparameters.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'parameters'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tnumber__type__parameters = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__number__type__parameters];
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
	'numerical type properties':Tnumerical_type_properties;
};
export class Cnumber__type__parameters extends AlanNode {
	public readonly properties:{
		readonly is_set:Cnumber__type__parameters.Dis_set,
		readonly is_valid:Cnumber__type__parameters.Dis_valid,
		readonly numerical_type_properties:Cnumerical_type_properties
	};
	public readonly inferences:{
		number_type: () => parameters.Cnumber
	} = {
		number_type: cache((detach:boolean) => {
			const client_state__report_parameters__parameters__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as parameters.Cnumber).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__parameters, public parent:Cparameters) {
		super();
		const $this = this;
		this.properties = {
			is_set: new Cnumber__type__parameters.Dis_set(init['is set'], $this),
			is_valid: new Cnumber__type__parameters.Dis_valid(init['is valid'], $this),
			numerical_type_properties: new Cnumber__type__parameters.Dnumerical_type_properties(init['numerical type properties'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_set.destroy();
		this.properties.is_valid.destroy();
		this.properties.numerical_type_properties.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__number__type__parameters = {
};
export class Cno__is_set__number__type__parameters extends AlanNode {
	constructor(init:Tno__is_set__number__type__parameters, public parent:Cnumber__type__parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__number__type__parameters = {
	'value':number;
};
export class Cyes__is_set__number__type__parameters extends AlanNode {
	public readonly properties:{
		readonly value:Cyes__is_set__number__type__parameters.Dvalue
	};
	constructor(init:Tyes__is_set__number__type__parameters, public parent:Cnumber__type__parameters) {
		super();
		const $this = this;
		this.properties = {
			value: new Cyes__is_set__number__type__parameters.Dvalue(init['value'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.value.destroy();
	}
	public set_value(data:number):void {
		(this.properties.value as Mutable<Cyes__is_set__number__type__parameters.Dvalue>).value = data;
		super.notify('value', data);
	}
	public subscribe_value(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__number__type__parameters = {
};
export class Cno__is_valid__number__type__parameters extends AlanNode {
	constructor(init:Tno__is_valid__number__type__parameters, public parent:Cnumber__type__parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__number__type__parameters = {
};
export class Cyes__is_valid__number__type__parameters extends AlanNode {
	constructor(init:Tyes__is_valid__number__type__parameters, public parent:Cnumber__type__parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__parameters = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__text__type__parameters];
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Ctext__type__parameters extends AlanNode {
	public readonly properties:{
		readonly is_set:Ctext__type__parameters.Dis_set,
		readonly is_valid:Ctext__type__parameters.Dis_valid
	};
	public readonly inferences:{
		text_type: () => parameters.Ctext
	} = {
		text_type: cache((detach:boolean) => {
			const client_state__report_parameters__parameters__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as parameters.Ctext).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__parameters, public parent:Cparameters) {
		super();
		const $this = this;
		this.properties = {
			is_set: new Ctext__type__parameters.Dis_set(init['is set'], $this),
			is_valid: new Ctext__type__parameters.Dis_valid(init['is valid'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_set.destroy();
		this.properties.is_valid.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__text__type__parameters = {
};
export class Cno__is_set__text__type__parameters extends AlanNode {
	constructor(init:Tno__is_set__text__type__parameters, public parent:Ctext__type__parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__text__type__parameters = {
	'value':string;
};
export class Cyes__is_set__text__type__parameters extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tyes__is_set__text__type__parameters, public parent:Ctext__type__parameters) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_value(value:string):void {
		(this.properties as Mutable<Cyes__is_set__text__type__parameters['properties']>).value = value;
		super.notify('value', value);
	}
	public subscribe_value(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__text__type__parameters = {
};
export class Cno__is_valid__text__type__parameters extends AlanNode {
	constructor(init:Tno__is_valid__text__type__parameters, public parent:Ctext__type__parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__text__type__parameters = {
};
export class Cyes__is_valid__text__type__parameters extends AlanNode {
	constructor(init:Tyes__is_valid__text__type__parameters, public parent:Ctext__type__parameters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vquery_position = { name: 'navigable text property', definition: client_state.Cqueries__yes}|{ name: 'node', definition: client_state.Cnode}
export class Cquery_position extends AlanStruct {
	constructor(
		public readonly variant:Vquery_position) { super(); }
	public cast<K extends Vquery_position['name']>(_variant:K):Extract<Vquery_position, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vquery_position['name']]:(($:Extract<Vquery_position, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'query position'`; }
	public is(other:Cquery_position):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vquery_entry_status_position = { name: 'result entry', definition: client_state.Centries__yes__has_result}|{ name: 'selected entry', definition: client_state.Cselected_entries}
export class Cquery_entry_status_position extends AlanStruct {
	constructor(
		public readonly variant:Vquery_entry_status_position) { super(); }
	public cast<K extends Vquery_entry_status_position['name']>(_variant:K):Extract<Vquery_entry_status_position, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vquery_entry_status_position['name']]:(($:Extract<Vquery_entry_status_position, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'query entry status position'`; }
	public is(other:Cquery_entry_status_position):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Vid_path_location = { name: 'query entry', definition: client_state.Cyes__initialized__query_entry_status}|{ name: 'join', definition: client_state.Cresolved__joined_collection}|{ name: 'view', definition: client_state.Copen_views}
export class Cid_path_location extends AlanStruct {
	constructor(
		public readonly variant:Vid_path_location) { super(); }
	public cast<K extends Vid_path_location['name']>(_variant:K):Extract<Vid_path_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vid_path_location['name']]:(($:Extract<Vid_path_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'id path location'`; }
	public is(other:Cid_path_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tquery_entry_status = {
	'initialized':['no', Tno__initialized__query_entry_status]|'unknown'|['unknown', {}]|['yes', Tyes__initialized__query_entry_status];
};
export class Cquery_entry_status extends AlanNode {
	public readonly properties:{
		readonly initialized:Cquery_entry_status.Dinitialized
	};
	constructor(init:Tquery_entry_status, public location:AlanNode, public input: {
		position: () => client_state.Cquery_entry_status_position,
		query: () => client_state.Cquery__client_state
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initialized: new Cquery_entry_status.Dinitialized(init['initialized'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.initialized.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__initialized__query_entry_status = {
	'referenced node':Tid_path;
};
export class Cno__initialized__query_entry_status extends AlanNode {
	public readonly properties:{
		readonly referenced_node:Cid_path
	};
	constructor(init:Tno__initialized__query_entry_status, public parent:Cquery_entry_status) {
		super();
		const $this = this;
		this.properties = {
			referenced_node: new Cno__initialized__query_entry_status.Dreferenced_node(init['referenced node'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.referenced_node.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunknown__initialized = {
};
export class Cunknown__initialized extends AlanNode {
	constructor(init:Tunknown__initialized, public parent:Cquery_entry_status) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'unknown'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__initialized__query_entry_status = {
	'referenced node':Tclient_id_path;
};
export class Cyes__initialized__query_entry_status extends AlanNode {
	public definitions:{
		id_path_location: Cid_path_location;
	} = {
		id_path_location: new Cid_path_location({name:'query entry', definition: this})
	}
	public readonly properties:{
		readonly referenced_node:Cclient_id_path
	};
	public readonly inferences:{
		use_for: () => client_state.Cquery__use_for__query
	} = {
		use_for: cache((detach:boolean) => {
			const client_state__query_entry_status__initialized__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.query())
				.then(context => {
					if (context?.properties.use_for.state.name === 'query') {
						return resolve(context.properties.use_for.state.node as client_state.Cquery__use_for__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__initialized__query_entry_status, public parent:Cquery_entry_status) {
		super();
		const $this = this;
		this.properties = {
			referenced_node: new Cyes__initialized__query_entry_status.Dreferenced_node(init['referenced node'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.referenced_node.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__client_state = {
	'can download files':'no'|['no', {}]|'yes'|['yes', {}];
	'columns':Record<string, Tcolumns>;
	'definition':string;
	'delay query':'no'|['no', {}]|'yes'|['yes', {}];
	'entries are editable':'no'|['no', {}]|['yes', Tyes__entries_are_editable];
	'filters':Record<string, Tfilters>;
	'has columns':'no'|['no', {}]|['yes', Tyes__has_columns];
	'has result':'no'|['no', {}]|['yes', Tyes__has_result];
	'has selected entries':'no'|['no', {}]|['yes', Tyes__has_selected_entries];
	'is busy querying':['no', Tno__is_busy_querying]|['yes', Tyes__is_busy_querying];
	'show all':'no'|['no', {}]|'yes'|['yes', {}];
	'use for':'candidates'|['candidates', {}]|'query'|['query', {}];
	'view definition':string;
	'view definition context':string;
};
export class Cquery__client_state extends AlanNode {
	public entry_builder: unknown;
	public readonly properties:{
		readonly can_download_files:Cquery__client_state.Dcan_download_files,
		readonly columns:Cquery__client_state.Dcolumns,
		readonly definition:Cquery__client_state.Ddefinition,
		readonly delay_query:Cquery__client_state.Ddelay_query,
		readonly entries_are_editable:Cquery__client_state.Dentries_are_editable,
		readonly filters:Cquery__client_state.Dfilters,
		readonly has_columns:Cquery__client_state.Dhas_columns,
		readonly has_result:Cquery__client_state.Dhas_result,
		readonly has_selected_entries:Cquery__client_state.Dhas_selected_entries,
		readonly is_busy_querying:Cquery__client_state.Dis_busy_querying,
		readonly show_all:Cquery__client_state.Dshow_all,
		readonly use_for:Cquery__client_state.Duse_for,
		readonly view_definition:Cquery__client_state.Dview_definition,
		readonly view_definition_context:Cquery__client_state.Dview_definition_context
	};
	constructor(init:Tquery__client_state, public location:AlanNode, public input: {
		position: () => client_state.Cquery_position
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			can_download_files: new Cquery__client_state.Dcan_download_files(init['can download files'], $this),
			columns: new Cquery__client_state.Dcolumns(init['columns'], $this),
			definition: new Cquery__client_state.Ddefinition(init['definition'], $this),
			delay_query: new Cquery__client_state.Ddelay_query(init['delay query'], $this),
			entries_are_editable: new Cquery__client_state.Dentries_are_editable(init['entries are editable'], $this),
			filters: new Cquery__client_state.Dfilters(init['filters'], $this),
			has_columns: new Cquery__client_state.Dhas_columns(init['has columns'], $this),
			has_result: new Cquery__client_state.Dhas_result(init['has result'], $this),
			has_selected_entries: new Cquery__client_state.Dhas_selected_entries(init['has selected entries'], $this),
			is_busy_querying: new Cquery__client_state.Dis_busy_querying(init['is busy querying'], $this),
			show_all: new Cquery__client_state.Dshow_all(init['show all'], $this),
			use_for: new Cquery__client_state.Duse_for(init['use for'], $this),
			view_definition: new Cquery__client_state.Dview_definition(init['view definition'], $this),
			view_definition_context: new Cquery__client_state.Dview_definition_context(init['view definition context'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.can_download_files.destroy();
		this.properties.columns.destroy();
		this.properties.definition.destroy();
		this.properties.delay_query.destroy();
		this.properties.entries_are_editable.destroy();
		this.properties.filters.destroy();
		this.properties.has_columns.destroy();
		this.properties.has_result.destroy();
		this.properties.has_selected_entries.destroy();
		this.properties.is_busy_querying.destroy();
		this.properties.show_all.destroy();
		this.properties.use_for.destroy();
		this.properties.view_definition.destroy();
		this.properties.view_definition_context.destroy();
	}
	public set_definition(value:string):void {
		this.properties.definition.unlink();
		(<(detach?:boolean) => void>(this.properties.definition as any).resolve)(true);
		(this.properties.definition as Mutable<Cquery__client_state.Ddefinition>).entry = value;
		super.notify('definition', value);
	}
	public subscribe_definition(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('definition', observer);
		observer.on_update(this.properties.definition.entry, subscription);
		return subscription;
	}
	public set_view_definition(value:string):void {
		this.properties.view_definition.unlink();
		(<(detach?:boolean) => void>(this.properties.view_definition as any).resolve)(true);
		(this.properties.view_definition as Mutable<Cquery__client_state.Dview_definition>).entry = value;
		super.notify('view definition', value);
	}
	public subscribe_view_definition(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('view definition', observer);
		observer.on_update(this.properties.view_definition.entry, subscription);
		return subscription;
	}
	public set_view_definition_context(value:string):void {
		this.properties.view_definition_context.unlink();
		(<(detach?:boolean) => void>(this.properties.view_definition_context as any).resolve)(true);
		(this.properties.view_definition_context as Mutable<Cquery__client_state.Dview_definition_context>).entry = value;
		super.notify('view definition context', value);
	}
	public subscribe_view_definition_context(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('view definition context', observer);
		observer.on_update(this.properties.view_definition_context.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__can_download_files__query = {
};
export class Cno__can_download_files__query extends AlanNode {
	constructor(init:Tno__can_download_files__query, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can download files'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_download_files__query = {
};
export class Cyes__can_download_files__query extends AlanNode {
	constructor(init:Tyes__can_download_files__query, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can download files'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kcolumns extends Reference<views.Ccolumns, string> {
	constructor(key:string, $this:Ccolumns) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.definition?.ref)
			.then(context => {
				const entry = context?.properties.columns.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'columns'::key`; }
}
export type Tcolumns = {
	'column type':'content'|['content', {}]|'id'|['id', {}];
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__columns];
	'name':string;
	'type':'file'|['file', {}]|['number', Tnumber__type__columns]|['state group', Tstate_group__type__columns]|['text', Ttext__type__columns]|'widget'|['widget', {}];
};
export class Ccolumns extends AlanDictionaryEntry {
	public key:Kcolumns;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly column_type:Ccolumns.Dcolumn_type,
		readonly has_successor:Ccolumns.Dhas_successor,
		readonly name:string,
		readonly type:Ccolumns.Dtype
	};
	constructor(key:string, init:Tcolumns, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.key = new Kcolumns(key, $this);
		this.properties = {
			column_type: new Ccolumns.Dcolumn_type(init['column type'], $this),
			has_successor: new Ccolumns.Dhas_successor(init['has successor'], $this),
			name: init['name'],
			type: new Ccolumns.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.column_type.destroy();
		this.properties.has_successor.destroy();
		this.properties.type.destroy();
	}
	public set_name(value:string):void {
		(this.properties as Mutable<Ccolumns['properties']>).name = value;
		super.notify('name', value);
	}
	public subscribe_name(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('name', observer);
		observer.on_update(this.properties.name, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'columns'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcontent__column_type__columns = {
};
export class Ccontent__column_type__columns extends AlanNode {
	constructor(init:Tcontent__column_type__columns, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'column type'?'content'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid__column_type__columns = {
};
export class Cid__column_type__columns extends AlanNode {
	constructor(init:Tid__column_type__columns, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'column type'?'id'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_successor__columns = {
};
export class Cno__has_successor__columns extends AlanNode {
	constructor(init:Tno__has_successor__columns, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__columns = {
	'successor':string;
};
export class Cyes__has_successor__columns extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__columns.Dsuccessor
	};
	constructor(init:Tyes__has_successor__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__columns.Dsuccessor(init['successor'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.successor.destroy();
	}
	public set_successor(value:string):void {
		this.properties.successor.unlink();
		(<(detach?:boolean) => void>(this.properties.successor as any).resolve)(true);
		(this.properties.successor as Mutable<Cyes__has_successor__columns.Dsuccessor>).entry = value;
		super.notify('successor', value);
	}
	public subscribe_successor(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('successor', observer);
		observer.on_update(this.properties.successor.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__columns = {
};
export class Cfile__type__columns extends AlanNode {
	public readonly inferences:{
		file_type: () => views.Cfile__type__columns
	} = {
		file_type: cache((detach:boolean) => {
			const client_state__query__columns__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as views.Cfile__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__columns, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__columns = {
	'has filter':'no'|['no', {}]|['yes', Tyes__has_filter__number];
};
export class Cnumber__type__columns extends AlanNode {
	public readonly properties:{
		readonly has_filter:Cnumber__type__columns.Dhas_filter
	};
	public readonly inferences:{
		number_type: () => views.Cnumber__type__columns
	} = {
		number_type: cache((detach:boolean) => {
			const client_state__query__columns__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as views.Cnumber__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			has_filter: new Cnumber__type__columns.Dhas_filter(init['has filter'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_filter.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_filter__number = {
};
export class Cno__has_filter__number extends AlanNode {
	constructor(init:Tno__has_filter__number, public parent:Cnumber__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_filter__number = {
	'criteria is valid':'no'|['no', {}]|['yes', Tyes__criteria_is_valid__yes__has_filter];
	'filter range':'no'|['no', {}]|'not available'|['not available', {}]|['yes', Tyes__filter_range];
	'filter set':'no'|['no', {}]|'yes'|['yes', {}];
	'has default filter':'no'|['no', {}]|'yes'|['yes', {}];
	'numerical type properties':Tnumerical_type_properties;
	'operator selected':'no'|['no', {}]|['yes', Tyes__operator_selected];
};
export class Cyes__has_filter__number extends AlanNode {
	public readonly properties:{
		readonly criteria_is_valid:Cyes__has_filter__number.Dcriteria_is_valid,
		readonly filter_range:Cyes__has_filter__number.Dfilter_range,
		readonly filter_set:Cyes__has_filter__number.Dfilter_set,
		readonly has_default_filter:Cyes__has_filter__number.Dhas_default_filter,
		readonly numerical_type_properties:Cnumerical_type_properties,
		readonly operator_selected:Cyes__has_filter__number.Doperator_selected
	};
	constructor(init:Tyes__has_filter__number, public parent:Cnumber__type__columns) {
		super();
		const $this = this;
		this.properties = {
			criteria_is_valid: new Cyes__has_filter__number.Dcriteria_is_valid(init['criteria is valid'], $this),
			filter_range: new Cyes__has_filter__number.Dfilter_range(init['filter range'], $this),
			filter_set: new Cyes__has_filter__number.Dfilter_set(init['filter set'], $this),
			has_default_filter: new Cyes__has_filter__number.Dhas_default_filter(init['has default filter'], $this),
			numerical_type_properties: new Cyes__has_filter__number.Dnumerical_type_properties(init['numerical type properties'], $this),
			operator_selected: new Cyes__has_filter__number.Doperator_selected(init['operator selected'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.criteria_is_valid.destroy();
		this.properties.filter_range.destroy();
		this.properties.filter_set.destroy();
		this.properties.has_default_filter.destroy();
		this.properties.numerical_type_properties.destroy();
		this.properties.operator_selected.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__criteria_is_valid__yes__has_filter = {
};
export class Cno__criteria_is_valid__yes__has_filter extends AlanNode {
	constructor(init:Tno__criteria_is_valid__yes__has_filter, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'criteria is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__criteria_is_valid__yes__has_filter = {
	'criteria':number;
};
export class Cyes__criteria_is_valid__yes__has_filter extends AlanNode {
	public readonly properties:{
		readonly criteria:Cyes__criteria_is_valid__yes__has_filter.Dcriteria
	};
	constructor(init:Tyes__criteria_is_valid__yes__has_filter, public parent:Cyes__has_filter__number) {
		super();
		const $this = this;
		this.properties = {
			criteria: new Cyes__criteria_is_valid__yes__has_filter.Dcriteria(init['criteria'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.criteria.destroy();
	}
	public set_criteria(data:number):void {
		(this.properties.criteria as Mutable<Cyes__criteria_is_valid__yes__has_filter.Dcriteria>).value = data;
		super.notify('criteria', data);
	}
	public subscribe_criteria(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('criteria', observer);
		observer.on_update(this.properties.criteria.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'criteria is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__filter_range = {
};
export class Cno__filter_range extends AlanNode {
	constructor(init:Tno__filter_range, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter range'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_available = {
};
export class Cnot_available extends AlanNode {
	constructor(init:Tnot_available, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter range'?'not available'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__filter_range = {
	'criteria is valid':'no'|['no', {}]|['yes', Tyes__criteria_is_valid__yes__filter_range];
	'operator':['greater than', Tgreater_than__operator__yes__filter_range]|['smaller than', Tsmaller_than__operator__yes__filter_range];
};
export class Cyes__filter_range extends AlanNode {
	public readonly properties:{
		readonly criteria_is_valid:Cyes__filter_range.Dcriteria_is_valid,
		readonly operator:Cyes__filter_range.Doperator
	};
	constructor(init:Tyes__filter_range, public parent:Cyes__has_filter__number) {
		super();
		const $this = this;
		this.properties = {
			criteria_is_valid: new Cyes__filter_range.Dcriteria_is_valid(init['criteria is valid'], $this),
			operator: new Cyes__filter_range.Doperator(init['operator'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.criteria_is_valid.destroy();
		this.properties.operator.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter range'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__criteria_is_valid__yes__filter_range = {
};
export class Cno__criteria_is_valid__yes__filter_range extends AlanNode {
	constructor(init:Tno__criteria_is_valid__yes__filter_range, public parent:Cyes__filter_range) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'criteria is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__criteria_is_valid__yes__filter_range = {
	'criteria':number;
};
export class Cyes__criteria_is_valid__yes__filter_range extends AlanNode {
	public readonly properties:{
		readonly criteria:Cyes__criteria_is_valid__yes__filter_range.Dcriteria
	};
	constructor(init:Tyes__criteria_is_valid__yes__filter_range, public parent:Cyes__filter_range) {
		super();
		const $this = this;
		this.properties = {
			criteria: new Cyes__criteria_is_valid__yes__filter_range.Dcriteria(init['criteria'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.criteria.destroy();
	}
	public set_criteria(data:number):void {
		(this.properties.criteria as Mutable<Cyes__criteria_is_valid__yes__filter_range.Dcriteria>).value = data;
		super.notify('criteria', data);
	}
	public subscribe_criteria(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('criteria', observer);
		observer.on_update(this.properties.criteria.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'criteria is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgreater_than__operator__yes__filter_range = {
	'or equal to':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cgreater_than__operator__yes__filter_range extends AlanNode {
	public readonly properties:{
		readonly or_equal_to:Cgreater_than__operator__yes__filter_range.Dor_equal_to
	};
	constructor(init:Tgreater_than__operator__yes__filter_range, public parent:Cyes__filter_range) {
		super();
		const $this = this;
		this.properties = {
			or_equal_to: new Cgreater_than__operator__yes__filter_range.Dor_equal_to(init['or equal to'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.or_equal_to.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'greater than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__or_equal_to__greater_than = {
};
export class Cno__or_equal_to__greater_than extends AlanNode {
	constructor(init:Tno__or_equal_to__greater_than, public parent:Cgreater_than__operator__yes__filter_range) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'or equal to'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__or_equal_to__greater_than = {
};
export class Cyes__or_equal_to__greater_than extends AlanNode {
	constructor(init:Tyes__or_equal_to__greater_than, public parent:Cgreater_than__operator__yes__filter_range) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'or equal to'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsmaller_than__operator__yes__filter_range = {
	'or equal to':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Csmaller_than__operator__yes__filter_range extends AlanNode {
	public readonly properties:{
		readonly or_equal_to:Csmaller_than__operator__yes__filter_range.Dor_equal_to
	};
	constructor(init:Tsmaller_than__operator__yes__filter_range, public parent:Cyes__filter_range) {
		super();
		const $this = this;
		this.properties = {
			or_equal_to: new Csmaller_than__operator__yes__filter_range.Dor_equal_to(init['or equal to'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.or_equal_to.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'smaller than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__or_equal_to__smaller_than = {
};
export class Cno__or_equal_to__smaller_than extends AlanNode {
	constructor(init:Tno__or_equal_to__smaller_than, public parent:Csmaller_than__operator__yes__filter_range) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'or equal to'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__or_equal_to__smaller_than = {
};
export class Cyes__or_equal_to__smaller_than extends AlanNode {
	constructor(init:Tyes__or_equal_to__smaller_than, public parent:Csmaller_than__operator__yes__filter_range) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'or equal to'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__filter_set__yes__has_filter__number = {
};
export class Cno__filter_set__yes__has_filter__number extends AlanNode {
	constructor(init:Tno__filter_set__yes__has_filter__number, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__filter_set__yes__has_filter__number = {
};
export class Cyes__filter_set__yes__has_filter__number extends AlanNode {
	constructor(init:Tyes__filter_set__yes__has_filter__number, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_default_filter__yes__has_filter__number = {
};
export class Cno__has_default_filter__yes__has_filter__number extends AlanNode {
	constructor(init:Tno__has_default_filter__yes__has_filter__number, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_default_filter__yes__has_filter__number = {
};
export class Cyes__has_default_filter__yes__has_filter__number extends AlanNode {
	constructor(init:Tyes__has_default_filter__yes__has_filter__number, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__operator_selected = {
};
export class Cno__operator_selected extends AlanNode {
	constructor(init:Tno__operator_selected, public parent:Cyes__has_filter__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__operator_selected = {
	'operator':'equal to'|['equal to', {}]|'greater than'|['greater than', {}]|'greater than or equal to'|['greater than or equal to', {}]|'smaller than'|['smaller than', {}]|'smaller than or equal to'|['smaller than or equal to', {}];
};
export class Cyes__operator_selected extends AlanNode {
	public readonly properties:{
		readonly operator:Cyes__operator_selected.Doperator
	};
	constructor(init:Tyes__operator_selected, public parent:Cyes__has_filter__number) {
		super();
		const $this = this;
		this.properties = {
			operator: new Cyes__operator_selected.Doperator(init['operator'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.operator.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequal_to = {
};
export class Cequal_to extends AlanNode {
	constructor(init:Tequal_to, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'equal to'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgreater_than__operator__yes__operator_selected = {
};
export class Cgreater_than__operator__yes__operator_selected extends AlanNode {
	constructor(init:Tgreater_than__operator__yes__operator_selected, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'greater than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgreater_than_or_equal_to = {
};
export class Cgreater_than_or_equal_to extends AlanNode {
	constructor(init:Tgreater_than_or_equal_to, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'greater than or equal to'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsmaller_than__operator__yes__operator_selected = {
};
export class Csmaller_than__operator__yes__operator_selected extends AlanNode {
	constructor(init:Tsmaller_than__operator__yes__operator_selected, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'smaller than'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsmaller_than_or_equal_to = {
};
export class Csmaller_than_or_equal_to extends AlanNode {
	constructor(init:Tsmaller_than_or_equal_to, public parent:Cyes__operator_selected) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operator'?'smaller than or equal to'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__columns = {
	'has filter':'no'|['no', {}]|['yes', Tyes__has_filter__state_group];
};
export class Cstate_group__type__columns extends AlanNode {
	public readonly properties:{
		readonly has_filter:Cstate_group__type__columns.Dhas_filter
	};
	public readonly inferences:{
		state_group_type: () => views.Cstate_group__type__columns
	} = {
		state_group_type: cache((detach:boolean) => {
			const client_state__query__columns__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as views.Cstate_group__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			has_filter: new Cstate_group__type__columns.Dhas_filter(init['has filter'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_filter.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_filter__state_group = {
};
export class Cno__has_filter__state_group extends AlanNode {
	constructor(init:Tno__has_filter__state_group, public parent:Cstate_group__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_filter__state_group = {
	'filter enabled':'no'|['no', {}]|'yes'|['yes', {}];
	'has default filter':'no'|['no', {}]|'yes'|['yes', {}];
	'states':Record<string, Tstates__yes>;
};
export class Cyes__has_filter__state_group extends AlanNode {
	public readonly properties:{
		readonly filter_enabled:Cyes__has_filter__state_group.Dfilter_enabled,
		readonly has_default_filter:Cyes__has_filter__state_group.Dhas_default_filter,
		readonly states:Cyes__has_filter__state_group.Dstates
	};
	constructor(init:Tyes__has_filter__state_group, public parent:Cstate_group__type__columns) {
		super();
		const $this = this;
		this.properties = {
			filter_enabled: new Cyes__has_filter__state_group.Dfilter_enabled(init['filter enabled'], $this),
			has_default_filter: new Cyes__has_filter__state_group.Dhas_default_filter(init['has default filter'], $this),
			states: new Cyes__has_filter__state_group.Dstates(init['states'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.filter_enabled.destroy();
		this.properties.has_default_filter.destroy();
		this.properties.states.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__filter_enabled = {
};
export class Cno__filter_enabled extends AlanNode {
	constructor(init:Tno__filter_enabled, public parent:Cyes__has_filter__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter enabled'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__filter_enabled = {
};
export class Cyes__filter_enabled extends AlanNode {
	constructor(init:Tyes__filter_enabled, public parent:Cyes__has_filter__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter enabled'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_default_filter__yes__has_filter__state_group = {
};
export class Cno__has_default_filter__yes__has_filter__state_group extends AlanNode {
	constructor(init:Tno__has_default_filter__yes__has_filter__state_group, public parent:Cyes__has_filter__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_default_filter__yes__has_filter__state_group = {
};
export class Cyes__has_default_filter__yes__has_filter__state_group extends AlanNode {
	constructor(init:Tyes__has_default_filter__yes__has_filter__state_group, public parent:Cyes__has_filter__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates__yes = {
	'is selected':'no'|['no', {}]|'yes'|['yes', {}];
	'name':string;
};
export class Cstates__yes extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly is_selected:Cstates__yes.Dis_selected,
		readonly name:string
	};
	constructor(key:string, init:Tstates__yes, public parent:Cyes__has_filter__state_group) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			is_selected: new Cstates__yes.Dis_selected(init['is selected'], $this),
			name: init['name']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_selected.destroy();
	}
	public set_name(value:string):void {
		(this.properties as Mutable<Cstates__yes['properties']>).name = value;
		super.notify('name', value);
	}
	public subscribe_name(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('name', observer);
		observer.on_update(this.properties.name, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__is_selected__states__yes = {
};
export class Cno__is_selected__states__yes extends AlanNode {
	constructor(init:Tno__is_selected__states__yes, public parent:Cstates__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_selected__states__yes = {
};
export class Cyes__is_selected__states__yes extends AlanNode {
	constructor(init:Tyes__is_selected__states__yes, public parent:Cstates__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__columns = {
	'filter on current id path':'no'|['no', {}]|'yes'|['yes', {}];
	'has filter':'no'|['no', {}]|['yes', Tyes__has_filter__text];
};
export class Ctext__type__columns extends AlanNode {
	public readonly properties:{
		readonly filter_on_current_id_path:Ctext__type__columns.Dfilter_on_current_id_path,
		readonly has_filter:Ctext__type__columns.Dhas_filter
	};
	public readonly inferences:{
		text_type: () => views.Ctext__type__columns
	} = {
		text_type: cache((detach:boolean) => {
			const client_state__query__columns__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as views.Ctext__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__columns, public parent:Ccolumns) {
		super();
		const $this = this;
		this.properties = {
			filter_on_current_id_path: new Ctext__type__columns.Dfilter_on_current_id_path(init['filter on current id path'], $this),
			has_filter: new Ctext__type__columns.Dhas_filter(init['has filter'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.filter_on_current_id_path.destroy();
		this.properties.has_filter.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__filter_on_current_id_path = {
};
export class Cno__filter_on_current_id_path extends AlanNode {
	constructor(init:Tno__filter_on_current_id_path, public parent:Ctext__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter on current id path'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__filter_on_current_id_path = {
};
export class Cyes__filter_on_current_id_path extends AlanNode {
	constructor(init:Tyes__filter_on_current_id_path, public parent:Ctext__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter on current id path'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_filter__text = {
};
export class Cno__has_filter__text extends AlanNode {
	constructor(init:Tno__has_filter__text, public parent:Ctext__type__columns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_filter__text = {
	'filter set':'no'|['no', {}]|'yes'|['yes', {}];
	'has default filter':'no'|['no', {}]|'yes'|['yes', {}];
	'operation':'contains'|['contains', {}]|'ends with'|['ends with', {}]|'equals'|['equals', {}]|'starts with'|['starts with', {}];
	'value':string;
};
export class Cyes__has_filter__text extends AlanNode {
	public readonly properties:{
		readonly filter_set:Cyes__has_filter__text.Dfilter_set,
		readonly has_default_filter:Cyes__has_filter__text.Dhas_default_filter,
		readonly operation:Cyes__has_filter__text.Doperation,
		readonly value:string
	};
	constructor(init:Tyes__has_filter__text, public parent:Ctext__type__columns) {
		super();
		const $this = this;
		this.properties = {
			filter_set: new Cyes__has_filter__text.Dfilter_set(init['filter set'], $this),
			has_default_filter: new Cyes__has_filter__text.Dhas_default_filter(init['has default filter'], $this),
			operation: new Cyes__has_filter__text.Doperation(init['operation'], $this),
			value: init['value']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.filter_set.destroy();
		this.properties.has_default_filter.destroy();
		this.properties.operation.destroy();
	}
	public set_value(value:string):void {
		(this.properties as Mutable<Cyes__has_filter__text['properties']>).value = value;
		super.notify('value', value);
	}
	public subscribe_value(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__filter_set__yes__has_filter__text = {
};
export class Cno__filter_set__yes__has_filter__text extends AlanNode {
	constructor(init:Tno__filter_set__yes__has_filter__text, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__filter_set__yes__has_filter__text = {
};
export class Cyes__filter_set__yes__has_filter__text extends AlanNode {
	constructor(init:Tyes__filter_set__yes__has_filter__text, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filter set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_default_filter__yes__has_filter__text = {
};
export class Cno__has_default_filter__yes__has_filter__text extends AlanNode {
	constructor(init:Tno__has_default_filter__yes__has_filter__text, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default filter'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_default_filter__yes__has_filter__text = {
};
export class Cyes__has_default_filter__yes__has_filter__text extends AlanNode {
	constructor(init:Tyes__has_default_filter__yes__has_filter__text, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has default filter'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcontains = {
};
export class Ccontains extends AlanNode {
	constructor(init:Tcontains, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'contains'`; }
	public get entity() { return this.parent.entity; }
}
export type Tends_with = {
};
export class Cends_with extends AlanNode {
	constructor(init:Tends_with, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'ends with'`; }
	public get entity() { return this.parent.entity; }
}
export type Tequals = {
};
export class Cequals extends AlanNode {
	constructor(init:Tequals, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'equals'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstarts_with = {
};
export class Cstarts_with extends AlanNode {
	constructor(init:Tstarts_with, public parent:Cyes__has_filter__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'starts with'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__type__columns = {
};
export class Cwidget__type__columns extends AlanNode {
	public readonly inferences:{
		widget_type: () => views.Cwidget__type__columns
	} = {
		widget_type: cache((detach:boolean) => {
			const client_state__query__columns__type__widget_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'widget') {
						return resolve(context.properties.type.state.node as views.Cwidget__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twidget__type__columns, public parent:Ccolumns) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__delay_query = {
};
export class Cno__delay_query extends AlanNode {
	constructor(init:Tno__delay_query, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'delay query'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__delay_query = {
};
export class Cyes__delay_query extends AlanNode {
	constructor(init:Tyes__delay_query, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'delay query'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__entries_are_editable = {
};
export class Cno__entries_are_editable extends AlanNode {
	constructor(init:Tno__entries_are_editable, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries are editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__entries_are_editable = {
	'can create entry':'no'|['no', {}]|['yes', Tyes__can_create_entry__yes];
	'is importing':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__entries_are_editable extends AlanNode {
	public readonly properties:{
		readonly can_create_entry:Cyes__entries_are_editable.Dcan_create_entry,
		readonly is_importing:Cyes__entries_are_editable.Dis_importing
	};
	constructor(init:Tyes__entries_are_editable, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.properties = {
			can_create_entry: new Cyes__entries_are_editable.Dcan_create_entry(init['can create entry'], $this),
			is_importing: new Cyes__entries_are_editable.Dis_importing(init['is importing'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.can_create_entry.destroy();
		this.properties.is_importing.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries are editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_create_entry__yes = {
};
export class Cno__can_create_entry__yes extends AlanNode {
	constructor(init:Tno__can_create_entry__yes, public parent:Cyes__entries_are_editable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can create entry'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_create_entry__yes = {
	'in':'candidates'|['candidates', {}]|['query', Tquery__in];
};
export class Cyes__can_create_entry__yes extends AlanNode {
	public readonly properties:{
		readonly in_:Cyes__can_create_entry__yes.Din_
	};
	constructor(init:Tyes__can_create_entry__yes, public parent:Cyes__entries_are_editable) {
		super();
		const $this = this;
		this.properties = {
			in_: new Cyes__can_create_entry__yes.Din_(init['in'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.in_.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can create entry'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcandidates__in = {
};
export class Ccandidates__in extends AlanNode {
	public readonly inferences:{
		candidates_query: () => client_state.Ccandidates__use_for__query
	} = {
		candidates_query: cache((detach:boolean) => {
			const client_state__query__entries_are_editable__yes__can_create_entry__yes__in__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.use_for.state.name === 'candidates') {
						return resolve(context.properties.use_for.state.node as client_state.Ccandidates__use_for__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcandidates__in, public parent:Cyes__can_create_entry__yes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'in'?'candidates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__in = {
	'collection':string;
};
export class Cquery__in extends AlanNode {
	public readonly properties:{
		readonly collection:Cquery__in.Dcollection
	};
	public readonly inferences:{
		query: () => client_state.Cquery__use_for__query
	} = {
		query: cache((detach:boolean) => {
			const client_state__query__entries_are_editable__yes__can_create_entry__yes__in__query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.use_for.state.name === 'query') {
						return resolve(context.properties.use_for.state.node as client_state.Cquery__use_for__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery__in, public parent:Cyes__can_create_entry__yes) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cquery__in.Dcollection(init['collection'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.collection.destroy();
	}
	public set_collection(value:string):void {
		this.properties.collection.unlink();
		(<(detach?:boolean) => void>(this.properties.collection as any).resolve)(true);
		(this.properties.collection as Mutable<Cquery__in.Dcollection>).entry = value;
		super.notify('collection', value);
	}
	public subscribe_collection(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('collection', observer);
		observer.on_update(this.properties.collection.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'in'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_importing = {
};
export class Cno__is_importing extends AlanNode {
	constructor(init:Tno__is_importing, public parent:Cyes__entries_are_editable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is importing'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_importing = {
};
export class Cyes__is_importing extends AlanNode {
	constructor(init:Tyes__is_importing, public parent:Cyes__entries_are_editable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is importing'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kfilters extends Reference<views.Cfilters__filters, string> {
	constructor(key:string, $this:Cfilters) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.definition?.ref)
			.then(context => context?.properties.filters)
			.then(context => {
				const entry = context?.properties.filters.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'::key`; }
}
export type Tfilters = {
	'active':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cfilters extends AlanDictionaryEntry {
	public key:Kfilters;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly active:Cfilters.Dactive
	};
	constructor(key:string, init:Tfilters, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.key = new Kfilters(key, $this);
		this.properties = {
			active: new Cfilters.Dactive(init['active'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.active.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'filters'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__active = {
};
export class Cno__active extends AlanNode {
	constructor(init:Tno__active, public parent:Cfilters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'active'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__active = {
};
export class Cyes__active extends AlanNode {
	constructor(init:Tyes__active, public parent:Cfilters) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'active'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_columns = {
};
export class Cno__has_columns extends AlanNode {
	constructor(init:Tno__has_columns, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has columns'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_columns = {
	'first':string;
};
export class Cyes__has_columns extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_columns.Dfirst
	};
	constructor(init:Tyes__has_columns, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_columns.Dfirst(init['first'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.first.destroy();
	}
	public set_first(value:string):void {
		this.properties.first.unlink();
		(<(detach?:boolean) => void>(this.properties.first as any).resolve)(true);
		(this.properties.first as Mutable<Cyes__has_columns.Dfirst>).entry = value;
		super.notify('first', value);
	}
	public subscribe_first(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('first', observer);
		observer.on_update(this.properties.first.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has columns'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_result = {
};
export class Cno__has_result extends AlanNode {
	constructor(init:Tno__has_result, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has result'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_result = {
	'amount of entries visible':number;
	'backend updates are available':'no'|['no', {}]|'yes'|['yes', {}];
	'entries':Record<string, Tentries__yes__has_result>;
	'has more entries than maximum':'no'|['no', {}]|['yes', Tyes__has_more_entries_than_maximum];
	'is empty':['no', Tno__is_empty__yes__has_result]|'yes'|['yes', {}];
	'selected entries':'all'|['all', {}]|'none'|['none', {}]|'some'|['some', {}];
	'total amount of entries':number;
};
export class Cyes__has_result extends AlanNode {
	public readonly properties:{
		readonly amount_of_entries_visible:Cyes__has_result.Damount_of_entries_visible,
		readonly backend_updates_are_available:Cyes__has_result.Dbackend_updates_are_available,
		readonly entries:Cyes__has_result.Dentries,
		readonly has_more_entries_than_maximum:Cyes__has_result.Dhas_more_entries_than_maximum,
		readonly is_empty:Cyes__has_result.Dis_empty,
		readonly selected_entries:Cyes__has_result.Dselected_entries,
		readonly total_amount_of_entries:Cyes__has_result.Dtotal_amount_of_entries
	};
	constructor(init:Tyes__has_result, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.properties = {
			amount_of_entries_visible: new Cyes__has_result.Damount_of_entries_visible(init['amount of entries visible'], $this),
			backend_updates_are_available: new Cyes__has_result.Dbackend_updates_are_available(init['backend updates are available'], $this),
			entries: new Cyes__has_result.Dentries(init['entries'], $this),
			has_more_entries_than_maximum: new Cyes__has_result.Dhas_more_entries_than_maximum(init['has more entries than maximum'], $this),
			is_empty: new Cyes__has_result.Dis_empty(init['is empty'], $this),
			selected_entries: new Cyes__has_result.Dselected_entries(init['selected entries'], $this),
			total_amount_of_entries: new Cyes__has_result.Dtotal_amount_of_entries(init['total amount of entries'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.amount_of_entries_visible.destroy();
		this.properties.backend_updates_are_available.destroy();
		this.properties.entries.destroy();
		this.properties.has_more_entries_than_maximum.destroy();
		this.properties.is_empty.destroy();
		this.properties.selected_entries.destroy();
		this.properties.total_amount_of_entries.destroy();
	}
	public set_amount_of_entries_visible(data:number):void {
		(this.properties.amount_of_entries_visible as Mutable<Cyes__has_result.Damount_of_entries_visible>).value = data;
		super.notify('amount of entries visible', data);
	}
	public subscribe_amount_of_entries_visible(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('amount of entries visible', observer);
		observer.on_update(this.properties.amount_of_entries_visible.value, subscription);
		return subscription;
	}
	public set_total_amount_of_entries(data:number):void {
		(this.properties.total_amount_of_entries as Mutable<Cyes__has_result.Dtotal_amount_of_entries>).value = data;
		super.notify('total amount of entries', data);
	}
	public subscribe_total_amount_of_entries(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('total amount of entries', observer);
		observer.on_update(this.properties.total_amount_of_entries.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has result'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__backend_updates_are_available = {
};
export class Cno__backend_updates_are_available extends AlanNode {
	constructor(init:Tno__backend_updates_are_available, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend updates are available'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__backend_updates_are_available = {
};
export class Cyes__backend_updates_are_available extends AlanNode {
	constructor(init:Tyes__backend_updates_are_available, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend updates are available'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__yes__has_result = {
	'cells':Record<string, Tcells>;
	'entry status':Tquery_entry_status;
	'has cells':'no'|['no', {}]|['yes', Tyes__has_cells];
	'is editable':'no'|['no', {}]|'yes'|['yes', {}];
	'is selected':'no'|['no', {}]|['yes', Tyes__is_selected__entries];
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
	'style':Tstyle;
	'use for':'candidates'|['candidates', {}]|'query'|['query', {}];
};
export class Centries__yes__has_result extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		query_entry_status_position: Cquery_entry_status_position;
	} = {
		query_entry_status_position: new Cquery_entry_status_position({name:'result entry', definition: this})
	}
	public readonly properties:{
		readonly cells:Centries__yes__has_result.Dcells,
		readonly entry_status:Cquery_entry_status,
		readonly has_cells:Centries__yes__has_result.Dhas_cells,
		readonly is_editable:Centries__yes__has_result.Dis_editable,
		readonly is_selected:Centries__yes__has_result.Dis_selected,
		readonly is_valid:Centries__yes__has_result.Dis_valid,
		readonly style:Cstyle,
		readonly use_for:Centries__yes__has_result.Duse_for
	};
	constructor(key:string, init:Tentries__yes__has_result, public parent:Cyes__has_result) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			cells: new Centries__yes__has_result.Dcells(init['cells'], $this),
			entry_status: new Centries__yes__has_result.Dentry_status(init['entry status'], $this),
			has_cells: new Centries__yes__has_result.Dhas_cells(init['has cells'], $this),
			is_editable: new Centries__yes__has_result.Dis_editable(init['is editable'], $this),
			is_selected: new Centries__yes__has_result.Dis_selected(init['is selected'], $this),
			is_valid: new Centries__yes__has_result.Dis_valid(init['is valid'], $this),
			style: new Centries__yes__has_result.Dstyle(init['style'], $this),
			use_for: new Centries__yes__has_result.Duse_for(init['use for'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.cells.destroy();
		this.properties.entry_status.destroy();
		this.properties.has_cells.destroy();
		this.properties.is_editable.destroy();
		this.properties.is_selected.destroy();
		this.properties.is_valid.destroy();
		this.properties.style.destroy();
		this.properties.use_for.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export class Kcells extends Reference<client_state.Ccolumns, string> {
	constructor(key:string, $this:Ccells) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => {
				const entry = context?.properties.columns.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cells'::key`; }
}
export type Tcells = {
	'column type':'content'|['content', {}]|'id'|['id', {}];
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor__cells];
	'type':['file', Tfile__type__cells]|['number', Tnumber__type__cells]|['state group', Tstate_group__type__cells]|['text', Ttext__type__cells]|'widget'|['widget', {}];
};
export class Ccells extends AlanDictionaryEntry {
	public key:Kcells;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly column_type:Ccells.Dcolumn_type,
		readonly has_successor:Ccells.Dhas_successor,
		readonly type:Ccells.Dtype
	};
	constructor(key:string, init:Tcells, public parent:Centries__yes__has_result) {
		super();
		const $this = this;
		this.key = new Kcells(key, $this);
		this.properties = {
			column_type: new Ccells.Dcolumn_type(init['column type'], $this),
			has_successor: new Ccells.Dhas_successor(init['has successor'], $this),
			type: new Ccells.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.column_type.destroy();
		this.properties.has_successor.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cells'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcontent__column_type__cells = {
};
export class Ccontent__column_type__cells extends AlanNode {
	constructor(init:Tcontent__column_type__cells, public parent:Ccells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'column type'?'content'`; }
	public get entity() { return this.parent.entity; }
}
export type Tid__column_type__cells = {
};
export class Cid__column_type__cells extends AlanNode {
	constructor(init:Tid__column_type__cells, public parent:Ccells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'column type'?'id'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_successor__cells = {
};
export class Cno__has_successor__cells extends AlanNode {
	constructor(init:Tno__has_successor__cells, public parent:Ccells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor__cells = {
	'successor':string;
};
export class Cyes__has_successor__cells extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor__cells.Dsuccessor
	};
	constructor(init:Tyes__has_successor__cells, public parent:Ccells) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor__cells.Dsuccessor(init['successor'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.successor.destroy();
	}
	public set_successor(value:string):void {
		this.properties.successor.unlink();
		(<(detach?:boolean) => void>(this.properties.successor as any).resolve)(true);
		(this.properties.successor as Mutable<Cyes__has_successor__cells.Dsuccessor>).entry = value;
		super.notify('successor', value);
	}
	public subscribe_successor(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('successor', observer);
		observer.on_update(this.properties.successor.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__cells = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__file__type__cells];
};
export class Cfile__type__cells extends AlanNode {
	public readonly properties:{
		readonly is_set:Cfile__type__cells.Dis_set
	};
	public readonly inferences:{
		column_type: () => client_state.Cfile__type__columns
	} = {
		column_type: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__cells__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as client_state.Cfile__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__cells, public parent:Ccells) {
		super();
		const $this = this;
		this.properties = {
			is_set: new Cfile__type__cells.Dis_set(init['is set'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_set.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__file__type__cells = {
};
export class Cno__is_set__file__type__cells extends AlanNode {
	constructor(init:Tno__is_set__file__type__cells, public parent:Cfile__type__cells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__file__type__cells = {
	'extension':string;
	'token':string;
};
export class Cyes__is_set__file__type__cells extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	constructor(init:Tyes__is_set__file__type__cells, public parent:Cfile__type__cells) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_extension(value:string):void {
		(this.properties as Mutable<Cyes__is_set__file__type__cells['properties']>).extension = value;
		super.notify('extension', value);
	}
	public subscribe_extension(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('extension', observer);
		observer.on_update(this.properties.extension, subscription);
		return subscription
	}
	public set_token(value:string):void {
		(this.properties as Mutable<Cyes__is_set__file__type__cells['properties']>).token = value;
		super.notify('token', value);
	}
	public subscribe_token(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('token', observer);
		observer.on_update(this.properties.token, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__cells = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__number__type__cells];
	'numerical type properties':Tnumerical_type_properties;
};
export class Cnumber__type__cells extends AlanNode {
	public readonly properties:{
		readonly is_set:Cnumber__type__cells.Dis_set,
		readonly numerical_type_properties:Cnumerical_type_properties
	};
	public readonly inferences:{
		column_type: () => client_state.Cnumber__type__columns
	} = {
		column_type: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__cells__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as client_state.Cnumber__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__cells, public parent:Ccells) {
		super();
		const $this = this;
		this.properties = {
			is_set: new Cnumber__type__cells.Dis_set(init['is set'], $this),
			numerical_type_properties: new Cnumber__type__cells.Dnumerical_type_properties(init['numerical type properties'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_set.destroy();
		this.properties.numerical_type_properties.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__number__type__cells = {
};
export class Cno__is_set__number__type__cells extends AlanNode {
	constructor(init:Tno__is_set__number__type__cells, public parent:Cnumber__type__cells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__number__type__cells = {
	'number':number;
	'textual representation':string;
};
export class Cyes__is_set__number__type__cells extends AlanNode {
	public readonly properties:{
		readonly number:Cyes__is_set__number__type__cells.Dnumber,
		readonly textual_representation:string
	};
	constructor(init:Tyes__is_set__number__type__cells, public parent:Cnumber__type__cells) {
		super();
		const $this = this;
		this.properties = {
			number: new Cyes__is_set__number__type__cells.Dnumber(init['number'], $this),
			textual_representation: init['textual representation']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.number.destroy();
	}
	public set_number(data:number):void {
		(this.properties.number as Mutable<Cyes__is_set__number__type__cells.Dnumber>).value = data;
		super.notify('number', data);
	}
	public subscribe_number(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('number', observer);
		observer.on_update(this.properties.number.value, subscription);
		return subscription;
	}
	public set_textual_representation(value:string):void {
		(this.properties as Mutable<Cyes__is_set__number__type__cells['properties']>).textual_representation = value;
		super.notify('textual representation', value);
	}
	public subscribe_textual_representation(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('textual representation', observer);
		observer.on_update(this.properties.textual_representation, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__cells = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__state_group__type__cells];
	'states':Record<string, Tstates__state_group__type__cells>;
};
export class Cstate_group__type__cells extends AlanNode {
	public readonly properties:{
		readonly is_set:Cstate_group__type__cells.Dis_set,
		readonly states:Cstate_group__type__cells.Dstates
	};
	public readonly inferences:{
		column_type: () => client_state.Cstate_group__type__columns
	} = {
		column_type: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__cells__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as client_state.Cstate_group__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__cells, public parent:Ccells) {
		super();
		const $this = this;
		this.properties = {
			is_set: new Cstate_group__type__cells.Dis_set(init['is set'], $this),
			states: new Cstate_group__type__cells.Dstates(init['states'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_set.destroy();
		this.properties.states.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__state_group__type__cells = {
};
export class Cno__is_set__state_group__type__cells extends AlanNode {
	constructor(init:Tno__is_set__state_group__type__cells, public parent:Cstate_group__type__cells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__state_group__type__cells = {
	'state':string;
	'style':Tstyle;
};
export class Cyes__is_set__state_group__type__cells extends AlanNode {
	public readonly properties:{
		readonly state:string,
		readonly style:Cstyle
	};
	constructor(init:Tyes__is_set__state_group__type__cells, public parent:Cstate_group__type__cells) {
		super();
		const $this = this;
		this.properties = {
			state: init['state'],
			style: new Cyes__is_set__state_group__type__cells.Dstyle(init['style'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.style.destroy();
	}
	public set_state(value:string):void {
		(this.properties as Mutable<Cyes__is_set__state_group__type__cells['properties']>).state = value;
		super.notify('state', value);
	}
	public subscribe_state(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('state', observer);
		observer.on_update(this.properties.state, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstates__state_group__type__cells = {
	'is selected':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cstates__state_group__type__cells extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly is_selected:Cstates__state_group__type__cells.Dis_selected
	};
	constructor(key:string, init:Tstates__state_group__type__cells, public parent:Cstate_group__type__cells) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			is_selected: new Cstates__state_group__type__cells.Dis_selected(init['is selected'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_selected.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__is_selected__states__state_group__type__cells = {
};
export class Cno__is_selected__states__state_group__type__cells extends AlanNode {
	constructor(init:Tno__is_selected__states__state_group__type__cells, public parent:Cstates__state_group__type__cells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_selected__states__state_group__type__cells = {
};
export class Cyes__is_selected__states__state_group__type__cells extends AlanNode {
	constructor(init:Tyes__is_selected__states__state_group__type__cells, public parent:Cstates__state_group__type__cells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__cells = {
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__text__type__cells];
};
export class Ctext__type__cells extends AlanNode {
	public readonly properties:{
		readonly is_set:Ctext__type__cells.Dis_set
	};
	public readonly inferences:{
		column_type: () => client_state.Ctext__type__columns
	} = {
		column_type: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__cells__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as client_state.Ctext__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__cells, public parent:Ccells) {
		super();
		const $this = this;
		this.properties = {
			is_set: new Ctext__type__cells.Dis_set(init['is set'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_set.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__text__type__cells = {
};
export class Cno__is_set__text__type__cells extends AlanNode {
	constructor(init:Tno__is_set__text__type__cells, public parent:Ctext__type__cells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__text__type__cells = {
	'text':string;
};
export class Cyes__is_set__text__type__cells extends AlanNode {
	public readonly properties:{
		readonly text:string
	};
	constructor(init:Tyes__is_set__text__type__cells, public parent:Ctext__type__cells) {
		super();
		const $this = this;
		this.properties = {
			text: init['text']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_text(value:string):void {
		(this.properties as Mutable<Cyes__is_set__text__type__cells['properties']>).text = value;
		super.notify('text', value);
	}
	public subscribe_text(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('text', observer);
		observer.on_update(this.properties.text, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Twidget__type__cells = {
};
export class Cwidget__type__cells extends AlanNode {
	public readonly inferences:{
		column_type: () => client_state.Cwidget__type__columns
	} = {
		column_type: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__cells__type__widget_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'widget') {
						return resolve(context.properties.type.state.node as client_state.Cwidget__type__columns).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twidget__type__cells, public parent:Ccells) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'widget'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_cells = {
};
export class Cno__has_cells extends AlanNode {
	constructor(init:Tno__has_cells, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has cells'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_cells = {
	'first':string;
};
export class Cyes__has_cells extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_cells.Dfirst
	};
	constructor(init:Tyes__has_cells, public parent:Centries__yes__has_result) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_cells.Dfirst(init['first'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.first.destroy();
	}
	public set_first(value:string):void {
		this.properties.first.unlink();
		(<(detach?:boolean) => void>(this.properties.first as any).resolve)(true);
		(this.properties.first as Mutable<Cyes__has_cells.Dfirst>).entry = value;
		super.notify('first', value);
	}
	public subscribe_first(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('first', observer);
		observer.on_update(this.properties.first.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has cells'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__entries = {
};
export class Cno__is_editable__entries extends AlanNode {
	constructor(init:Tno__is_editable__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__entries = {
};
export class Cyes__is_editable__entries extends AlanNode {
	constructor(init:Tyes__is_editable__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_selected__entries = {
};
export class Cno__is_selected__entries extends AlanNode {
	constructor(init:Tno__is_selected__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_selected__entries = {
	'selected entry':string;
};
export class Cyes__is_selected__entries extends AlanNode {
	public readonly properties:{
		readonly selected_entry:Cyes__is_selected__entries.Dselected_entry
	};
	public readonly inferences:{
		selection: () => client_state.Cyes__has_selected_entries
	} = {
		selection: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__is_selected__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.has_selected_entries.state.name === 'yes') {
						return resolve(context.properties.has_selected_entries.state.node as client_state.Cyes__has_selected_entries).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__is_selected__entries, public parent:Centries__yes__has_result) {
		super();
		const $this = this;
		this.properties = {
			selected_entry: new Cyes__is_selected__entries.Dselected_entry(init['selected entry'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.selected_entry.destroy();
	}
	public set_selected_entry(value:string):void {
		this.properties.selected_entry.unlink();
		(<(detach?:boolean) => void>(this.properties.selected_entry as any).resolve)(true);
		(this.properties.selected_entry as Mutable<Cyes__is_selected__entries.Dselected_entry>).entry = value;
		super.notify('selected entry', value);
	}
	public subscribe_selected_entry(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('selected entry', observer);
		observer.on_update(this.properties.selected_entry.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__entries = {
};
export class Cno__is_valid__entries extends AlanNode {
	constructor(init:Tno__is_valid__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__entries = {
};
export class Cyes__is_valid__entries extends AlanNode {
	constructor(init:Tyes__is_valid__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcandidates__use_for__entries = {
};
export class Ccandidates__use_for__entries extends AlanNode {
	public readonly inferences:{
		use: () => client_state.Ccandidates__use_for__query
	} = {
		use: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__use_for__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.use_for.state.name === 'candidates') {
						return resolve(context.properties.use_for.state.node as client_state.Ccandidates__use_for__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcandidates__use_for__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'use for'?'candidates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__use_for__entries = {
};
export class Cquery__use_for__entries extends AlanNode {
	public readonly inferences:{
		use: () => client_state.Cquery__use_for__query
	} = {
		use: cache((detach:boolean) => {
			const client_state__query__has_result__yes__entries__use_for__query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.use_for.state.name === 'query') {
						return resolve(context.properties.use_for.state.node as client_state.Cquery__use_for__query).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery__use_for__entries, public parent:Centries__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'use for'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_more_entries_than_maximum = {
};
export class Cno__has_more_entries_than_maximum extends AlanNode {
	constructor(init:Tno__has_more_entries_than_maximum, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has more entries than maximum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_more_entries_than_maximum = {
	'can show all':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__has_more_entries_than_maximum extends AlanNode {
	public readonly properties:{
		readonly can_show_all:Cyes__has_more_entries_than_maximum.Dcan_show_all
	};
	constructor(init:Tyes__has_more_entries_than_maximum, public parent:Cyes__has_result) {
		super();
		const $this = this;
		this.properties = {
			can_show_all: new Cyes__has_more_entries_than_maximum.Dcan_show_all(init['can show all'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.can_show_all.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has more entries than maximum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_show_all = {
};
export class Cno__can_show_all extends AlanNode {
	constructor(init:Tno__can_show_all, public parent:Cyes__has_more_entries_than_maximum) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can show all'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_show_all = {
};
export class Cyes__can_show_all extends AlanNode {
	constructor(init:Tyes__can_show_all, public parent:Cyes__has_more_entries_than_maximum) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can show all'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_empty__yes__has_result = {
	'result size':'multiple'|['multiple', {}]|'one'|['one', {}];
};
export class Cno__is_empty__yes__has_result extends AlanNode {
	public readonly properties:{
		readonly result_size:Cno__is_empty__yes__has_result.Dresult_size
	};
	constructor(init:Tno__is_empty__yes__has_result, public parent:Cyes__has_result) {
		super();
		const $this = this;
		this.properties = {
			result_size: new Cno__is_empty__yes__has_result.Dresult_size(init['result size'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.result_size.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is empty'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmultiple__result_size = {
};
export class Cmultiple__result_size extends AlanNode {
	constructor(init:Tmultiple__result_size, public parent:Cno__is_empty__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result size'?'multiple'`; }
	public get entity() { return this.parent.entity; }
}
export type Tone = {
};
export class Cone extends AlanNode {
	constructor(init:Tone, public parent:Cno__is_empty__yes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'result size'?'one'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_empty__yes__has_result = {
};
export class Cyes__is_empty__yes__has_result extends AlanNode {
	constructor(init:Tyes__is_empty__yes__has_result, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is empty'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tall = {
};
export class Call extends AlanNode {
	constructor(init:Tall, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'selected entries'?'all'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__selected_entries = {
};
export class Cnone__selected_entries extends AlanNode {
	constructor(init:Tnone__selected_entries, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'selected entries'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsome = {
};
export class Csome extends AlanNode {
	constructor(init:Tsome, public parent:Cyes__has_result) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'selected entries'?'some'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_selected_entries = {
};
export class Cno__has_selected_entries extends AlanNode {
	constructor(init:Tno__has_selected_entries, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has selected entries'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_selected_entries = {
	'number of editable selected entries':number;
	'number of selected entries':number;
	'selected entries':Record<string, Tselected_entries>;
};
export class Cyes__has_selected_entries extends AlanNode {
	public readonly properties:{
		readonly number_of_editable_selected_entries:Cyes__has_selected_entries.Dnumber_of_editable_selected_entries,
		readonly number_of_selected_entries:Cyes__has_selected_entries.Dnumber_of_selected_entries,
		readonly selected_entries:Cyes__has_selected_entries.Dselected_entries
	};
	constructor(init:Tyes__has_selected_entries, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.properties = {
			number_of_editable_selected_entries: new Cyes__has_selected_entries.Dnumber_of_editable_selected_entries(init['number of editable selected entries'], $this),
			number_of_selected_entries: new Cyes__has_selected_entries.Dnumber_of_selected_entries(init['number of selected entries'], $this),
			selected_entries: new Cyes__has_selected_entries.Dselected_entries(init['selected entries'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.number_of_editable_selected_entries.destroy();
		this.properties.number_of_selected_entries.destroy();
		this.properties.selected_entries.destroy();
	}
	public set_number_of_editable_selected_entries(data:number):void {
		(this.properties.number_of_editable_selected_entries as Mutable<Cyes__has_selected_entries.Dnumber_of_editable_selected_entries>).value = data;
		super.notify('number of editable selected entries', data);
	}
	public subscribe_number_of_editable_selected_entries(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('number of editable selected entries', observer);
		observer.on_update(this.properties.number_of_editable_selected_entries.value, subscription);
		return subscription;
	}
	public set_number_of_selected_entries(data:number):void {
		(this.properties.number_of_selected_entries as Mutable<Cyes__has_selected_entries.Dnumber_of_selected_entries>).value = data;
		super.notify('number of selected entries', data);
	}
	public subscribe_number_of_selected_entries(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('number of selected entries', observer);
		observer.on_update(this.properties.number_of_selected_entries.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has selected entries'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tselected_entries = {
	'entry status':Tquery_entry_status;
	'is editable':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cselected_entries extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		query_entry_status_position: Cquery_entry_status_position;
	} = {
		query_entry_status_position: new Cquery_entry_status_position({name:'selected entry', definition: this})
	}
	public readonly properties:{
		readonly entry_status:Cquery_entry_status,
		readonly is_editable:Cselected_entries.Dis_editable
	};
	constructor(key:string, init:Tselected_entries, public parent:Cyes__has_selected_entries) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			entry_status: new Cselected_entries.Dentry_status(init['entry status'], $this),
			is_editable: new Cselected_entries.Dis_editable(init['is editable'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.entry_status.destroy();
		this.properties.is_editable.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'selected entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__is_editable__selected_entries = {
};
export class Cno__is_editable__selected_entries extends AlanNode {
	constructor(init:Tno__is_editable__selected_entries, public parent:Cselected_entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__selected_entries = {
};
export class Cyes__is_editable__selected_entries extends AlanNode {
	constructor(init:Tyes__is_editable__selected_entries, public parent:Cselected_entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_busy_querying = {
	'request status':'failure'|['failure', {}]|'success'|['success', {}];
};
export class Cno__is_busy_querying extends AlanNode {
	public readonly properties:{
		readonly request_status:Cno__is_busy_querying.Drequest_status
	};
	constructor(init:Tno__is_busy_querying, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.properties = {
			request_status: new Cno__is_busy_querying.Drequest_status(init['request status'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.request_status.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is busy querying'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__request_status = {
};
export class Cfailure__request_status extends AlanNode {
	constructor(init:Tfailure__request_status, public parent:Cno__is_busy_querying) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'request status'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__request_status = {
};
export class Csuccess__request_status extends AlanNode {
	constructor(init:Tsuccess__request_status, public parent:Cno__is_busy_querying) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'request status'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_busy_querying = {
	'requery needed':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__is_busy_querying extends AlanNode {
	public readonly properties:{
		readonly requery_needed:Cyes__is_busy_querying.Drequery_needed
	};
	constructor(init:Tyes__is_busy_querying, public parent:Cquery__client_state) {
		super();
		const $this = this;
		this.properties = {
			requery_needed: new Cyes__is_busy_querying.Drequery_needed(init['requery needed'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.requery_needed.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is busy querying'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__requery_needed = {
};
export class Cno__requery_needed extends AlanNode {
	constructor(init:Tno__requery_needed, public parent:Cyes__is_busy_querying) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'requery needed'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__requery_needed = {
};
export class Cyes__requery_needed extends AlanNode {
	constructor(init:Tyes__requery_needed, public parent:Cyes__is_busy_querying) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'requery needed'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__show_all = {
};
export class Cno__show_all extends AlanNode {
	constructor(init:Tno__show_all, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'show all'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__show_all = {
};
export class Cyes__show_all extends AlanNode {
	constructor(init:Tyes__show_all, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'show all'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcandidates__use_for__query = {
};
export class Ccandidates__use_for__query extends AlanNode {
	public readonly inferences:{
		candidates_context: () => views.Ccandidates__context,
		reference_property: () => client_state.Cqueries__yes
	} = {
		candidates_context: cache((detach:boolean) => {
			const client_state__query__use_for__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.definition?.ref)
				.then(context => {
					if (context?.properties.context.state.name === 'candidates') {
						return resolve(context.properties.context.state.node as views.Ccandidates__context).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		reference_property: cache((detach:boolean) => {
			const client_state__query__use_for__candidates_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.position())
				.then(context => context?.variant.name === 'navigable text property' ? context.variant.definition as client_state.Cqueries__yes : undefined).result!;
		})

	}
	constructor(init:Tcandidates__use_for__query, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'use for'?'candidates'`; }
	public get entity() { return this.parent.entity; }
}
export type Tquery__use_for__query = {
};
export class Cquery__use_for__query extends AlanNode {
	public readonly inferences:{
		node: () => client_state.Cnode,
		node_context: () => views.Cnode__context
	} = {
		node: cache((detach:boolean) => {
			const client_state__query__use_for__query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.position())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as client_state.Cnode : undefined).result!;
		})
		,
		node_context: cache((detach:boolean) => {
			const client_state__query__use_for__query_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.definition?.ref)
				.then(context => {
					if (context?.properties.context.state.name === 'node') {
						return resolve(context.properties.context.state.node as views.Cnode__context).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tquery__use_for__query, public parent:Cquery__client_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'use for'?'query'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumerical_type_properties = {
	'initialized':'no'|['no', {}]|['yes', Tyes__initialized__numerical_type_properties];
};
export class Cnumerical_type_properties extends AlanNode {
	public readonly properties:{
		readonly initialized:Cnumerical_type_properties.Dinitialized
	};
	constructor(init:Tnumerical_type_properties, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			initialized: new Cnumerical_type_properties.Dinitialized(init['initialized'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.initialized.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__initialized__numerical_type_properties = {
};
export class Cno__initialized__numerical_type_properties extends AlanNode {
	constructor(init:Tno__initialized__numerical_type_properties, public parent:Cnumerical_type_properties) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__initialized__numerical_type_properties = {
	'decimals':number;
	'unit':string;
};
export class Cyes__initialized__numerical_type_properties extends AlanNode {
	public readonly properties:{
		readonly decimals:Cyes__initialized__numerical_type_properties.Ddecimals,
		readonly unit:string
	};
	constructor(init:Tyes__initialized__numerical_type_properties, public parent:Cnumerical_type_properties) {
		super();
		const $this = this;
		this.properties = {
			decimals: new Cyes__initialized__numerical_type_properties.Ddecimals(init['decimals'], $this),
			unit: init['unit']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.decimals.destroy();
	}
	public set_decimals(data:number):void {
		(this.properties.decimals as Mutable<Cyes__initialized__numerical_type_properties.Ddecimals>).value = data;
		super.notify('decimals', data);
	}
	public subscribe_decimals(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('decimals', observer);
		observer.on_update(this.properties.decimals.value, subscription);
		return subscription;
	}
	public set_unit(value:string):void {
		(this.properties as Mutable<Cyes__initialized__numerical_type_properties['properties']>).unit = value;
		super.notify('unit', value);
	}
	public subscribe_unit(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('unit', observer);
		observer.on_update(this.properties.unit, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
type Vnode_parent = { name: 'node', definition: client_state.Cnode}|{ name: 'none', definition: (typeof Cnode_parent.Pnone)}
export class Cnode_parent extends AlanStruct {
	public static Pnone:Cnode_parent = new class PrimitiveInstance extends Cnode_parent {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Cnode_parent})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vnode_parent) { super(); }
	public cast<K extends Vnode_parent['name']>(_variant:K):Extract<Vnode_parent, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vnode_parent['name']]:(($:Extract<Vnode_parent, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'node parent'`; }
	public is(other:Cnode_parent):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__node_parent = client_state.Cnode_parent;
type Ventity = { name: 'root', definition: client_state.Cclient_state}|{ name: 'entry', definition: client_state.Centries__collection}
export class Centity extends AlanStruct {
	constructor(
		public readonly variant:Ventity, public input: {
			node: () => client_state.Cnode,
			parent: () => client_state.Cnode_parent,
			root: () => client_state.Croot_position
		}) { super(); }
	public definitions:{
		node_position: Cnode_position;
	} = {
		node_position: new Cnode_position({name:'entity', definition: this}, {
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.parent()).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.root()).result!)
		})
	}
	public readonly output:{
		node: () => client_state.Cnode;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	};
	public cast<K extends Ventity['name']>(_variant:K):Extract<Ventity, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Ventity['name']]:(($:Extract<Ventity, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'entity'`; }
	public is(other:Centity):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tnode = {
	'attributes':Record<string, Tattributes>;
	'can be deleted':'no'|['no', {}]|'yes'|['yes', {}];
	'is busy discarding':'no'|['no', {}]|'yes'|['yes', {}];
	'is busy saving':'no'|['no', {}]|'yes'|['yes', {}];
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
	'location':['local', Tlocal__location__node]|['remote', Tremote__location__node];
	'modified':'no'|['no', {}]|'yes'|['yes', {}];
	'queries':Record<string, Tqueries__node>;
	'read permitted':'no'|['no', {}]|'yes'|['yes', {}];
	'report types':Record<string, Treport_types__node>;
	'todo':'no'|['no', {}]|['yes', Tyes__todo];
};
export class Cnode extends AlanNode {
	public subscribed_on_valid: boolean;
	public subscribed_on_change: boolean;
	public definitions:{
		node_parent: Cnode_parent;
		query_position: Cquery_position;
	} = {
		node_parent: new Cnode_parent({name:'node', definition: this}),
		query_position: new Cquery_position({name:'node', definition: this})
	}
	public readonly properties:{
		readonly attributes:Cnode.Dattributes,
		readonly can_be_deleted:Cnode.Dcan_be_deleted,
		readonly is_busy_discarding:Cnode.Dis_busy_discarding,
		readonly is_busy_saving:Cnode.Dis_busy_saving,
		readonly is_valid:Cnode.Dis_valid,
		readonly location:Cnode.Dlocation,
		readonly modified:Cnode.Dmodified,
		readonly queries:Cnode.Dqueries,
		readonly read_permitted:Cnode.Dread_permitted,
		readonly report_types:Cnode.Dreport_types,
		readonly todo:Cnode.Dtodo
	};
	public readonly output:{
		node_type: () => application.Cnode__application;
		position: () => client_state.Cnode_position;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node_type()).result!),
		position: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.position()).result!)
	};
	constructor(init:Tnode, public location:AlanNode, public input: {
		entity: () => client_state.Centity,
		location: () => client_state.Clocation,
		node_type: () => application.Cnode__application,
		position: () => client_state.Cnode_position
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cnode.Dattributes(init['attributes'], $this),
			can_be_deleted: new Cnode.Dcan_be_deleted(init['can be deleted'], $this),
			is_busy_discarding: new Cnode.Dis_busy_discarding(init['is busy discarding'], $this),
			is_busy_saving: new Cnode.Dis_busy_saving(init['is busy saving'], $this),
			is_valid: new Cnode.Dis_valid(init['is valid'], $this),
			location: new Cnode.Dlocation(init['location'], $this),
			modified: new Cnode.Dmodified(init['modified'], $this),
			queries: new Cnode.Dqueries(init['queries'], $this),
			read_permitted: new Cnode.Dread_permitted(init['read permitted'], $this),
			report_types: new Cnode.Dreport_types(init['report types'], $this),
			todo: new Cnode.Dtodo(init['todo'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.attributes.destroy();
		this.properties.can_be_deleted.destroy();
		this.properties.is_busy_discarding.destroy();
		this.properties.is_busy_saving.destroy();
		this.properties.is_valid.destroy();
		this.properties.location.destroy();
		this.properties.modified.destroy();
		this.properties.queries.destroy();
		this.properties.read_permitted.destroy();
		this.properties.report_types.destroy();
		this.properties.todo.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kattributes extends Reference<application.Cattributes, string> {
	constructor(key:string, $this:Cattributes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.node_type())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes = {
	'type':['operation', Toperation__type__attributes]|['property', Tproperty];
};
export class Cattributes extends AlanDictionaryEntry {
	public key:Kattributes;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly type:Cattributes.Dtype
	};
	constructor(key:string, init:Tattributes, public parent:Cnode) {
		super();
		const $this = this;
		this.key = new Kattributes(key, $this);
		this.properties = {
			type: new Cattributes.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Toperation__type__attributes = {
	'started':'no'|['no', {}]|['yes', Tyes__started];
	'type':'action'|['action', {}]|['command', Tcommand];
};
export class Coperation__type__attributes extends AlanNode {
	public readonly properties:{
		readonly started:Coperation__type__attributes.Dstarted,
		readonly type:Coperation__type__attributes.Dtype
	};
	constructor(init:Toperation__type__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			started: new Coperation__type__attributes.Dstarted(init['started'], $this),
			type: new Coperation__type__attributes.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.started.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__started = {
};
export class Cno__started extends AlanNode {
	constructor(init:Tno__started, public parent:Coperation__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'started'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__started = {
	'executable':'no'|['no', {}]|'yes'|['yes', {}];
	'executing':['no', Tno__executing]|['yes', Tyes__executing];
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
	'node':Tnode;
};
export class Cyes__started extends AlanNode {
	public definitions:{
		root_position: Croot_position;
	} = {
		root_position: new Croot_position({name:'operation', definition: this})
	}
	public readonly properties:{
		readonly executable:Cyes__started.Dexecutable,
		readonly executing:Cyes__started.Dexecuting,
		readonly is_valid:Cyes__started.Dis_valid,
		readonly node:Cnode
	};
	constructor(init:Tyes__started, public parent:Coperation__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			executable: new Cyes__started.Dexecutable(init['executable'], $this),
			executing: new Cyes__started.Dexecuting(init['executing'], $this),
			is_valid: new Cyes__started.Dis_valid(init['is valid'], $this),
			node: new Cyes__started.Dnode(init['node'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.executable.destroy();
		this.properties.executing.destroy();
		this.properties.is_valid.destroy();
		this.properties.node.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'started'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__executable = {
};
export class Cno__executable extends AlanNode {
	constructor(init:Tno__executable, public parent:Cyes__started) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'executable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__executable = {
};
export class Cyes__executable extends AlanNode {
	constructor(init:Tyes__executable, public parent:Cyes__started) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'executable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__executing = {
	'last result':'failure'|['failure', {}]|'not invoked'|['not invoked', {}]|'success'|['success', {}];
};
export class Cno__executing extends AlanNode {
	public readonly properties:{
		readonly last_result:Cno__executing.Dlast_result
	};
	constructor(init:Tno__executing, public parent:Cyes__started) {
		super();
		const $this = this;
		this.properties = {
			last_result: new Cno__executing.Dlast_result(init['last result'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.last_result.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'executing'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__last_result = {
};
export class Cfailure__last_result extends AlanNode {
	constructor(init:Tfailure__last_result, public parent:Cno__executing) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last result'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_invoked = {
};
export class Cnot_invoked extends AlanNode {
	constructor(init:Tnot_invoked, public parent:Cno__executing) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last result'?'not invoked'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__last_result = {
};
export class Csuccess__last_result extends AlanNode {
	constructor(init:Tsuccess__last_result, public parent:Cno__executing) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last result'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__executing = {
	'can continue':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__executing extends AlanNode {
	public readonly properties:{
		readonly can_continue:Cyes__executing.Dcan_continue
	};
	constructor(init:Tyes__executing, public parent:Cyes__started) {
		super();
		const $this = this;
		this.properties = {
			can_continue: new Cyes__executing.Dcan_continue(init['can continue'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.can_continue.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'executing'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_continue = {
};
export class Cno__can_continue extends AlanNode {
	constructor(init:Tno__can_continue, public parent:Cyes__executing) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can continue'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_continue = {
};
export class Cyes__can_continue extends AlanNode {
	constructor(init:Tyes__can_continue, public parent:Cyes__executing) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can continue'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__yes = {
};
export class Cno__is_valid__yes extends AlanNode {
	constructor(init:Tno__is_valid__yes, public parent:Cyes__started) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__yes = {
};
export class Cyes__is_valid__yes extends AlanNode {
	constructor(init:Tyes__is_valid__yes, public parent:Cyes__started) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Taction = {
};
export class Caction extends AlanNode {
	public readonly output:{
		parameter_definition: () => application.Cnode__application;
	} = {
		parameter_definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.action_type())
			.then(context => context?.properties.parameters).result!)
	}
	public readonly inferences:{
		action_type: () => application.Caction
	} = {
		action_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__operation__type__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'action') {
						return resolve(context.properties.type.state.node as application.Caction).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Taction, public parent:Coperation__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'action'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand = {
	'execute permitted':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Ccommand extends AlanNode {
	public readonly properties:{
		readonly execute_permitted:Ccommand.Dexecute_permitted
	};
	public readonly output:{
		parameter_definition: () => application.Cnode__application;
	} = {
		parameter_definition: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.command_type())
			.then(context => context?.properties.parameters).result!)
	}
	public readonly inferences:{
		command_type: () => application.Ccommand__type,
		global_type: () => application.Cglobal
	} = {
		command_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__operation__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'command') {
						return resolve(context.properties.type.state.node as application.Ccommand__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		global_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__operation__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.command_type())
				.then(context => {
					if (context?.properties.type.state.name === 'global') {
						return resolve(context.properties.type.state.node as application.Cglobal).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand, public parent:Coperation__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			execute_permitted: new Ccommand.Dexecute_permitted(init['execute permitted'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.execute_permitted.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__execute_permitted = {
};
export class Cno__execute_permitted extends AlanNode {
	constructor(init:Tno__execute_permitted, public parent:Ccommand) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'execute permitted'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__execute_permitted = {
};
export class Cyes__execute_permitted extends AlanNode {
	constructor(init:Tyes__execute_permitted, public parent:Ccommand) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'execute permitted'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty = {
	'type':['collection', Tcollection]|['file', Tfile__type__property]|['group', Tgroup__type__property]|['number', Tnumber__type__property]|['state group', Tstate_group__type__property]|['text', Ttext__type__property];
};
export class Cproperty extends AlanNode {
	public readonly properties:{
		readonly type:Cproperty.Dtype
	};
	public readonly inferences:{
		property_type: () => application.Cproperty__type__attributes
	} = {
		property_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as application.Cproperty__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproperty, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cproperty.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection = {
	'amount of entries':number;
	'can create entry':'no'|['no', {}]|'yes'|['yes', {}];
	'can download files':'no'|['no', {}]|'yes'|['yes', {}];
	'entries':Record<string, Tentries__collection>;
	'has ordered graphs':'no'|['no', {}]|['yes', Tyes__has_ordered_graphs];
	'is editable':'no'|['no', {}]|['yes', Tyes__is_editable__collection];
	'is empty':['no', Tno__is_empty__collection]|'yes'|['yes', {}];
	'is valid':['no', Tno__is_valid__collection]|'yes'|['yes', {}];
	'joined':'no'|['no', {}]|['yes', Tyes__joined];
	'last created entry':['entry', Tentry__last_created_entry]|'none'|['none', {}];
	'modified':'no'|['no', {}]|['yes', Tyes__modified__collection];
};
export class Ccollection extends AlanNode {
	public readonly properties:{
		readonly amount_of_entries:Ccollection.Damount_of_entries,
		readonly can_create_entry:Ccollection.Dcan_create_entry,
		readonly can_download_files:Ccollection.Dcan_download_files,
		readonly entries:Ccollection.Dentries,
		readonly has_ordered_graphs:Ccollection.Dhas_ordered_graphs,
		readonly is_editable:Ccollection.Dis_editable,
		readonly is_empty:Ccollection.Dis_empty,
		readonly is_valid:Ccollection.Dis_valid,
		readonly joined:Ccollection.Djoined,
		readonly last_created_entry:Ccollection.Dlast_created_entry,
		readonly modified:Ccollection.Dmodified
	};
	public readonly inferences:{
		collection_type: () => application.Ccollection__type__property
	} = {
		collection_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			amount_of_entries: new Ccollection.Damount_of_entries(init['amount of entries'], $this),
			can_create_entry: new Ccollection.Dcan_create_entry(init['can create entry'], $this),
			can_download_files: new Ccollection.Dcan_download_files(init['can download files'], $this),
			entries: new Ccollection.Dentries(init['entries'], $this),
			has_ordered_graphs: new Ccollection.Dhas_ordered_graphs(init['has ordered graphs'], $this),
			is_editable: new Ccollection.Dis_editable(init['is editable'], $this),
			is_empty: new Ccollection.Dis_empty(init['is empty'], $this),
			is_valid: new Ccollection.Dis_valid(init['is valid'], $this),
			joined: new Ccollection.Djoined(init['joined'], $this),
			last_created_entry: new Ccollection.Dlast_created_entry(init['last created entry'], $this),
			modified: new Ccollection.Dmodified(init['modified'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.amount_of_entries.destroy();
		this.properties.can_create_entry.destroy();
		this.properties.can_download_files.destroy();
		this.properties.entries.destroy();
		this.properties.has_ordered_graphs.destroy();
		this.properties.is_editable.destroy();
		this.properties.is_empty.destroy();
		this.properties.is_valid.destroy();
		this.properties.joined.destroy();
		this.properties.last_created_entry.destroy();
		this.properties.modified.destroy();
	}
	public set_amount_of_entries(data:number):void {
		(this.properties.amount_of_entries as Mutable<Ccollection.Damount_of_entries>).value = data;
		super.notify('amount of entries', data);
	}
	public subscribe_amount_of_entries(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('amount of entries', observer);
		observer.on_update(this.properties.amount_of_entries.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_create_entry__collection = {
};
export class Cno__can_create_entry__collection extends AlanNode {
	constructor(init:Tno__can_create_entry__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can create entry'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_create_entry__collection = {
};
export class Cyes__can_create_entry__collection extends AlanNode {
	constructor(init:Tyes__can_create_entry__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can create entry'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_download_files__collection = {
};
export class Cno__can_download_files__collection extends AlanNode {
	constructor(init:Tno__can_download_files__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can download files'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_download_files__collection = {
};
export class Cyes__can_download_files__collection extends AlanNode {
	constructor(init:Tyes__can_download_files__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can download files'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__collection = {
	'key':string;
	'location':['local', Tlocal__location__entries]|['remote', Tremote__location__entries];
	'node':Tnode;
	'style':Tstyle;
};
export class Centries__collection extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		entity: Centity;
	} = {
		entity: new Centity({name:'entry', definition: this}, {
			node: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.node).result!),
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.position())
				.then(context => context?.component_root.output.root()).result!)
		})
	}
	public readonly properties:{
		readonly key:Centries__collection.Dkey,
		readonly location:Centries__collection.Dlocation,
		readonly node:Cnode,
		readonly style:Cstyle
	};
	constructor(key:string, init:Tentries__collection, public parent:Ccollection) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			key: new Centries__collection.Dkey(init['key'], $this),
			location: new Centries__collection.Dlocation(init['location'], $this),
			node: new Centries__collection.Dnode(init['node'], $this),
			style: new Centries__collection.Dstyle(init['style'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.key.destroy();
		this.properties.location.destroy();
		this.properties.node.destroy();
		this.properties.style.destroy();
	}
	public set_key(value:string):void {
		this.properties.key.unlink();
		(<(detach?:boolean) => void>(this.properties.key as any).resolve)(true);
		(this.properties.key as Mutable<Centries__collection.Dkey>).entry = value;
		super.notify('key', value);
	}
	public subscribe_key(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('key', observer);
		observer.on_update(this.properties.key.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tlocal__location__entries = {
	'modified by':'engine'|['engine', {}]|'user'|['user', {}];
};
export class Clocal__location__entries extends AlanNode {
	public readonly properties:{
		readonly modified_by:Clocal__location__entries.Dmodified_by
	};
	public readonly output:{
		entry_node_location: () => client_state.Clocation;
	} = {
		entry_node_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_state.Clocation.Plocal).result!)
	}
	constructor(init:Tlocal__location__entries, public parent:Centries__collection) {
		super();
		const $this = this;
		this.properties = {
			modified_by: new Clocal__location__entries.Dmodified_by(init['modified by'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.modified_by.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'location'?'local'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengine__modified_by = {
};
export class Cengine__modified_by extends AlanNode {
	constructor(init:Tengine__modified_by, public parent:Clocal__location__entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified by'?'engine'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__modified_by = {
};
export class Cuser__modified_by extends AlanNode {
	constructor(init:Tuser__modified_by, public parent:Clocal__location__entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified by'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremote__location__entries = {
	'marked for deletion':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cremote__location__entries extends AlanNode {
	public readonly properties:{
		readonly marked_for_deletion:Cremote__location__entries.Dmarked_for_deletion
	};
	public readonly output:{
		entry_node_location: () => client_state.Clocation;
	} = {
		entry_node_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_state.Clocation.Premote).result!)
	}
	public readonly inferences:{
		parent_exists: () => client_state.Cremote__location__node
	} = {
		parent_exists: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__collection__entries__location__remote_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.location.state.name === 'remote') {
						return resolve(context.properties.location.state.node as client_state.Cremote__location__node).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tremote__location__entries, public parent:Centries__collection) {
		super();
		const $this = this;
		this.properties = {
			marked_for_deletion: new Cremote__location__entries.Dmarked_for_deletion(init['marked for deletion'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.marked_for_deletion.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'location'?'remote'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__marked_for_deletion = {
};
export class Cno__marked_for_deletion extends AlanNode {
	constructor(init:Tno__marked_for_deletion, public parent:Cremote__location__entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'marked for deletion'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__marked_for_deletion = {
};
export class Cyes__marked_for_deletion extends AlanNode {
	constructor(init:Tyes__marked_for_deletion, public parent:Cremote__location__entries) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'marked for deletion'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_ordered_graphs = {
};
export class Cno__has_ordered_graphs extends AlanNode {
	constructor(init:Tno__has_ordered_graphs, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has ordered graphs'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_ordered_graphs = {
	'ordered graphs':Record<string, Tordered_graphs>;
};
export class Cyes__has_ordered_graphs extends AlanNode {
	public readonly properties:{
		readonly ordered_graphs:Cyes__has_ordered_graphs.Dordered_graphs
	};
	constructor(init:Tyes__has_ordered_graphs, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			ordered_graphs: new Cyes__has_ordered_graphs.Dordered_graphs(init['ordered graphs'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.ordered_graphs.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has ordered graphs'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kordered_graphs extends Reference<application.Cordered, string> {
	constructor(key:string, $this:Cordered_graphs) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection_type())
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				const entry = context?.properties.graphs.get(this.entry)!;
				return resolve(entry)
				.then(context => {
					if (context?.properties.type.state.name === 'ordered') {
						return context.properties.type.state.node as application.Cordered;
					} else {
						return undefined;
					}
				}).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'::key`; }
}
export type Tordered_graphs = {
	'is valid':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cordered_graphs extends AlanDictionaryEntry {
	public key:Kordered_graphs;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly is_valid:Cordered_graphs.Dis_valid
	};
	constructor(key:string, init:Tordered_graphs, public parent:Cyes__has_ordered_graphs) {
		super();
		const $this = this;
		this.key = new Kordered_graphs(key, $this);
		this.properties = {
			is_valid: new Cordered_graphs.Dis_valid(init['is valid'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.is_valid.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__is_valid__ordered_graphs = {
};
export class Cno__is_valid__ordered_graphs extends AlanNode {
	constructor(init:Tno__is_valid__ordered_graphs, public parent:Cordered_graphs) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__ordered_graphs = {
};
export class Cyes__is_valid__ordered_graphs extends AlanNode {
	constructor(init:Tyes__is_valid__ordered_graphs, public parent:Cordered_graphs) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__collection = {
};
export class Cno__is_editable__collection extends AlanNode {
	constructor(init:Tno__is_editable__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__collection = {
	'edit mode':'off'|['off', {}]|'on'|['on', {}];
};
export class Cyes__is_editable__collection extends AlanNode {
	public readonly properties:{
		readonly edit_mode:Cyes__is_editable__collection.Dedit_mode
	};
	constructor(init:Tyes__is_editable__collection, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			edit_mode: new Cyes__is_editable__collection.Dedit_mode(init['edit mode'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.edit_mode.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Toff__edit_mode__yes__is_editable__collection = {
};
export class Coff__edit_mode__yes__is_editable__collection extends AlanNode {
	constructor(init:Toff__edit_mode__yes__is_editable__collection, public parent:Cyes__is_editable__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'off'`; }
	public get entity() { return this.parent.entity; }
}
export type Ton__edit_mode__yes__is_editable__collection = {
};
export class Con__edit_mode__yes__is_editable__collection extends AlanNode {
	constructor(init:Ton__edit_mode__yes__is_editable__collection, public parent:Cyes__is_editable__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'on'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_empty__collection = {
	'size':'multiple'|['multiple', {}]|'single'|['single', {}];
};
export class Cno__is_empty__collection extends AlanNode {
	public readonly properties:{
		readonly size:Cno__is_empty__collection.Dsize
	};
	constructor(init:Tno__is_empty__collection, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			size: new Cno__is_empty__collection.Dsize(init['size'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.size.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is empty'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmultiple__size__no__is_empty__collection = {
};
export class Cmultiple__size__no__is_empty__collection extends AlanNode {
	constructor(init:Tmultiple__size__no__is_empty__collection, public parent:Cno__is_empty__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'size'?'multiple'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsingle__size__no__is_empty__collection = {
};
export class Csingle__size__no__is_empty__collection extends AlanNode {
	constructor(init:Tsingle__size__no__is_empty__collection, public parent:Cno__is_empty__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'size'?'single'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_empty__collection = {
};
export class Cyes__is_empty__collection extends AlanNode {
	constructor(init:Tyes__is_empty__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is empty'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__collection = {
	'cardinality constraint violation':'no'|['no', {}]|'yes'|['yes', {}];
	'entries are invalid':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cno__is_valid__collection extends AlanNode {
	public readonly properties:{
		readonly cardinality_constraint_violation:Cno__is_valid__collection.Dcardinality_constraint_violation,
		readonly entries_are_invalid:Cno__is_valid__collection.Dentries_are_invalid
	};
	constructor(init:Tno__is_valid__collection, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			cardinality_constraint_violation: new Cno__is_valid__collection.Dcardinality_constraint_violation(init['cardinality constraint violation'], $this),
			entries_are_invalid: new Cno__is_valid__collection.Dentries_are_invalid(init['entries are invalid'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.cardinality_constraint_violation.destroy();
		this.properties.entries_are_invalid.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__cardinality_constraint_violation = {
};
export class Cno__cardinality_constraint_violation extends AlanNode {
	constructor(init:Tno__cardinality_constraint_violation, public parent:Cno__is_valid__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cardinality constraint violation'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__cardinality_constraint_violation = {
};
export class Cyes__cardinality_constraint_violation extends AlanNode {
	constructor(init:Tyes__cardinality_constraint_violation, public parent:Cno__is_valid__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cardinality constraint violation'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__entries_are_invalid = {
};
export class Cno__entries_are_invalid extends AlanNode {
	constructor(init:Tno__entries_are_invalid, public parent:Cno__is_valid__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries are invalid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__entries_are_invalid = {
};
export class Cyes__entries_are_invalid extends AlanNode {
	constructor(init:Tyes__entries_are_invalid, public parent:Cno__is_valid__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries are invalid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__collection = {
};
export class Cyes__is_valid__collection extends AlanNode {
	constructor(init:Tyes__is_valid__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__joined = {
};
export class Cno__joined extends AlanNode {
	constructor(init:Tno__joined, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'joined'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__joined = {
	'amount of entries':number;
	'entries':Record<string, Tentries__yes__joined>;
	'is empty':['no', Tno__is_empty__yes__joined]|'yes'|['yes', {}];
	'joined collection':['resolved', Tresolved__joined_collection]|'unresolved'|['unresolved', {}];
};
export class Cyes__joined extends AlanNode {
	public readonly properties:{
		readonly amount_of_entries:Cyes__joined.Damount_of_entries,
		readonly entries:Cyes__joined.Dentries,
		readonly is_empty:Cyes__joined.Dis_empty,
		readonly joined_collection:Cyes__joined.Djoined_collection
	};
	constructor(init:Tyes__joined, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			amount_of_entries: new Cyes__joined.Damount_of_entries(init['amount of entries'], $this),
			entries: new Cyes__joined.Dentries(init['entries'], $this),
			is_empty: new Cyes__joined.Dis_empty(init['is empty'], $this),
			joined_collection: new Cyes__joined.Djoined_collection(init['joined collection'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.amount_of_entries.destroy();
		this.properties.entries.destroy();
		this.properties.is_empty.destroy();
		this.properties.joined_collection.destroy();
	}
	public set_amount_of_entries(data:number):void {
		(this.properties.amount_of_entries as Mutable<Cyes__joined.Damount_of_entries>).value = data;
		super.notify('amount of entries', data);
	}
	public subscribe_amount_of_entries(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('amount of entries', observer);
		observer.on_update(this.properties.amount_of_entries.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'joined'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentries__yes__joined = {
	'collection entry':['entry', Tentry__collection_entry]|'none'|['none', {}];
	'referenced entry':['entry', Tentry__referenced_entry]|'none'|['none', {}];
};
export class Centries__yes__joined extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly collection_entry:Centries__yes__joined.Dcollection_entry,
		readonly referenced_entry:Centries__yes__joined.Dreferenced_entry
	};
	constructor(key:string, init:Tentries__yes__joined, public parent:Cyes__joined) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			collection_entry: new Centries__yes__joined.Dcollection_entry(init['collection entry'], $this),
			referenced_entry: new Centries__yes__joined.Dreferenced_entry(init['referenced entry'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.collection_entry.destroy();
		this.properties.referenced_entry.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'entries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tentry__collection_entry = {
	'entry':string;
};
export class Centry__collection_entry extends AlanNode {
	public readonly properties:{
		readonly entry:Centry__collection_entry.Dentry
	};
	constructor(init:Tentry__collection_entry, public parent:Centries__yes__joined) {
		super();
		const $this = this;
		this.properties = {
			entry: new Centry__collection_entry.Dentry(init['entry'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.entry.destroy();
	}
	public set_entry(value:string):void {
		this.properties.entry.unlink();
		(<(detach?:boolean) => void>(this.properties.entry as any).resolve)(true);
		(this.properties.entry as Mutable<Centry__collection_entry.Dentry>).entry = value;
		super.notify('entry', value);
	}
	public subscribe_entry(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('entry', observer);
		observer.on_update(this.properties.entry.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'collection entry'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__collection_entry = {
};
export class Cnone__collection_entry extends AlanNode {
	constructor(init:Tnone__collection_entry, public parent:Centries__yes__joined) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'collection entry'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__referenced_entry = {
	'can create entry':'no'|['no', {}]|'yes'|['yes', {}];
	'entry':string;
};
export class Centry__referenced_entry extends AlanNode {
	public readonly properties:{
		readonly can_create_entry:Centry__referenced_entry.Dcan_create_entry,
		readonly entry:Centry__referenced_entry.Dentry
	};
	public readonly inferences:{
		joined_collection: () => client_state.Cresolved__joined_collection
	} = {
		joined_collection: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__collection__joined__yes__entries__referenced_entry__entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.joined_collection.state.name === 'resolved') {
						return resolve(context.properties.joined_collection.state.node as client_state.Cresolved__joined_collection).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tentry__referenced_entry, public parent:Centries__yes__joined) {
		super();
		const $this = this;
		this.properties = {
			can_create_entry: new Centry__referenced_entry.Dcan_create_entry(init['can create entry'], $this),
			entry: new Centry__referenced_entry.Dentry(init['entry'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.can_create_entry.destroy();
		this.properties.entry.destroy();
	}
	public set_entry(value:string):void {
		this.properties.entry.unlink();
		(<(detach?:boolean) => void>(this.properties.entry as any).resolve)(true);
		(this.properties.entry as Mutable<Centry__referenced_entry.Dentry>).entry = value;
		super.notify('entry', value);
	}
	public subscribe_entry(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('entry', observer);
		observer.on_update(this.properties.entry.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'referenced entry'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_create_entry__entry = {
};
export class Cno__can_create_entry__entry extends AlanNode {
	constructor(init:Tno__can_create_entry__entry, public parent:Centry__referenced_entry) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can create entry'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_create_entry__entry = {
};
export class Cyes__can_create_entry__entry extends AlanNode {
	constructor(init:Tyes__can_create_entry__entry, public parent:Centry__referenced_entry) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can create entry'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__referenced_entry = {
};
export class Cnone__referenced_entry extends AlanNode {
	constructor(init:Tnone__referenced_entry, public parent:Centries__yes__joined) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'referenced entry'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_empty__yes__joined = {
	'size':'multiple'|['multiple', {}]|'single'|['single', {}];
};
export class Cno__is_empty__yes__joined extends AlanNode {
	public readonly properties:{
		readonly size:Cno__is_empty__yes__joined.Dsize
	};
	constructor(init:Tno__is_empty__yes__joined, public parent:Cyes__joined) {
		super();
		const $this = this;
		this.properties = {
			size: new Cno__is_empty__yes__joined.Dsize(init['size'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.size.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is empty'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmultiple__size__no__is_empty__yes = {
};
export class Cmultiple__size__no__is_empty__yes extends AlanNode {
	constructor(init:Tmultiple__size__no__is_empty__yes, public parent:Cno__is_empty__yes__joined) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'size'?'multiple'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsingle__size__no__is_empty__yes = {
};
export class Csingle__size__no__is_empty__yes extends AlanNode {
	constructor(init:Tsingle__size__no__is_empty__yes, public parent:Cno__is_empty__yes__joined) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'size'?'single'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_empty__yes__joined = {
};
export class Cyes__is_empty__yes__joined extends AlanNode {
	constructor(init:Tyes__is_empty__yes__joined, public parent:Cyes__joined) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is empty'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tresolved__joined_collection = {
	'collection':string;
	'referenced node':Tclient_id_path;
};
export class Cresolved__joined_collection extends AlanNode {
	public definitions:{
		id_path_location: Cid_path_location;
	} = {
		id_path_location: new Cid_path_location({name:'join', definition: this})
	}
	public readonly properties:{
		readonly collection:Cresolved__joined_collection.Dcollection,
		readonly referenced_node:Cclient_id_path
	};
	constructor(init:Tresolved__joined_collection, public parent:Cyes__joined) {
		super();
		const $this = this;
		this.properties = {
			collection: new Cresolved__joined_collection.Dcollection(init['collection'], $this),
			referenced_node: new Cresolved__joined_collection.Dreferenced_node(init['referenced node'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.collection.destroy();
		this.properties.referenced_node.destroy();
	}
	public set_collection(value:string):void {
		this.properties.collection.unlink();
		(<(detach?:boolean) => void>(this.properties.collection as any).resolve)(true);
		(this.properties.collection as Mutable<Cresolved__joined_collection.Dcollection>).entry = value;
		super.notify('collection', value);
	}
	public subscribe_collection(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('collection', observer);
		observer.on_update(this.properties.collection.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'joined collection'?'resolved'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunresolved__joined_collection = {
};
export class Cunresolved__joined_collection extends AlanNode {
	constructor(init:Tunresolved__joined_collection, public parent:Cyes__joined) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'joined collection'?'unresolved'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry__last_created_entry = {
	'entry':string;
};
export class Centry__last_created_entry extends AlanNode {
	public readonly properties:{
		readonly entry:Centry__last_created_entry.Dentry
	};
	constructor(init:Tentry__last_created_entry, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			entry: new Centry__last_created_entry.Dentry(init['entry'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.entry.destroy();
	}
	public set_entry(value:string):void {
		this.properties.entry.unlink();
		(<(detach?:boolean) => void>(this.properties.entry as any).resolve)(true);
		(this.properties.entry as Mutable<Centry__last_created_entry.Dentry>).entry = value;
		super.notify('entry', value);
	}
	public subscribe_entry(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('entry', observer);
		observer.on_update(this.properties.entry.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last created entry'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__last_created_entry = {
};
export class Cnone__last_created_entry extends AlanNode {
	constructor(init:Tnone__last_created_entry, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last created entry'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__modified__collection = {
};
export class Cno__modified__collection extends AlanNode {
	constructor(init:Tno__modified__collection, public parent:Ccollection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__modified__collection = {
	'by':'engine'|['engine', {}]|'user'|['user', {}];
};
export class Cyes__modified__collection extends AlanNode {
	public readonly properties:{
		readonly by:Cyes__modified__collection.Dby
	};
	constructor(init:Tyes__modified__collection, public parent:Ccollection) {
		super();
		const $this = this;
		this.properties = {
			by: new Cyes__modified__collection.Dby(init['by'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.by.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengine__by__yes__modified = {
};
export class Cengine__by__yes__modified extends AlanNode {
	constructor(init:Tengine__by__yes__modified, public parent:Cyes__modified__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'engine'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__by__yes__modified = {
};
export class Cuser__by__yes__modified extends AlanNode {
	constructor(init:Tuser__by__yes__modified, public parent:Cyes__modified__collection) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__property = {
	'backend known':'no'|['no', {}]|['yes', Tyes__backend_known__file];
	'is editable':'no'|['no', {}]|['yes', Tyes__is_editable__file];
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__file__type__property];
	'is valid':['no', Tno__is_valid__file]|'yes'|['yes', {}];
};
export class Cfile__type__property extends AlanNode {
	public readonly properties:{
		readonly backend_known:Cfile__type__property.Dbackend_known,
		readonly is_editable:Cfile__type__property.Dis_editable,
		readonly is_set:Cfile__type__property.Dis_set,
		readonly is_valid:Cfile__type__property.Dis_valid
	};
	public readonly inferences:{
		file_type: () => application.Cfile
	} = {
		file_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as application.Cfile).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			backend_known: new Cfile__type__property.Dbackend_known(init['backend known'], $this),
			is_editable: new Cfile__type__property.Dis_editable(init['is editable'], $this),
			is_set: new Cfile__type__property.Dis_set(init['is set'], $this),
			is_valid: new Cfile__type__property.Dis_valid(init['is valid'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.backend_known.destroy();
		this.properties.is_editable.destroy();
		this.properties.is_set.destroy();
		this.properties.is_valid.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__backend_known__file = {
};
export class Cno__backend_known__file extends AlanNode {
	constructor(init:Tno__backend_known__file, public parent:Cfile__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend known'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__backend_known__file = {
	'is readable':'no'|['no', {}]|['yes', Tyes__is_readable__yes__backend_known__file];
};
export class Cyes__backend_known__file extends AlanNode {
	public readonly properties:{
		readonly is_readable:Cyes__backend_known__file.Dis_readable
	};
	constructor(init:Tyes__backend_known__file, public parent:Cfile__type__property) {
		super();
		const $this = this;
		this.properties = {
			is_readable: new Cyes__backend_known__file.Dis_readable(init['is readable'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_readable.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend known'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_readable__yes__backend_known__file = {
};
export class Cno__is_readable__yes__backend_known__file extends AlanNode {
	constructor(init:Tno__is_readable__yes__backend_known__file, public parent:Cyes__backend_known__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is readable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_readable__yes__backend_known__file = {
	'extension':string;
	'token':string;
};
export class Cyes__is_readable__yes__backend_known__file extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	constructor(init:Tyes__is_readable__yes__backend_known__file, public parent:Cyes__backend_known__file) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_extension(value:string):void {
		(this.properties as Mutable<Cyes__is_readable__yes__backend_known__file['properties']>).extension = value;
		super.notify('extension', value);
	}
	public subscribe_extension(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('extension', observer);
		observer.on_update(this.properties.extension, subscription);
		return subscription
	}
	public set_token(value:string):void {
		(this.properties as Mutable<Cyes__is_readable__yes__backend_known__file['properties']>).token = value;
		super.notify('token', value);
	}
	public subscribe_token(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('token', observer);
		observer.on_update(this.properties.token, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is readable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__file = {
};
export class Cno__is_editable__file extends AlanNode {
	constructor(init:Tno__is_editable__file, public parent:Cfile__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__file = {
	'edit mode':'off'|['off', {}]|'on'|['on', {}];
	'modified':'no'|['no', {}]|['yes', Tyes__modified__yes__is_editable__file];
};
export class Cyes__is_editable__file extends AlanNode {
	public readonly properties:{
		readonly edit_mode:Cyes__is_editable__file.Dedit_mode,
		readonly modified:Cyes__is_editable__file.Dmodified
	};
	public readonly inferences:{
		elementary: () => application.Cbase__type__file
	} = {
		elementary: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__file__is_editable__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.file_type())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__file).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__is_editable__file, public parent:Cfile__type__property) {
		super();
		const $this = this;
		this.properties = {
			edit_mode: new Cyes__is_editable__file.Dedit_mode(init['edit mode'], $this),
			modified: new Cyes__is_editable__file.Dmodified(init['modified'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.edit_mode.destroy();
		this.properties.modified.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Toff__edit_mode__yes__is_editable__file = {
};
export class Coff__edit_mode__yes__is_editable__file extends AlanNode {
	constructor(init:Toff__edit_mode__yes__is_editable__file, public parent:Cyes__is_editable__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'off'`; }
	public get entity() { return this.parent.entity; }
}
export type Ton__edit_mode__yes__is_editable__file = {
};
export class Con__edit_mode__yes__is_editable__file extends AlanNode {
	constructor(init:Ton__edit_mode__yes__is_editable__file, public parent:Cyes__is_editable__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'on'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__modified__yes__is_editable__file = {
};
export class Cno__modified__yes__is_editable__file extends AlanNode {
	constructor(init:Tno__modified__yes__is_editable__file, public parent:Cyes__is_editable__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__modified__yes__is_editable__file = {
	'extension':string;
	'upload status':['done', Tdone]|'not uploaded'|['not uploaded', {}]|'uploading'|['uploading', {}];
};
export class Cyes__modified__yes__is_editable__file extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly upload_status:Cyes__modified__yes__is_editable__file.Dupload_status
	};
	constructor(init:Tyes__modified__yes__is_editable__file, public parent:Cyes__is_editable__file) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			upload_status: new Cyes__modified__yes__is_editable__file.Dupload_status(init['upload status'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.upload_status.destroy();
	}
	public set_extension(value:string):void {
		(this.properties as Mutable<Cyes__modified__yes__is_editable__file['properties']>).extension = value;
		super.notify('extension', value);
	}
	public subscribe_extension(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('extension', observer);
		observer.on_update(this.properties.extension, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdone = {
	'token':string;
};
export class Cdone extends AlanNode {
	public readonly properties:{
		readonly token:string
	};
	constructor(init:Tdone, public parent:Cyes__modified__yes__is_editable__file) {
		super();
		const $this = this;
		this.properties = {
			token: init['token']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_token(value:string):void {
		(this.properties as Mutable<Cdone['properties']>).token = value;
		super.notify('token', value);
	}
	public subscribe_token(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('token', observer);
		observer.on_update(this.properties.token, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'upload status'?'done'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_uploaded = {
};
export class Cnot_uploaded extends AlanNode {
	constructor(init:Tnot_uploaded, public parent:Cyes__modified__yes__is_editable__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'upload status'?'not uploaded'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuploading = {
};
export class Cuploading extends AlanNode {
	constructor(init:Tuploading, public parent:Cyes__modified__yes__is_editable__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'upload status'?'uploading'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__file__type__property = {
};
export class Cno__is_set__file__type__property extends AlanNode {
	constructor(init:Tno__is_set__file__type__property, public parent:Cfile__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__file__type__property = {
	'extension':string;
	'token':string;
};
export class Cyes__is_set__file__type__property extends AlanNode {
	public readonly properties:{
		readonly extension:string,
		readonly token:string
	};
	constructor(init:Tyes__is_set__file__type__property, public parent:Cfile__type__property) {
		super();
		const $this = this;
		this.properties = {
			extension: init['extension'],
			token: init['token']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_extension(value:string):void {
		(this.properties as Mutable<Cyes__is_set__file__type__property['properties']>).extension = value;
		super.notify('extension', value);
	}
	public subscribe_extension(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('extension', observer);
		observer.on_update(this.properties.extension, subscription);
		return subscription
	}
	public set_token(value:string):void {
		(this.properties as Mutable<Cyes__is_set__file__type__property['properties']>).token = value;
		super.notify('token', value);
	}
	public subscribe_token(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('token', observer);
		observer.on_update(this.properties.token, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__file = {
	'extension not valid':'no'|['no', {}]|['yes', Tyes__extension_not_valid];
	'not set':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cno__is_valid__file extends AlanNode {
	public readonly properties:{
		readonly extension_not_valid:Cno__is_valid__file.Dextension_not_valid,
		readonly not_set:Cno__is_valid__file.Dnot_set
	};
	constructor(init:Tno__is_valid__file, public parent:Cfile__type__property) {
		super();
		const $this = this;
		this.properties = {
			extension_not_valid: new Cno__is_valid__file.Dextension_not_valid(init['extension not valid'], $this),
			not_set: new Cno__is_valid__file.Dnot_set(init['not set'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.extension_not_valid.destroy();
		this.properties.not_set.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__extension_not_valid = {
};
export class Cno__extension_not_valid extends AlanNode {
	constructor(init:Tno__extension_not_valid, public parent:Cno__is_valid__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'extension not valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__extension_not_valid = {
	'pattern':string;
};
export class Cyes__extension_not_valid extends AlanNode {
	public readonly properties:{
		readonly pattern:string
	};
	constructor(init:Tyes__extension_not_valid, public parent:Cno__is_valid__file) {
		super();
		const $this = this;
		this.properties = {
			pattern: init['pattern']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_pattern(value:string):void {
		(this.properties as Mutable<Cyes__extension_not_valid['properties']>).pattern = value;
		super.notify('pattern', value);
	}
	public subscribe_pattern(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('pattern', observer);
		observer.on_update(this.properties.pattern, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'extension not valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__not_set__no__is_valid__file = {
};
export class Cno__not_set__no__is_valid__file extends AlanNode {
	constructor(init:Tno__not_set__no__is_valid__file, public parent:Cno__is_valid__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'not set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__not_set__no__is_valid__file = {
};
export class Cyes__not_set__no__is_valid__file extends AlanNode {
	constructor(init:Tyes__not_set__no__is_valid__file, public parent:Cno__is_valid__file) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'not set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__file = {
};
export class Cyes__is_valid__file extends AlanNode {
	constructor(init:Tyes__is_valid__file, public parent:Cfile__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__property = {
	'node':Tnode;
};
export class Cgroup__type__property extends AlanNode {
	public definitions:{
		node_position: Cnode_position;
	} = {
		node_position: new Cnode_position({name:'group', definition: this}, {
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.position())
				.then(context => context?.component_root.output.root()).result!)
		})
	}
	public readonly properties:{
		readonly node:Cnode
	};
	public readonly inferences:{
		group_type: () => application.Cgroup__type__property
	} = {
		group_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			node: new Cgroup__type__property.Dnode(init['node'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.node.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property = {
	'backend known':'no'|['no', {}]|['yes', Tyes__backend_known__number];
	'is editable':'no'|['no', {}]|['yes', Tyes__is_editable__number];
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__number__type__property];
	'is valid':['no', Tno__is_valid__number__type__property]|'yes'|['yes', {}];
	'number type':'integer'|['integer', {}]|'positive'|['positive', {}];
	'numerical type properties':Tnumerical_type_properties;
};
export class Cnumber__type__property extends AlanNode {
	public readonly properties:{
		readonly backend_known:Cnumber__type__property.Dbackend_known,
		readonly is_editable:Cnumber__type__property.Dis_editable,
		readonly is_set:Cnumber__type__property.Dis_set,
		readonly is_valid:Cnumber__type__property.Dis_valid,
		readonly number_type:Cnumber__type__property.Dnumber_type,
		readonly numerical_type_properties:Cnumerical_type_properties
	};
	public readonly inferences:{
		number_type: () => application.Cnumber__type__property
	} = {
		number_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			backend_known: new Cnumber__type__property.Dbackend_known(init['backend known'], $this),
			is_editable: new Cnumber__type__property.Dis_editable(init['is editable'], $this),
			is_set: new Cnumber__type__property.Dis_set(init['is set'], $this),
			is_valid: new Cnumber__type__property.Dis_valid(init['is valid'], $this),
			number_type: new Cnumber__type__property.Dnumber_type(init['number type'], $this),
			numerical_type_properties: new Cnumber__type__property.Dnumerical_type_properties(init['numerical type properties'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.backend_known.destroy();
		this.properties.is_editable.destroy();
		this.properties.is_set.destroy();
		this.properties.is_valid.destroy();
		this.properties.number_type.destroy();
		this.properties.numerical_type_properties.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__backend_known__number = {
};
export class Cno__backend_known__number extends AlanNode {
	constructor(init:Tno__backend_known__number, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend known'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__backend_known__number = {
	'is readable':'no'|['no', {}]|['yes', Tyes__is_readable__yes__backend_known__number];
};
export class Cyes__backend_known__number extends AlanNode {
	public readonly properties:{
		readonly is_readable:Cyes__backend_known__number.Dis_readable
	};
	constructor(init:Tyes__backend_known__number, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			is_readable: new Cyes__backend_known__number.Dis_readable(init['is readable'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.is_readable.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend known'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_readable__yes__backend_known__number = {
};
export class Cno__is_readable__yes__backend_known__number extends AlanNode {
	constructor(init:Tno__is_readable__yes__backend_known__number, public parent:Cyes__backend_known__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is readable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_readable__yes__backend_known__number = {
	'number':number;
};
export class Cyes__is_readable__yes__backend_known__number extends AlanNode {
	public readonly properties:{
		readonly number:Cyes__is_readable__yes__backend_known__number.Dnumber
	};
	constructor(init:Tyes__is_readable__yes__backend_known__number, public parent:Cyes__backend_known__number) {
		super();
		const $this = this;
		this.properties = {
			number: new Cyes__is_readable__yes__backend_known__number.Dnumber(init['number'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.number.destroy();
	}
	public set_number(data:number):void {
		(this.properties.number as Mutable<Cyes__is_readable__yes__backend_known__number.Dnumber>).value = data;
		super.notify('number', data);
	}
	public subscribe_number(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('number', observer);
		observer.on_update(this.properties.number.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is readable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__number = {
};
export class Cno__is_editable__number extends AlanNode {
	constructor(init:Tno__is_editable__number, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__number = {
	'edit mode':'off'|['off', {}]|'on'|['on', {}];
	'modified':'no'|['no', {}]|['yes', Tyes__modified__yes__is_editable__number];
};
export class Cyes__is_editable__number extends AlanNode {
	public readonly properties:{
		readonly edit_mode:Cyes__is_editable__number.Dedit_mode,
		readonly modified:Cyes__is_editable__number.Dmodified
	};
	public readonly inferences:{
		elementary: () => application.Csimple__type__base
	} = {
		elementary: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__number__is_editable__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.number_type())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number)
						.then(context => {
							if (context?.properties.type.state.name === 'simple') {
								return context.properties.type.state.node as application.Csimple__type__base;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__is_editable__number, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			edit_mode: new Cyes__is_editable__number.Dedit_mode(init['edit mode'], $this),
			modified: new Cyes__is_editable__number.Dmodified(init['modified'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.edit_mode.destroy();
		this.properties.modified.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Toff__edit_mode__yes__is_editable__number = {
};
export class Coff__edit_mode__yes__is_editable__number extends AlanNode {
	constructor(init:Toff__edit_mode__yes__is_editable__number, public parent:Cyes__is_editable__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'off'`; }
	public get entity() { return this.parent.entity; }
}
export type Ton__edit_mode__yes__is_editable__number = {
};
export class Con__edit_mode__yes__is_editable__number extends AlanNode {
	constructor(init:Ton__edit_mode__yes__is_editable__number, public parent:Cyes__is_editable__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'on'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__modified__yes__is_editable__number = {
};
export class Cno__modified__yes__is_editable__number extends AlanNode {
	constructor(init:Tno__modified__yes__is_editable__number, public parent:Cyes__is_editable__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__modified__yes__is_editable__number = {
	'number':number;
};
export class Cyes__modified__yes__is_editable__number extends AlanNode {
	public readonly properties:{
		readonly number:Cyes__modified__yes__is_editable__number.Dnumber
	};
	constructor(init:Tyes__modified__yes__is_editable__number, public parent:Cyes__is_editable__number) {
		super();
		const $this = this;
		this.properties = {
			number: new Cyes__modified__yes__is_editable__number.Dnumber(init['number'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.number.destroy();
	}
	public set_number(data:number):void {
		(this.properties.number as Mutable<Cyes__modified__yes__is_editable__number.Dnumber>).value = data;
		super.notify('number', data);
	}
	public subscribe_number(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('number', observer);
		observer.on_update(this.properties.number.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__number__type__property = {
};
export class Cno__is_set__number__type__property extends AlanNode {
	constructor(init:Tno__is_set__number__type__property, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__number__type__property = {
	'by':'engine'|['engine', {}]|'user'|['user', {}];
	'number':number;
};
export class Cyes__is_set__number__type__property extends AlanNode {
	public readonly properties:{
		readonly by:Cyes__is_set__number__type__property.Dby,
		readonly number:Cyes__is_set__number__type__property.Dnumber
	};
	constructor(init:Tyes__is_set__number__type__property, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			by: new Cyes__is_set__number__type__property.Dby(init['by'], $this),
			number: new Cyes__is_set__number__type__property.Dnumber(init['number'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.by.destroy();
		this.properties.number.destroy();
	}
	public set_number(data:number):void {
		(this.properties.number as Mutable<Cyes__is_set__number__type__property.Dnumber>).value = data;
		super.notify('number', data);
	}
	public subscribe_number(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('number', observer);
		observer.on_update(this.properties.number.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengine__by__yes__is_set__number = {
};
export class Cengine__by__yes__is_set__number extends AlanNode {
	constructor(init:Tengine__by__yes__is_set__number, public parent:Cyes__is_set__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'engine'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__by__yes__is_set__number = {
};
export class Cuser__by__yes__is_set__number extends AlanNode {
	constructor(init:Tuser__by__yes__is_set__number, public parent:Cyes__is_set__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__number__type__property = {
	'exceeds maximum':'no'|['no', {}]|['yes', Tyes__exceeds_maximum];
	'exceeds minimum':'no'|['no', {}]|['yes', Tyes__exceeds_minimum];
	'not a number':'no'|['no', {}]|'yes'|['yes', {}];
	'not set':'no'|['no', {}]|'yes'|['yes', {}];
	'should be positive':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cno__is_valid__number__type__property extends AlanNode {
	public readonly properties:{
		readonly exceeds_maximum:Cno__is_valid__number__type__property.Dexceeds_maximum,
		readonly exceeds_minimum:Cno__is_valid__number__type__property.Dexceeds_minimum,
		readonly not_a_number:Cno__is_valid__number__type__property.Dnot_a_number,
		readonly not_set:Cno__is_valid__number__type__property.Dnot_set,
		readonly should_be_positive:Cno__is_valid__number__type__property.Dshould_be_positive
	};
	constructor(init:Tno__is_valid__number__type__property, public parent:Cnumber__type__property) {
		super();
		const $this = this;
		this.properties = {
			exceeds_maximum: new Cno__is_valid__number__type__property.Dexceeds_maximum(init['exceeds maximum'], $this),
			exceeds_minimum: new Cno__is_valid__number__type__property.Dexceeds_minimum(init['exceeds minimum'], $this),
			not_a_number: new Cno__is_valid__number__type__property.Dnot_a_number(init['not a number'], $this),
			not_set: new Cno__is_valid__number__type__property.Dnot_set(init['not set'], $this),
			should_be_positive: new Cno__is_valid__number__type__property.Dshould_be_positive(init['should be positive'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.exceeds_maximum.destroy();
		this.properties.exceeds_minimum.destroy();
		this.properties.not_a_number.destroy();
		this.properties.not_set.destroy();
		this.properties.should_be_positive.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__exceeds_maximum = {
};
export class Cno__exceeds_maximum extends AlanNode {
	constructor(init:Tno__exceeds_maximum, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'exceeds maximum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__exceeds_maximum = {
	'maximum':number;
};
export class Cyes__exceeds_maximum extends AlanNode {
	public readonly properties:{
		readonly maximum:Cyes__exceeds_maximum.Dmaximum
	};
	constructor(init:Tyes__exceeds_maximum, public parent:Cno__is_valid__number__type__property) {
		super();
		const $this = this;
		this.properties = {
			maximum: new Cyes__exceeds_maximum.Dmaximum(init['maximum'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.maximum.destroy();
	}
	public set_maximum(data:number):void {
		(this.properties.maximum as Mutable<Cyes__exceeds_maximum.Dmaximum>).value = data;
		super.notify('maximum', data);
	}
	public subscribe_maximum(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('maximum', observer);
		observer.on_update(this.properties.maximum.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'exceeds maximum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__exceeds_minimum = {
};
export class Cno__exceeds_minimum extends AlanNode {
	constructor(init:Tno__exceeds_minimum, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'exceeds minimum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__exceeds_minimum = {
	'minimum':number;
};
export class Cyes__exceeds_minimum extends AlanNode {
	public readonly properties:{
		readonly minimum:Cyes__exceeds_minimum.Dminimum
	};
	constructor(init:Tyes__exceeds_minimum, public parent:Cno__is_valid__number__type__property) {
		super();
		const $this = this;
		this.properties = {
			minimum: new Cyes__exceeds_minimum.Dminimum(init['minimum'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.minimum.destroy();
	}
	public set_minimum(data:number):void {
		(this.properties.minimum as Mutable<Cyes__exceeds_minimum.Dminimum>).value = data;
		super.notify('minimum', data);
	}
	public subscribe_minimum(observer:NumberObserver):Subscription {
		const subscription = super._subscribe_property('minimum', observer);
		observer.on_update(this.properties.minimum.value, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'exceeds minimum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__not_a_number = {
};
export class Cno__not_a_number extends AlanNode {
	constructor(init:Tno__not_a_number, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'not a number'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__not_a_number = {
};
export class Cyes__not_a_number extends AlanNode {
	constructor(init:Tyes__not_a_number, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'not a number'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__not_set__no__is_valid__number = {
};
export class Cno__not_set__no__is_valid__number extends AlanNode {
	constructor(init:Tno__not_set__no__is_valid__number, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'not set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__not_set__no__is_valid__number = {
};
export class Cyes__not_set__no__is_valid__number extends AlanNode {
	constructor(init:Tyes__not_set__no__is_valid__number, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'not set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__should_be_positive = {
};
export class Cno__should_be_positive extends AlanNode {
	constructor(init:Tno__should_be_positive, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'should be positive'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__should_be_positive = {
};
export class Cyes__should_be_positive extends AlanNode {
	constructor(init:Tyes__should_be_positive, public parent:Cno__is_valid__number__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'should be positive'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__number__type__property = {
};
export class Cyes__is_valid__number__type__property extends AlanNode {
	constructor(init:Tyes__is_valid__number__type__property, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tinteger = {
};
export class Cinteger extends AlanNode {
	constructor(init:Tinteger, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'number type'?'integer'`; }
	public get entity() { return this.parent.entity; }
}
export type Tpositive = {
};
export class Cpositive extends AlanNode {
	constructor(init:Tpositive, public parent:Cnumber__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'number type'?'positive'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property = {
	'initialized':'no'|['no', {}]|['yes', Tyes__initialized__state_group];
	'is editable':'no'|['no', {}]|['yes', Tyes__is_editable__state_group];
	'is modified':'no'|['no', {}]|'yes'|['yes', {}];
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__state_group__type__property];
	'is valid':['no', Tno__is_valid__state_group]|'yes'|['yes', {}];
	'states':Record<string, Tstates__state_group__type__property>;
};
export class Cstate_group__type__property extends AlanNode {
	public readonly properties:{
		readonly initialized:Cstate_group__type__property.Dinitialized,
		readonly is_editable:Cstate_group__type__property.Dis_editable,
		readonly is_modified:Cstate_group__type__property.Dis_modified,
		readonly is_set:Cstate_group__type__property.Dis_set,
		readonly is_valid:Cstate_group__type__property.Dis_valid,
		readonly states:Cstate_group__type__property.Dstates
	};
	public readonly inferences:{
		state_group_type: () => application.Cstate_group
	} = {
		state_group_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			initialized: new Cstate_group__type__property.Dinitialized(init['initialized'], $this),
			is_editable: new Cstate_group__type__property.Dis_editable(init['is editable'], $this),
			is_modified: new Cstate_group__type__property.Dis_modified(init['is modified'], $this),
			is_set: new Cstate_group__type__property.Dis_set(init['is set'], $this),
			is_valid: new Cstate_group__type__property.Dis_valid(init['is valid'], $this),
			states: new Cstate_group__type__property.Dstates(init['states'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.initialized.destroy();
		this.properties.is_editable.destroy();
		this.properties.is_modified.destroy();
		this.properties.is_set.destroy();
		this.properties.is_valid.destroy();
		this.properties.states.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__initialized__state_group = {
};
export class Cno__initialized__state_group extends AlanNode {
	constructor(init:Tno__initialized__state_group, public parent:Cstate_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__initialized__state_group = {
	'state in backend':string;
};
export class Cyes__initialized__state_group extends AlanNode {
	public readonly properties:{
		readonly state_in_backend:Cyes__initialized__state_group.Dstate_in_backend
	};
	constructor(init:Tyes__initialized__state_group, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.properties = {
			state_in_backend: new Cyes__initialized__state_group.Dstate_in_backend(init['state in backend'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.state_in_backend.destroy();
	}
	public set_state_in_backend(value:string):void {
		this.properties.state_in_backend.unlink();
		(<(detach?:boolean) => void>(this.properties.state_in_backend as any).resolve)(true);
		(this.properties.state_in_backend as Mutable<Cyes__initialized__state_group.Dstate_in_backend>).entry = value;
		super.notify('state in backend', value);
	}
	public subscribe_state_in_backend(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state in backend', observer);
		observer.on_update(this.properties.state_in_backend.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__state_group = {
};
export class Cno__is_editable__state_group extends AlanNode {
	constructor(init:Tno__is_editable__state_group, public parent:Cstate_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__state_group = {
	'edit mode':'off'|['off', {}]|'on'|['on', {}];
};
export class Cyes__is_editable__state_group extends AlanNode {
	public readonly properties:{
		readonly edit_mode:Cyes__is_editable__state_group.Dedit_mode
	};
	public readonly inferences:{
		elementary: () => application.Cbase__type__state_group
	} = {
		elementary: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__state_group__is_editable__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.state_group_type())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__state_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__is_editable__state_group, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.properties = {
			edit_mode: new Cyes__is_editable__state_group.Dedit_mode(init['edit mode'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.edit_mode.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Toff__edit_mode__yes__is_editable__state_group = {
};
export class Coff__edit_mode__yes__is_editable__state_group extends AlanNode {
	constructor(init:Toff__edit_mode__yes__is_editable__state_group, public parent:Cyes__is_editable__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'off'`; }
	public get entity() { return this.parent.entity; }
}
export type Ton__edit_mode__yes__is_editable__state_group = {
};
export class Con__edit_mode__yes__is_editable__state_group extends AlanNode {
	constructor(init:Ton__edit_mode__yes__is_editable__state_group, public parent:Cyes__is_editable__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'on'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_modified = {
};
export class Cno__is_modified extends AlanNode {
	constructor(init:Tno__is_modified, public parent:Cstate_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is modified'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_modified = {
};
export class Cyes__is_modified extends AlanNode {
	constructor(init:Tyes__is_modified, public parent:Cstate_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is modified'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__state_group__type__property = {
};
export class Cno__is_set__state_group__type__property extends AlanNode {
	constructor(init:Tno__is_set__state_group__type__property, public parent:Cstate_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__state_group__type__property = {
	'by':'engine'|['engine', {}]|'user'|['user', {}];
	'state':string;
};
export class Cyes__is_set__state_group__type__property extends AlanNode {
	public readonly properties:{
		readonly by:Cyes__is_set__state_group__type__property.Dby,
		readonly state:Cyes__is_set__state_group__type__property.Dstate
	};
	constructor(init:Tyes__is_set__state_group__type__property, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.properties = {
			by: new Cyes__is_set__state_group__type__property.Dby(init['by'], $this),
			state: new Cyes__is_set__state_group__type__property.Dstate(init['state'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.by.destroy();
		this.properties.state.destroy();
	}
	public set_state(value:string):void {
		this.properties.state.unlink();
		(<(detach?:boolean) => void>(this.properties.state as any).resolve)(true);
		(this.properties.state as Mutable<Cyes__is_set__state_group__type__property.Dstate>).entry = value;
		super.notify('state', value);
	}
	public subscribe_state(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state', observer);
		observer.on_update(this.properties.state.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengine__by__yes__is_set__state_group = {
};
export class Cengine__by__yes__is_set__state_group extends AlanNode {
	constructor(init:Tengine__by__yes__is_set__state_group, public parent:Cyes__is_set__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'engine'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__by__yes__is_set__state_group = {
};
export class Cuser__by__yes__is_set__state_group extends AlanNode {
	constructor(init:Tuser__by__yes__is_set__state_group, public parent:Cyes__is_set__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__state_group = {
	'invalid rules':'no'|['no', {}]|'yes'|['yes', {}];
	'no state selected':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cno__is_valid__state_group extends AlanNode {
	public readonly properties:{
		readonly invalid_rules:Cno__is_valid__state_group.Dinvalid_rules,
		readonly no_state_selected:Cno__is_valid__state_group.Dno_state_selected
	};
	constructor(init:Tno__is_valid__state_group, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.properties = {
			invalid_rules: new Cno__is_valid__state_group.Dinvalid_rules(init['invalid rules'], $this),
			no_state_selected: new Cno__is_valid__state_group.Dno_state_selected(init['no state selected'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.invalid_rules.destroy();
		this.properties.no_state_selected.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__invalid_rules__no__is_valid__state_group = {
};
export class Cno__invalid_rules__no__is_valid__state_group extends AlanNode {
	constructor(init:Tno__invalid_rules__no__is_valid__state_group, public parent:Cno__is_valid__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'invalid rules'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__invalid_rules__no__is_valid__state_group = {
};
export class Cyes__invalid_rules__no__is_valid__state_group extends AlanNode {
	constructor(init:Tyes__invalid_rules__no__is_valid__state_group, public parent:Cno__is_valid__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'invalid rules'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__no_state_selected = {
};
export class Cno__no_state_selected extends AlanNode {
	constructor(init:Tno__no_state_selected, public parent:Cno__is_valid__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'no state selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__no_state_selected = {
};
export class Cyes__no_state_selected extends AlanNode {
	constructor(init:Tyes__no_state_selected, public parent:Cno__is_valid__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'no state selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__state_group = {
};
export class Cyes__is_valid__state_group extends AlanNode {
	constructor(init:Tyes__is_valid__state_group, public parent:Cstate_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_group__type__property extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_group__type__property) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group_type())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_group__type__property = {
	'create permitted':'no'|['no', {}]|'yes'|['yes', {}];
	'is selected':'no'|['no', {}]|'yes'|['yes', {}];
	'location':'local'|['local', {}]|'remote'|['remote', {}];
	'node':Tnode;
	'rules':Twhere_rules;
	'style':Tstyle;
};
export class Cstates__state_group__type__property extends AlanDictionaryEntry {
	public key:Kstates__state_group__type__property;
	public get key_value() { return this.key.entry; }
	public definitions:{
		node_position: Cnode_position;
		where_rules_definer: Cwhere_rules_definer;
	} = {
		node_position: new Cnode_position({name:'state', definition: this}, {
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.definitions.node_parent).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.position())
				.then(context => context?.component_root.output.root()).result!)
		}),
		where_rules_definer: new Cwhere_rules_definer({name:'state', definition: this})
	}
	public readonly properties:{
		readonly create_permitted:Cstates__state_group__type__property.Dcreate_permitted,
		readonly is_selected:Cstates__state_group__type__property.Dis_selected,
		readonly location:Cstates__state_group__type__property.Dlocation,
		readonly node:Cnode,
		readonly rules:Cwhere_rules,
		readonly style:Cstyle
	};
	constructor(key:string, init:Tstates__state_group__type__property, public parent:Cstate_group__type__property) {
		super();
		const $this = this;
		this.key = new Kstates__state_group__type__property(key, $this);
		this.properties = {
			create_permitted: new Cstates__state_group__type__property.Dcreate_permitted(init['create permitted'], $this),
			is_selected: new Cstates__state_group__type__property.Dis_selected(init['is selected'], $this),
			location: new Cstates__state_group__type__property.Dlocation(init['location'], $this),
			node: new Cstates__state_group__type__property.Dnode(init['node'], $this),
			rules: new Cstates__state_group__type__property.Drules(init['rules'], $this),
			style: new Cstates__state_group__type__property.Dstyle(init['style'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.create_permitted.destroy();
		this.properties.is_selected.destroy();
		this.properties.location.destroy();
		this.properties.node.destroy();
		this.properties.rules.destroy();
		this.properties.style.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__create_permitted = {
};
export class Cno__create_permitted extends AlanNode {
	constructor(init:Tno__create_permitted, public parent:Cstates__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'create permitted'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__create_permitted = {
};
export class Cyes__create_permitted extends AlanNode {
	constructor(init:Tyes__create_permitted, public parent:Cstates__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'create permitted'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_selected__states__state_group__type__property = {
};
export class Cno__is_selected__states__state_group__type__property extends AlanNode {
	constructor(init:Tno__is_selected__states__state_group__type__property, public parent:Cstates__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_selected__states__state_group__type__property = {
};
export class Cyes__is_selected__states__state_group__type__property extends AlanNode {
	constructor(init:Tyes__is_selected__states__state_group__type__property, public parent:Cstates__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is selected'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlocal__location__states = {
};
export class Clocal__location__states extends AlanNode {
	public readonly output:{
		state_node_location: () => client_state.Clocation;
	} = {
		state_node_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_state.Clocation.Plocal).result!)
	}
	constructor(init:Tlocal__location__states, public parent:Cstates__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'location'?'local'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremote__location__states = {
};
export class Cremote__location__states extends AlanNode {
	public readonly output:{
		state_node_location: () => client_state.Clocation;
	} = {
		state_node_location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(() => client_state.Clocation.Premote).result!)
	}
	constructor(init:Tremote__location__states, public parent:Cstates__state_group__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'location'?'remote'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__property = {
	'backend known':'no'|['no', {}]|['yes', Tyes__backend_known__text];
	'is editable':'no'|['no', {}]|['yes', Tyes__is_editable__text];
	'is key':'no'|['no', {}]|['yes', Tyes__is_key];
	'is set':'no'|['no', {}]|['yes', Tyes__is_set__text__type__property];
	'is valid':['no', Tno__is_valid__text__type__property]|'yes'|['yes', {}];
	'navigable':'no'|['no', {}]|['yes', Tyes__navigable];
	'validation result':'failure'|['failure', {}]|'success'|['success', {}];
};
export class Ctext__type__property extends AlanNode {
	public readonly properties:{
		readonly backend_known:Ctext__type__property.Dbackend_known,
		readonly is_editable:Ctext__type__property.Dis_editable,
		readonly is_key:Ctext__type__property.Dis_key,
		readonly is_set:Ctext__type__property.Dis_set,
		readonly is_valid:Ctext__type__property.Dis_valid,
		readonly navigable:Ctext__type__property.Dnavigable,
		readonly validation_result:Ctext__type__property.Dvalidation_result
	};
	public readonly inferences:{
		text_type: () => application.Ctext__type__property
	} = {
		text_type: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property, public parent:Cproperty) {
		super();
		const $this = this;
		this.properties = {
			backend_known: new Ctext__type__property.Dbackend_known(init['backend known'], $this),
			is_editable: new Ctext__type__property.Dis_editable(init['is editable'], $this),
			is_key: new Ctext__type__property.Dis_key(init['is key'], $this),
			is_set: new Ctext__type__property.Dis_set(init['is set'], $this),
			is_valid: new Ctext__type__property.Dis_valid(init['is valid'], $this),
			navigable: new Ctext__type__property.Dnavigable(init['navigable'], $this),
			validation_result: new Ctext__type__property.Dvalidation_result(init['validation result'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.backend_known.destroy();
		this.properties.is_editable.destroy();
		this.properties.is_key.destroy();
		this.properties.is_set.destroy();
		this.properties.is_valid.destroy();
		this.properties.navigable.destroy();
		this.properties.validation_result.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__backend_known__text = {
};
export class Cno__backend_known__text extends AlanNode {
	constructor(init:Tno__backend_known__text, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend known'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__backend_known__text = {
	'value':string;
};
export class Cyes__backend_known__text extends AlanNode {
	public readonly properties:{
		readonly value:string
	};
	constructor(init:Tyes__backend_known__text, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			value: init['value']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_value(value:string):void {
		(this.properties as Mutable<Cyes__backend_known__text['properties']>).value = value;
		super.notify('value', value);
	}
	public subscribe_value(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'backend known'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__text = {
};
export class Cno__is_editable__text extends AlanNode {
	constructor(init:Tno__is_editable__text, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__text = {
	'edit mode':'off'|['off', {}]|'on'|['on', {}];
	'modified':'no'|['no', {}]|['yes', Tyes__modified__yes__is_editable__text];
};
export class Cyes__is_editable__text extends AlanNode {
	public readonly properties:{
		readonly edit_mode:Cyes__is_editable__text.Dedit_mode,
		readonly modified:Cyes__is_editable__text.Dmodified
	};
	public readonly inferences:{
		elementary: () => application.Cbase__type__text
	} = {
		elementary: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text__is_editable__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text_type())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__is_editable__text, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			edit_mode: new Cyes__is_editable__text.Dedit_mode(init['edit mode'], $this),
			modified: new Cyes__is_editable__text.Dmodified(init['modified'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.edit_mode.destroy();
		this.properties.modified.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Toff__edit_mode__yes__is_editable__text = {
};
export class Coff__edit_mode__yes__is_editable__text extends AlanNode {
	constructor(init:Toff__edit_mode__yes__is_editable__text, public parent:Cyes__is_editable__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'off'`; }
	public get entity() { return this.parent.entity; }
}
export type Ton__edit_mode__yes__is_editable__text = {
};
export class Con__edit_mode__yes__is_editable__text extends AlanNode {
	constructor(init:Ton__edit_mode__yes__is_editable__text, public parent:Cyes__is_editable__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'on'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__modified__yes__is_editable__text = {
};
export class Cno__modified__yes__is_editable__text extends AlanNode {
	constructor(init:Tno__modified__yes__is_editable__text, public parent:Cyes__is_editable__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__modified__yes__is_editable__text = {
	'saving':'no'|['no', {}]|'yes'|['yes', {}];
	'value':string;
};
export class Cyes__modified__yes__is_editable__text extends AlanNode {
	public readonly properties:{
		readonly saving:Cyes__modified__yes__is_editable__text.Dsaving,
		readonly value:string
	};
	constructor(init:Tyes__modified__yes__is_editable__text, public parent:Cyes__is_editable__text) {
		super();
		const $this = this;
		this.properties = {
			saving: new Cyes__modified__yes__is_editable__text.Dsaving(init['saving'], $this),
			value: init['value']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.saving.destroy();
	}
	public set_value(value:string):void {
		(this.properties as Mutable<Cyes__modified__yes__is_editable__text['properties']>).value = value;
		super.notify('value', value);
	}
	public subscribe_value(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('value', observer);
		observer.on_update(this.properties.value, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__saving = {
};
export class Cno__saving extends AlanNode {
	constructor(init:Tno__saving, public parent:Cyes__modified__yes__is_editable__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'saving'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__saving = {
};
export class Cyes__saving extends AlanNode {
	constructor(init:Tyes__saving, public parent:Cyes__modified__yes__is_editable__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'saving'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_key = {
};
export class Cno__is_key extends AlanNode {
	constructor(init:Tno__is_key, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is key'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_key = {
	'key unique':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__is_key extends AlanNode {
	public readonly properties:{
		readonly key_unique:Cyes__is_key.Dkey_unique
	};
	public readonly inferences:{
		entry: () => client_state.Centries__collection
	} = {
		entry: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text__is_key__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.entity())
				.then(context => context?.variant.name === 'entry' ? context.variant.definition as client_state.Centries__collection : undefined).result!;
		})

	}
	constructor(init:Tyes__is_key, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			key_unique: new Cyes__is_key.Dkey_unique(init['key unique'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.key_unique.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is key'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__key_unique = {
};
export class Cno__key_unique extends AlanNode {
	constructor(init:Tno__key_unique, public parent:Cyes__is_key) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key unique'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__key_unique = {
};
export class Cyes__key_unique extends AlanNode {
	constructor(init:Tyes__key_unique, public parent:Cyes__is_key) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key unique'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_set__text__type__property = {
};
export class Cno__is_set__text__type__property extends AlanNode {
	constructor(init:Tno__is_set__text__type__property, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_set__text__type__property = {
	'by':'engine'|['engine', {}]|'user'|['user', {}];
	'text':string;
};
export class Cyes__is_set__text__type__property extends AlanNode {
	public readonly properties:{
		readonly by:Cyes__is_set__text__type__property.Dby,
		readonly text:string
	};
	constructor(init:Tyes__is_set__text__type__property, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			by: new Cyes__is_set__text__type__property.Dby(init['by'], $this),
			text: init['text']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.by.destroy();
	}
	public set_text(value:string):void {
		(this.properties as Mutable<Cyes__is_set__text__type__property['properties']>).text = value;
		super.notify('text', value);
	}
	public subscribe_text(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('text', observer);
		observer.on_update(this.properties.text, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is set'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengine__by__yes__is_set__text = {
};
export class Cengine__by__yes__is_set__text extends AlanNode {
	constructor(init:Tengine__by__yes__is_set__text, public parent:Cyes__is_set__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'engine'`; }
	public get entity() { return this.parent.entity; }
}
export type Tuser__by__yes__is_set__text = {
};
export class Cuser__by__yes__is_set__text extends AlanNode {
	constructor(init:Tuser__by__yes__is_set__text, public parent:Cyes__is_set__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'by'?'user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__text__type__property = {
	'invalid rules':'no'|['no', {}]|'yes'|['yes', {}];
	'key is not unique':'no'|['no', {}]|'yes'|['yes', {}];
	'reference not resolved':'no'|['no', {}]|'yes'|['yes', {}];
	'referenced node not valid':'no'|['no', {}]|'yes'|['yes', {}];
	'text does not match pattern':'no'|['no', {}]|['yes', Tyes__text_does_not_match_pattern];
};
export class Cno__is_valid__text__type__property extends AlanNode {
	public readonly properties:{
		readonly invalid_rules:Cno__is_valid__text__type__property.Dinvalid_rules,
		readonly key_is_not_unique:Cno__is_valid__text__type__property.Dkey_is_not_unique,
		readonly reference_not_resolved:Cno__is_valid__text__type__property.Dreference_not_resolved,
		readonly referenced_node_not_valid:Cno__is_valid__text__type__property.Dreferenced_node_not_valid,
		readonly text_does_not_match_pattern:Cno__is_valid__text__type__property.Dtext_does_not_match_pattern
	};
	constructor(init:Tno__is_valid__text__type__property, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			invalid_rules: new Cno__is_valid__text__type__property.Dinvalid_rules(init['invalid rules'], $this),
			key_is_not_unique: new Cno__is_valid__text__type__property.Dkey_is_not_unique(init['key is not unique'], $this),
			reference_not_resolved: new Cno__is_valid__text__type__property.Dreference_not_resolved(init['reference not resolved'], $this),
			referenced_node_not_valid: new Cno__is_valid__text__type__property.Dreferenced_node_not_valid(init['referenced node not valid'], $this),
			text_does_not_match_pattern: new Cno__is_valid__text__type__property.Dtext_does_not_match_pattern(init['text does not match pattern'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.invalid_rules.destroy();
		this.properties.key_is_not_unique.destroy();
		this.properties.reference_not_resolved.destroy();
		this.properties.referenced_node_not_valid.destroy();
		this.properties.text_does_not_match_pattern.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__invalid_rules__no__is_valid__text = {
};
export class Cno__invalid_rules__no__is_valid__text extends AlanNode {
	constructor(init:Tno__invalid_rules__no__is_valid__text, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'invalid rules'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__invalid_rules__no__is_valid__text = {
};
export class Cyes__invalid_rules__no__is_valid__text extends AlanNode {
	constructor(init:Tyes__invalid_rules__no__is_valid__text, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'invalid rules'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__key_is_not_unique = {
};
export class Cno__key_is_not_unique extends AlanNode {
	constructor(init:Tno__key_is_not_unique, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key is not unique'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__key_is_not_unique = {
};
export class Cyes__key_is_not_unique extends AlanNode {
	constructor(init:Tyes__key_is_not_unique, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'key is not unique'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__reference_not_resolved = {
};
export class Cno__reference_not_resolved extends AlanNode {
	constructor(init:Tno__reference_not_resolved, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference not resolved'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__reference_not_resolved = {
};
export class Cyes__reference_not_resolved extends AlanNode {
	constructor(init:Tyes__reference_not_resolved, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reference not resolved'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__referenced_node_not_valid = {
};
export class Cno__referenced_node_not_valid extends AlanNode {
	constructor(init:Tno__referenced_node_not_valid, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'referenced node not valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__referenced_node_not_valid = {
};
export class Cyes__referenced_node_not_valid extends AlanNode {
	constructor(init:Tyes__referenced_node_not_valid, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'referenced node not valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__text_does_not_match_pattern = {
};
export class Cno__text_does_not_match_pattern extends AlanNode {
	constructor(init:Tno__text_does_not_match_pattern, public parent:Cno__is_valid__text__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'text does not match pattern'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__text_does_not_match_pattern = {
	'pattern':string;
};
export class Cyes__text_does_not_match_pattern extends AlanNode {
	public readonly properties:{
		readonly pattern:string
	};
	constructor(init:Tyes__text_does_not_match_pattern, public parent:Cno__is_valid__text__type__property) {
		super();
		const $this = this;
		this.properties = {
			pattern: init['pattern']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_pattern(value:string):void {
		(this.properties as Mutable<Cyes__text_does_not_match_pattern['properties']>).pattern = value;
		super.notify('pattern', value);
	}
	public subscribe_pattern(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('pattern', observer);
		observer.on_update(this.properties.pattern, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'text does not match pattern'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__text__type__property = {
};
export class Cyes__is_valid__text__type__property extends AlanNode {
	constructor(init:Tyes__is_valid__text__type__property, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__navigable = {
};
export class Cno__navigable extends AlanNode {
	public readonly inferences:{
		is_navigable: () => application.Cno__has_reference
	} = {
		is_navigable: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text__navigable__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text_type())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'no') {
						return resolve(context.properties.has_reference.state.node as application.Cno__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__navigable, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'navigable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__navigable = {
	'queries':Record<string, Tqueries__yes>;
	'resolve status':Tresolve_status;
	'rules':Twhere_rules;
	'type':'mandatory'|['mandatory', {}]|'optional'|['optional', {}];
};
export class Cyes__navigable extends AlanNode {
	public definitions:{
		resolvable: Cresolvable;
		where_rules_definer: Cwhere_rules_definer;
	} = {
		resolvable: new Cresolvable({name:'reference', definition: this}),
		where_rules_definer: new Cwhere_rules_definer({name:'reference', definition: this})
	}
	public readonly properties:{
		readonly queries:Cyes__navigable.Dqueries,
		readonly resolve_status:Cresolve_status,
		readonly rules:Cwhere_rules,
		readonly type:Cyes__navigable.Dtype
	};
	public readonly inferences:{
		is_navigable: () => application.Cyes__has_reference
	} = {
		is_navigable: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text__navigable__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text_type())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__navigable, public parent:Ctext__type__property) {
		super();
		const $this = this;
		this.properties = {
			queries: new Cyes__navigable.Dqueries(init['queries'], $this),
			resolve_status: new Cyes__navigable.Dresolve_status(init['resolve status'], $this),
			rules: new Cyes__navigable.Drules(init['rules'], $this),
			type: new Cyes__navigable.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.queries.destroy();
		this.properties.resolve_status.destroy();
		this.properties.rules.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'navigable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tqueries__yes = {
	'query':Tquery__client_state;
};
export class Cqueries__yes extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		query_position: Cquery_position;
	} = {
		query_position: new Cquery_position({name:'navigable text property', definition: this})
	}
	public readonly properties:{
		readonly query:Cquery__client_state
	};
	constructor(key:string, init:Tqueries__yes, public parent:Cyes__navigable) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			query: new Cqueries__yes.Dquery(init['query'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.query.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'queries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tmandatory = {
};
export class Cmandatory extends AlanNode {
	public readonly inferences:{
		mandatory: () => application.Cmandatory__type
	} = {
		mandatory: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text__navigable__yes__type__mandatory_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.is_navigable())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'mandatory') {
						return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tmandatory, public parent:Cyes__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'mandatory'`; }
	public get entity() { return this.parent.entity; }
}
export type Toptional = {
};
export class Coptional extends AlanNode {
	public readonly inferences:{
		optional: () => application.Coptional__type
	} = {
		optional: cache((detach:boolean) => {
			const client_state__node__attributes__type__property__type__text__navigable__yes__type__optional_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.is_navigable())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'optional') {
						return resolve(context.properties.type.state.node as application.Coptional__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Toptional, public parent:Cyes__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'optional'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfailure__validation_result__text = {
};
export class Cfailure__validation_result__text extends AlanNode {
	constructor(init:Tfailure__validation_result__text, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation result'?'failure'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__validation_result__text = {
};
export class Csuccess__validation_result__text extends AlanNode {
	constructor(init:Tsuccess__validation_result__text, public parent:Ctext__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation result'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_be_deleted = {
};
export class Cno__can_be_deleted extends AlanNode {
	constructor(init:Tno__can_be_deleted, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can be deleted'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_be_deleted = {
};
export class Cyes__can_be_deleted extends AlanNode {
	constructor(init:Tyes__can_be_deleted, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can be deleted'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_busy_discarding = {
};
export class Cno__is_busy_discarding extends AlanNode {
	constructor(init:Tno__is_busy_discarding, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is busy discarding'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_busy_discarding = {
};
export class Cyes__is_busy_discarding extends AlanNode {
	constructor(init:Tyes__is_busy_discarding, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is busy discarding'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_busy_saving = {
};
export class Cno__is_busy_saving extends AlanNode {
	constructor(init:Tno__is_busy_saving, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is busy saving'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_busy_saving = {
};
export class Cyes__is_busy_saving extends AlanNode {
	constructor(init:Tyes__is_busy_saving, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is busy saving'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_valid__node = {
};
export class Cno__is_valid__node extends AlanNode {
	constructor(init:Tno__is_valid__node, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_valid__node = {
};
export class Cyes__is_valid__node extends AlanNode {
	constructor(init:Tyes__is_valid__node, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is valid'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlocal__location__node = {
	'parent location':'local'|['local', {}]|'remote'|['remote', {}];
};
export class Clocal__location__node extends AlanNode {
	public readonly properties:{
		readonly parent_location:Clocal__location__node.Dparent_location
	};
	public readonly inferences:{
		local: () => client_state.Clocal__location__client_state
	} = {
		local: cache((detach:boolean) => {
			const client_state__node__location__local_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.variant.name === 'local' ? context.variant.definition as client_state.Clocal__location__client_state : undefined).result!;
		})

	}
	constructor(init:Tlocal__location__node, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			parent_location: new Clocal__location__node.Dparent_location(init['parent location'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.parent_location.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'location'?'local'`; }
	public get entity() { return this.parent.entity; }
}
export type Tlocal__parent_location = {
};
export class Clocal__parent_location extends AlanNode {
	constructor(init:Tlocal__parent_location, public parent:Clocal__location__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'parent location'?'local'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremote__parent_location = {
};
export class Cremote__parent_location extends AlanNode {
	constructor(init:Tremote__parent_location, public parent:Clocal__location__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'parent location'?'remote'`; }
	public get entity() { return this.parent.entity; }
}
export type Tremote__location__node = {
	'edit mode':'off'|['off', {}]|'on'|['on', {}];
};
export class Cremote__location__node extends AlanNode {
	public readonly properties:{
		readonly edit_mode:Cremote__location__node.Dedit_mode
	};
	public readonly inferences:{
		remote: () => client_state.Cremote__location__client_state
	} = {
		remote: cache((detach:boolean) => {
			const client_state__node__location__remote_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.location())
				.then(context => context?.variant.name === 'remote' ? context.variant.definition as client_state.Cremote__location__client_state : undefined).result!;
		})

	}
	constructor(init:Tremote__location__node, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			edit_mode: new Cremote__location__node.Dedit_mode(init['edit mode'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.edit_mode.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'location'?'remote'`; }
	public get entity() { return this.parent.entity; }
}
export type Toff__edit_mode__remote = {
};
export class Coff__edit_mode__remote extends AlanNode {
	constructor(init:Toff__edit_mode__remote, public parent:Cremote__location__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'off'`; }
	public get entity() { return this.parent.entity; }
}
export type Ton__edit_mode__remote = {
};
export class Con__edit_mode__remote extends AlanNode {
	constructor(init:Ton__edit_mode__remote, public parent:Cremote__location__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'edit mode'?'on'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__modified__node = {
};
export class Cno__modified__node extends AlanNode {
	constructor(init:Tno__modified__node, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__modified__node = {
};
export class Cyes__modified__node extends AlanNode {
	constructor(init:Tyes__modified__node, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modified'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tqueries__node = {
	'query':Tquery__client_state;
};
export class Cqueries__node extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly query:Cquery__client_state
	};
	constructor(key:string, init:Tqueries__node, public parent:Cnode) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			query: new Cqueries__node.Dquery(init['query'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.query.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'queries'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__read_permitted = {
};
export class Cno__read_permitted extends AlanNode {
	constructor(init:Tno__read_permitted, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'read permitted'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__read_permitted = {
};
export class Cyes__read_permitted extends AlanNode {
	constructor(init:Tyes__read_permitted, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'read permitted'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kreport_types__node extends Reference<parameters.Cparameters, string> {
	constructor(key:string, $this:Creport_types__node) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent.root.input.reports[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'report types'::key`; }
}
export type Treport_types__node = {
	'initialized':'no'|['no', {}]|['yes', Tyes__initialized__report_types];
};
export class Creport_types__node extends AlanDictionaryEntry {
	public key:Kreport_types__node;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly initialized:Creport_types__node.Dinitialized
	};
	constructor(key:string, init:Treport_types__node, public parent:Cnode) {
		super();
		const $this = this;
		this.key = new Kreport_types__node(key, $this);
		this.properties = {
			initialized: new Creport_types__node.Dinitialized(init['initialized'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.initialized.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'report types'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__initialized__report_types = {
};
export class Cno__initialized__report_types extends AlanNode {
	constructor(init:Tno__initialized__report_types, public parent:Creport_types__node) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__initialized__report_types = {
	'parameters':Treport_parameters;
	'templates':Record<string, {}>;
};
export class Cyes__initialized__report_types extends AlanNode {
	public readonly properties:{
		readonly parameters:Creport_parameters,
		readonly templates:Cyes__initialized__report_types.Dtemplates
	};
	constructor(init:Tyes__initialized__report_types, public parent:Creport_types__node) {
		super();
		const $this = this;
		this.properties = {
			parameters: new Cyes__initialized__report_types.Dparameters(init['parameters'], $this),
			templates: new Cyes__initialized__report_types.Dtemplates(init['templates'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.parameters.destroy();
		this.properties.templates.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'initialized'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttemplates = {
};
export class Ctemplates extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	constructor(key:string, init:Ttemplates, public parent:Cyes__initialized__report_types) {
		super();
		const $this = this;
		this.key = key;
	}
	public destroy(): void {
		super.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'templates'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__todo = {
};
export class Cno__todo extends AlanNode {
	constructor(init:Tno__todo, public parent:Cnode) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__todo = {
	'for':['current user', Tcurrent_user]|'someone else'|['someone else', {}];
};
export class Cyes__todo extends AlanNode {
	public readonly properties:{
		readonly for_:Cyes__todo.Dfor_
	};
	public readonly inferences:{
		has_todo: () => application.Cyes__todo
	} = {
		has_todo: cache((detach:boolean) => {
			const client_state__node__todo__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.properties.todo_definition)
				.then(context => {
					if (context?.properties.todo.state.name === 'yes') {
						return resolve(context.properties.todo.state.node as application.Cyes__todo).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__todo, public parent:Cnode) {
		super();
		const $this = this;
		this.properties = {
			for_: new Cyes__todo.Dfor_(init['for'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.for_.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'todo'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_user = {
	'and':'no one else'|['no one else', {}]|'others'|['others', {}];
};
export class Ccurrent_user extends AlanNode {
	public readonly properties:{
		readonly and:Ccurrent_user.Dand
	};
	constructor(init:Tcurrent_user, public parent:Cyes__todo) {
		super();
		const $this = this;
		this.properties = {
			and: new Ccurrent_user.Dand(init['and'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.and.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'for'?'current user'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno_one_else = {
};
export class Cno_one_else extends AlanNode {
	constructor(init:Tno_one_else, public parent:Ccurrent_user) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'and'?'no one else'`; }
	public get entity() { return this.parent.entity; }
}
export type Tothers = {
};
export class Cothers extends AlanNode {
	constructor(init:Tothers, public parent:Ccurrent_user) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'and'?'others'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsomeone_else = {
};
export class Csomeone_else extends AlanNode {
	constructor(init:Tsomeone_else, public parent:Cyes__todo) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'for'?'someone else'`; }
	public get entity() { return this.parent.entity; }
}
type Vlocation = { name: 'local', definition: (typeof Clocation.Plocal)}|{ name: 'remote', definition: (typeof Clocation.Premote)}
export class Clocation extends AlanStruct {
	public static Plocal:Clocation = new class PrimitiveInstance extends Clocation {
		constructor () {
			super({name: 'local', definition: undefined as unknown as Clocation})
			this.variant.definition = this;
		}
	}
	public static Premote:Clocation = new class PrimitiveInstance extends Clocation {
		constructor () {
			super({name: 'remote', definition: undefined as unknown as Clocation})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vlocation) { super(); }
	public cast<K extends Vlocation['name']>(_variant:K):Extract<Vlocation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vlocation['name']]:(($:Extract<Vlocation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'location'`; }
	public is(other:Clocation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Clocal__location__client_state = client_state.Clocation;
export import Cremote__location__client_state = client_state.Clocation;
export type Tid_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__id_path];
};
export class Cid_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cid_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tid_path, public location:AlanNode, public input: {
		node_type: () => application.Cnode__application
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cid_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_steps.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__id_path = {
};
export class Cno__has_steps__id_path extends AlanNode {
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node_type()).result!)
	}
	constructor(init:Tno__has_steps__id_path, public parent:Cid_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__id_path = {
	'tail':Tid_path;
	'type':['collection entry', Tcollection_entry__type__yes__has_steps__id_path]|['group', Tgroup__type__yes__has_steps__id_path]|['state', Tstate__type__yes__has_steps__id_path];
};
export class Cyes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cid_path,
		readonly type:Cyes__has_steps__id_path.Dtype
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__id_path, public parent:Cid_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__id_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__id_path.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.tail.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type__yes__has_steps__id_path = {
	'collection':string;
	'id':string;
};
export class Ccollection_entry__type__yes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry__type__yes__has_steps__id_path.Dcollection,
		readonly id:string
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.collection?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection_entry__type__yes__has_steps__id_path, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry__type__yes__has_steps__id_path.Dcollection(init['collection'], $this),
			id: init['id']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.collection.destroy();
	}
	public set_collection(value:string):void {
		this.properties.collection.unlink();
		(<(detach?:boolean) => void>(this.properties.collection as any).resolve)(true);
		(this.properties.collection as Mutable<Ccollection_entry__type__yes__has_steps__id_path.Dcollection>).entry = value;
		super.notify('collection', value);
	}
	public subscribe_collection(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('collection', observer);
		observer.on_update(this.properties.collection.entry, subscription);
		return subscription;
	}
	public set_id(value:string):void {
		(this.properties as Mutable<Ccollection_entry__type__yes__has_steps__id_path['properties']>).id = value;
		super.notify('id', value);
	}
	public subscribe_id(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('id', observer);
		observer.on_update(this.properties.id, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__id_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__id_path.Dgroup
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__id_path, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__id_path.Dgroup(init['group'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.group.destroy();
	}
	public set_group(value:string):void {
		this.properties.group.unlink();
		(<(detach?:boolean) => void>(this.properties.group as any).resolve)(true);
		(this.properties.group as Mutable<Cgroup__type__yes__has_steps__id_path.Dgroup>).entry = value;
		super.notify('group', value);
	}
	public subscribe_group(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('group', observer);
		observer.on_update(this.properties.group.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__id_path = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__id_path extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__id_path.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__id_path.Dstate_group
	};
	public readonly output:{
		result_node: () => application.Cnode__application;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__yes__has_steps__id_path, public parent:Cyes__has_steps__id_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__id_path.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__id_path.Dstate_group(init['state group'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.state.destroy();
		this.properties.state_group.destroy();
	}
	public set_state(value:string):void {
		this.properties.state.unlink();
		(<(detach?:boolean) => void>(this.properties.state as any).resolve)(true);
		(this.properties.state as Mutable<Cstate__type__yes__has_steps__id_path.Dstate>).entry = value;
		super.notify('state', value);
	}
	public subscribe_state(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state', observer);
		observer.on_update(this.properties.state.entry, subscription);
		return subscription;
	}
	public set_state_group(value:string):void {
		this.properties.state_group.unlink();
		(<(detach?:boolean) => void>(this.properties.state_group as any).resolve)(true);
		(this.properties.state_group as Mutable<Cstate__type__yes__has_steps__id_path.Dstate_group>).entry = value;
		super.notify('state group', value);
	}
	public subscribe_state_group(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state group', observer);
		observer.on_update(this.properties.state_group.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tclient_id_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__client_id_path];
};
export class Cclient_id_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cclient_id_path.Dhas_steps
	};
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tclient_id_path, public location:AlanNode, public input: {
		location: () => client_state.Cid_path_location,
		node: () => client_state.Cnode
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cclient_id_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_steps.destroy();
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__client_id_path = {
};
export class Cno__has_steps__client_id_path extends AlanNode {
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_steps__client_id_path, public parent:Cclient_id_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__client_id_path = {
	'tail':Tclient_id_path;
	'type':['collection entry', Tcollection_entry__type__yes__has_steps__client_id_path]|['group', Tgroup__type__yes__has_steps__client_id_path]|['operation', Toperation__type__yes]|['state', Tstate__type__yes__has_steps__client_id_path];
};
export class Cyes__has_steps__client_id_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cclient_id_path,
		readonly type:Cyes__has_steps__client_id_path.Dtype
	};
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__client_id_path, public parent:Cclient_id_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__client_id_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__client_id_path.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.tail.destroy();
		this.properties.type.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection_entry__type__yes__has_steps__client_id_path = {
	'collection':string;
	'entry':string;
};
export class Ccollection_entry__type__yes__has_steps__client_id_path extends AlanNode {
	public readonly properties:{
		readonly collection:Ccollection_entry__type__yes__has_steps__client_id_path.Dcollection,
		readonly entry:Ccollection_entry__type__yes__has_steps__client_id_path.Dentry
	};
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.entry?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tcollection_entry__type__yes__has_steps__client_id_path, public parent:Cyes__has_steps__client_id_path) {
		super();
		const $this = this;
		this.properties = {
			collection: new Ccollection_entry__type__yes__has_steps__client_id_path.Dcollection(init['collection'], $this),
			entry: new Ccollection_entry__type__yes__has_steps__client_id_path.Dentry(init['entry'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.collection.destroy();
		this.properties.entry.destroy();
	}
	public set_collection(value:string):void {
		this.properties.collection.unlink();
		(<(detach?:boolean) => void>(this.properties.collection as any).resolve)(true);
		(this.properties.collection as Mutable<Ccollection_entry__type__yes__has_steps__client_id_path.Dcollection>).entry = value;
		super.notify('collection', value);
	}
	public subscribe_collection(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('collection', observer);
		observer.on_update(this.properties.collection.entry, subscription);
		return subscription;
	}
	public set_entry(value:string):void {
		this.properties.entry.unlink();
		(<(detach?:boolean) => void>(this.properties.entry as any).resolve)(true);
		(this.properties.entry as Mutable<Ccollection_entry__type__yes__has_steps__client_id_path.Dentry>).entry = value;
		super.notify('entry', value);
	}
	public subscribe_entry(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('entry', observer);
		observer.on_update(this.properties.entry.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes__has_steps__client_id_path = {
	'group':string;
};
export class Cgroup__type__yes__has_steps__client_id_path extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes__has_steps__client_id_path.Dgroup
	};
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tgroup__type__yes__has_steps__client_id_path, public parent:Cyes__has_steps__client_id_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes__has_steps__client_id_path.Dgroup(init['group'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.group.destroy();
	}
	public set_group(value:string):void {
		this.properties.group.unlink();
		(<(detach?:boolean) => void>(this.properties.group as any).resolve)(true);
		(this.properties.group as Mutable<Cgroup__type__yes__has_steps__client_id_path.Dgroup>).entry = value;
		super.notify('group', value);
	}
	public subscribe_group(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('group', observer);
		observer.on_update(this.properties.group.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Toperation__type__yes = {
	'operation':string;
};
export class Coperation__type__yes extends AlanNode {
	public readonly properties:{
		readonly operation:Coperation__type__yes.Doperation
	};
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.operation?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Toperation__type__yes, public parent:Cyes__has_steps__client_id_path) {
		super();
		const $this = this;
		this.properties = {
			operation: new Coperation__type__yes.Doperation(init['operation'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.operation.destroy();
	}
	public set_operation(value:string):void {
		this.properties.operation.unlink();
		(<(detach?:boolean) => void>(this.properties.operation as any).resolve)(true);
		(this.properties.operation as Mutable<Coperation__type__yes.Doperation>).entry = value;
		super.notify('operation', value);
	}
	public subscribe_operation(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('operation', observer);
		observer.on_update(this.properties.operation.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type__yes__has_steps__client_id_path = {
	'state':string;
	'state group':string;
};
export class Cstate__type__yes__has_steps__client_id_path extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type__yes__has_steps__client_id_path.Dstate,
		readonly state_group:Cstate__type__yes__has_steps__client_id_path.Dstate_group
	};
	public readonly output:{
		result_node: () => client_state.Cnode;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.node).result!)
	}
	constructor(init:Tstate__type__yes__has_steps__client_id_path, public parent:Cyes__has_steps__client_id_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type__yes__has_steps__client_id_path.Dstate(init['state'], $this),
			state_group: new Cstate__type__yes__has_steps__client_id_path.Dstate_group(init['state group'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.state.destroy();
		this.properties.state_group.destroy();
	}
	public set_state(value:string):void {
		this.properties.state.unlink();
		(<(detach?:boolean) => void>(this.properties.state as any).resolve)(true);
		(this.properties.state as Mutable<Cstate__type__yes__has_steps__client_id_path.Dstate>).entry = value;
		super.notify('state', value);
	}
	public subscribe_state(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state', observer);
		observer.on_update(this.properties.state.entry, subscription);
		return subscription;
	}
	public set_state_group(value:string):void {
		this.properties.state_group.unlink();
		(<(detach?:boolean) => void>(this.properties.state_group as any).resolve)(true);
		(this.properties.state_group as Mutable<Cstate__type__yes__has_steps__client_id_path.Dstate_group>).entry = value;
		super.notify('state group', value);
	}
	public subscribe_state_group(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('state group', observer);
		observer.on_update(this.properties.state_group.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}

export type Tclient_state = {
	'alt key pressed':'no'|['no', {}]|'yes'|['yes', {}];
	'browser family':'Chromium'|['Chromium', {}]|'Edge'|['Edge', {}]|'Safari'|['Safari', {}]|'undetermined'|['undetermined', {}];
	'ctrl key pressed':'no'|['no', {}]|'yes'|['yes', {}];
	'has landing page':'no'|['no', {}]|'yes'|['yes', {}];
	'is editable':'no'|['no', {}]|'yes'|['yes', {}];
	'languages':Record<string, Tlanguages>;
	'logo url':string;
	'logo-sideways url':string;
	'messages':Record<string, Tmessages>;
	'meta key pressed':'no'|['no', {}]|'yes'|['yes', {}];
	'modules':Record<string, Tmodules>;
	'network connection status':'alive'|['alive', {}]|'dead'|['dead', {}];
	'platform family':'Android'|['Android', {}]|'Linux'|['Linux', {}]|'Windows'|['Windows', {}]|'iOS'|['iOS', {}]|'macOS'|['macOS', {}]|'undetermined'|['undetermined', {}];
	'report types':Record<string, {}>;
	'reports':Record<string, Treports>;
	'root':Tnode;
	'selected language':string;
	'server connection status':'alive'|['alive', {}]|'dead'|['dead', {}];
	'session':Tsession;
	'shift key pressed':'no'|['no', {}]|'yes'|['yes', {}];
	'toggle key pressed':'no'|['no', {}]|'yes'|['yes', {}];
	'version':string;
	'windows':Record<string, Twindows>;
};
export class Cclient_state extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public definitions:{
		entity: Centity;
		root_position: Croot_position;
	} = {
		entity: new Centity({name:'root', definition: this}, {
			node: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.root).result!),
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(() => client_state.Cnode_parent.Pnone).result!),
			root: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.definitions.root_position).result!)
		}),
		root_position: new Croot_position({name:'root', definition: this})
	}
	public readonly properties:{
		readonly alt_key_pressed:Cclient_state.Dalt_key_pressed,
		readonly browser_family:Cclient_state.Dbrowser_family,
		readonly ctrl_key_pressed:Cclient_state.Dctrl_key_pressed,
		readonly has_landing_page:Cclient_state.Dhas_landing_page,
		readonly is_editable:Cclient_state.Dis_editable,
		readonly languages:Cclient_state.Dlanguages,
		readonly logo_url:string,
		readonly logo_sideways_url:string,
		readonly messages:Cclient_state.Dmessages,
		readonly meta_key_pressed:Cclient_state.Dmeta_key_pressed,
		readonly modules:Cclient_state.Dmodules,
		readonly network_connection_status:Cclient_state.Dnetwork_connection_status,
		readonly platform_family:Cclient_state.Dplatform_family,
		readonly report_types:Cclient_state.Dreport_types,
		readonly reports:Cclient_state.Dreports,
		readonly root:Cnode,
		readonly selected_language:Cclient_state.Dselected_language,
		readonly server_connection_status:Cclient_state.Dserver_connection_status,
		readonly session:Csession,
		readonly shift_key_pressed:Cclient_state.Dshift_key_pressed,
		readonly toggle_key_pressed:Cclient_state.Dtoggle_key_pressed,
		readonly version:string,
		readonly windows:Cclient_state.Dwindows
	};
	constructor(init:Tclient_state, public readonly input: {
	'application':application.Capplication
	'client_bindings':client_bindings.Cclient_bindings
	'gui_model':gui_model.Cgui_model
	'languages':{[name:string]:translations.Ctranslations}
	'reports':{[name:string]:parameters.Cparameters}
	'settings':settings.Csettings
	'views':{[name:string]:views.Cviews}
	'widget':{[name:string]:widget.Cwidget}}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			alt_key_pressed: new Cclient_state.Dalt_key_pressed(init['alt key pressed'], $this),
			browser_family: new Cclient_state.Dbrowser_family(init['browser family'], $this),
			ctrl_key_pressed: new Cclient_state.Dctrl_key_pressed(init['ctrl key pressed'], $this),
			has_landing_page: new Cclient_state.Dhas_landing_page(init['has landing page'], $this),
			is_editable: new Cclient_state.Dis_editable(init['is editable'], $this),
			languages: new Cclient_state.Dlanguages(init['languages'], $this),
			logo_url: init['logo url'],
			logo_sideways_url: init['logo-sideways url'],
			messages: new Cclient_state.Dmessages(init['messages'], $this),
			meta_key_pressed: new Cclient_state.Dmeta_key_pressed(init['meta key pressed'], $this),
			modules: new Cclient_state.Dmodules(init['modules'], $this),
			network_connection_status: new Cclient_state.Dnetwork_connection_status(init['network connection status'], $this),
			platform_family: new Cclient_state.Dplatform_family(init['platform family'], $this),
			report_types: new Cclient_state.Dreport_types(init['report types'], $this),
			reports: new Cclient_state.Dreports(init['reports'], $this),
			root: new Cclient_state.Droot(init['root'], $this),
			selected_language: new Cclient_state.Dselected_language(init['selected language'], $this),
			server_connection_status: new Cclient_state.Dserver_connection_status(init['server connection status'], $this),
			session: new Csession(init['session'], $this),
			shift_key_pressed: new Cclient_state.Dshift_key_pressed(init['shift key pressed'], $this),
			toggle_key_pressed: new Cclient_state.Dtoggle_key_pressed(init['toggle key pressed'], $this),
			version: init['version'],
			windows: new Cclient_state.Dwindows(init['windows'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.alt_key_pressed.destroy();
		this.properties.browser_family.destroy();
		this.properties.ctrl_key_pressed.destroy();
		this.properties.has_landing_page.destroy();
		this.properties.is_editable.destroy();
		this.properties.languages.destroy();
		this.properties.messages.destroy();
		this.properties.meta_key_pressed.destroy();
		this.properties.modules.destroy();
		this.properties.network_connection_status.destroy();
		this.properties.platform_family.destroy();
		this.properties.report_types.destroy();
		this.properties.reports.destroy();
		this.properties.root.destroy();
		this.properties.selected_language.destroy();
		this.properties.server_connection_status.destroy();
		this.properties.session.destroy();
		this.properties.shift_key_pressed.destroy();
		this.properties.toggle_key_pressed.destroy();
		this.properties.windows.destroy();
	}
	public set_logo_url(value:string):void {
		(this.properties as Mutable<Cclient_state['properties']>).logo_url = value;
		super.notify('logo url', value);
	}
	public subscribe_logo_url(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('logo url', observer);
		observer.on_update(this.properties.logo_url, subscription);
		return subscription
	}
	public set_logo_sideways_url(value:string):void {
		(this.properties as Mutable<Cclient_state['properties']>).logo_sideways_url = value;
		super.notify('logo-sideways url', value);
	}
	public subscribe_logo_sideways_url(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('logo-sideways url', observer);
		observer.on_update(this.properties.logo_sideways_url, subscription);
		return subscription
	}
	public set_selected_language(value:string):void {
		this.properties.selected_language.unlink();
		(<(detach?:boolean) => void>(this.properties.selected_language as any).resolve)(true);
		(this.properties.selected_language as Mutable<Cclient_state.Dselected_language>).entry = value;
		super.notify('selected language', value);
	}
	public subscribe_selected_language(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('selected language', observer);
		observer.on_update(this.properties.selected_language.entry, subscription);
		return subscription;
	}
	public set_version(value:string):void {
		(this.properties as Mutable<Cclient_state['properties']>).version = value;
		super.notify('version', value);
	}
	public subscribe_version(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('version', observer);
		observer.on_update(this.properties.version, subscription);
		return subscription
	}
	public get path() { return ``; }

	private _transacting: boolean = false;
	public get transacting() {
		return this._transacting
	}
	private _deferred_subscribers = new Set<() => void>();
	defer(callback: () => void, destructor?: IDestructable): void {
		this._deferred_subscribers.add(() => destructor?.destroyed ? void 0 : callback());
		this.notify_subscribers();
	}
	private _deferred_destruction_subscribers = new Set<() => void>();
	defer_destruction(callback: () => void): void {
		this._deferred_destruction_subscribers.add(() => callback());
		this.notify_subscribers();
	}
	private _notifying_subscribers = false;
	private notify_subscribers() {
		if (this._transacting === false && this._notifying_subscribers === false) {
			this._notifying_subscribers = true;
			let subscriber: () => void
			while (subscriber = this._deferred_destruction_subscribers.values().next().value ?? this._deferred_subscribers.values().next().value) {
				this._deferred_destruction_subscribers.delete(subscriber);
				this._deferred_subscribers.delete(subscriber);
				subscriber();
			}
			this._deferred_subscribers.clear();
			this._notifying_subscribers = false;
		}
	}
	public transact(doapply:() => void) {
		if (this._transacting === true) {
			doapply();
		} else {
			this._transacting = true;
			doapply();
			this._transacting = false;
			this.notify_subscribers();
		}
	}
	public get entity() { return this; }
}
export type Tno__alt_key_pressed = {
};
export class Cno__alt_key_pressed extends AlanNode {
	constructor(init:Tno__alt_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'alt key pressed'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__alt_key_pressed = {
};
export class Cyes__alt_key_pressed extends AlanNode {
	constructor(init:Tyes__alt_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'alt key pressed'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type TChromium = {
};
export class CChromium extends AlanNode {
	constructor(init:TChromium, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'browser family'?'Chromium'`; }
	public get entity() { return this.parent.entity; }
}
export type TEdge = {
};
export class CEdge extends AlanNode {
	constructor(init:TEdge, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'browser family'?'Edge'`; }
	public get entity() { return this.parent.entity; }
}
export type TSafari = {
};
export class CSafari extends AlanNode {
	constructor(init:TSafari, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'browser family'?'Safari'`; }
	public get entity() { return this.parent.entity; }
}
export type Tundetermined__browser_family = {
};
export class Cundetermined__browser_family extends AlanNode {
	constructor(init:Tundetermined__browser_family, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'browser family'?'undetermined'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__ctrl_key_pressed = {
};
export class Cno__ctrl_key_pressed extends AlanNode {
	constructor(init:Tno__ctrl_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ctrl key pressed'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__ctrl_key_pressed = {
};
export class Cyes__ctrl_key_pressed extends AlanNode {
	constructor(init:Tyes__ctrl_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ctrl key pressed'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_landing_page = {
};
export class Cno__has_landing_page extends AlanNode {
	constructor(init:Tno__has_landing_page, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has landing page'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_landing_page = {
};
export class Cyes__has_landing_page extends AlanNode {
	constructor(init:Tyes__has_landing_page, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has landing page'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_editable__client_state = {
};
export class Cno__is_editable__client_state extends AlanNode {
	constructor(init:Tno__is_editable__client_state, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_editable__client_state = {
};
export class Cyes__is_editable__client_state extends AlanNode {
	constructor(init:Tyes__is_editable__client_state, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is editable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Klanguages extends Reference<translations.Ctranslations, string> {
	constructor(key:string, $this:Clanguages) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent.root.input.languages[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'languages'::key`; }
}
export type Tlanguages = {
	'label':string;
};
export class Clanguages extends AlanDictionaryEntry {
	public key:Klanguages;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly label:string
	};
	constructor(key:string, init:Tlanguages, public parent:Cclient_state) {
		super();
		const $this = this;
		this.key = new Klanguages(key, $this);
		this.properties = {
			label: init['label']
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
	}
	public set_label(value:string):void {
		(this.properties as Mutable<Clanguages['properties']>).label = value;
		super.notify('label', value);
	}
	public subscribe_label(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('label', observer);
		observer.on_update(this.properties.label, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'languages'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tmessages = {
	'has body':'no'|['no', {}]|['yes', Tyes__has_body];
	'title':string;
	'type':'error'|['error', {}]|'success'|['success', {}]|'waiting'|['waiting', {}]|'warning'|['warning', {}];
};
export class Cmessages extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_body:Cmessages.Dhas_body,
		readonly title:string,
		readonly type:Cmessages.Dtype
	};
	constructor(key:string, init:Tmessages, public parent:Cclient_state) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_body: new Cmessages.Dhas_body(init['has body'], $this),
			title: init['title'],
			type: new Cmessages.Dtype(init['type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_body.destroy();
		this.properties.type.destroy();
	}
	public set_title(value:string):void {
		(this.properties as Mutable<Cmessages['properties']>).title = value;
		super.notify('title', value);
	}
	public subscribe_title(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('title', observer);
		observer.on_update(this.properties.title, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'messages'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_body = {
};
export class Cno__has_body extends AlanNode {
	constructor(init:Tno__has_body, public parent:Cmessages) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has body'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_body = {
	'body':string;
};
export class Cyes__has_body extends AlanNode {
	public readonly properties:{
		readonly body:string
	};
	constructor(init:Tyes__has_body, public parent:Cmessages) {
		super();
		const $this = this;
		this.properties = {
			body: init['body']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_body(value:string):void {
		(this.properties as Mutable<Cyes__has_body['properties']>).body = value;
		super.notify('body', value);
	}
	public subscribe_body(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('body', observer);
		observer.on_update(this.properties.body, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has body'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Terror__type = {
};
export class Cerror__type extends AlanNode {
	constructor(init:Terror__type, public parent:Cmessages) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'error'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsuccess__type = {
};
export class Csuccess__type extends AlanNode {
	constructor(init:Tsuccess__type, public parent:Cmessages) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'success'`; }
	public get entity() { return this.parent.entity; }
}
export type Twaiting = {
};
export class Cwaiting extends AlanNode {
	constructor(init:Twaiting, public parent:Cmessages) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'waiting'`; }
	public get entity() { return this.parent.entity; }
}
export type Twarning__type = {
};
export class Cwarning__type extends AlanNode {
	constructor(init:Twarning__type, public parent:Cmessages) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'warning'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__meta_key_pressed = {
};
export class Cno__meta_key_pressed extends AlanNode {
	constructor(init:Tno__meta_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'meta key pressed'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__meta_key_pressed = {
};
export class Cyes__meta_key_pressed extends AlanNode {
	constructor(init:Tyes__meta_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'meta key pressed'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmodules = {
	'has logo':'no'|['no', {}]|['yes', Tyes__has_logo];
	'name':string;
};
export class Cmodules extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly has_logo:Cmodules.Dhas_logo,
		readonly name:string
	};
	constructor(key:string, init:Tmodules, public parent:Cclient_state) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			has_logo: new Cmodules.Dhas_logo(init['has logo'], $this),
			name: init['name']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.has_logo.destroy();
	}
	public set_name(value:string):void {
		(this.properties as Mutable<Cmodules['properties']>).name = value;
		super.notify('name', value);
	}
	public subscribe_name(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('name', observer);
		observer.on_update(this.properties.name, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'modules'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__has_logo = {
};
export class Cno__has_logo extends AlanNode {
	constructor(init:Tno__has_logo, public parent:Cmodules) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has logo'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_logo = {
	'url':string;
};
export class Cyes__has_logo extends AlanNode {
	public readonly properties:{
		readonly url:string
	};
	constructor(init:Tyes__has_logo, public parent:Cmodules) {
		super();
		const $this = this;
		this.properties = {
			url: init['url']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_url(value:string):void {
		(this.properties as Mutable<Cyes__has_logo['properties']>).url = value;
		super.notify('url', value);
	}
	public subscribe_url(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('url', observer);
		observer.on_update(this.properties.url, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has logo'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Talive__network_connection_status = {
};
export class Calive__network_connection_status extends AlanNode {
	constructor(init:Talive__network_connection_status, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'network connection status'?'alive'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdead__network_connection_status = {
};
export class Cdead__network_connection_status extends AlanNode {
	constructor(init:Tdead__network_connection_status, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'network connection status'?'dead'`; }
	public get entity() { return this.parent.entity; }
}
export type TAndroid = {
};
export class CAndroid extends AlanNode {
	constructor(init:TAndroid, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'platform family'?'Android'`; }
	public get entity() { return this.parent.entity; }
}
export type TLinux = {
};
export class CLinux extends AlanNode {
	constructor(init:TLinux, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'platform family'?'Linux'`; }
	public get entity() { return this.parent.entity; }
}
export type TWindows = {
};
export class CWindows extends AlanNode {
	constructor(init:TWindows, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'platform family'?'Windows'`; }
	public get entity() { return this.parent.entity; }
}
export type TiOS = {
};
export class CiOS extends AlanNode {
	constructor(init:TiOS, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'platform family'?'iOS'`; }
	public get entity() { return this.parent.entity; }
}
export type TmacOS = {
};
export class CmacOS extends AlanNode {
	constructor(init:TmacOS, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'platform family'?'macOS'`; }
	public get entity() { return this.parent.entity; }
}
export type Tundetermined__platform_family = {
};
export class Cundetermined__platform_family extends AlanNode {
	constructor(init:Tundetermined__platform_family, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'platform family'?'undetermined'`; }
	public get entity() { return this.parent.entity; }
}
export class Kreport_types__client_state extends Reference<parameters.Cparameters, string> {
	constructor(key:string, $this:Creport_types__client_state) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent.root.input.reports[this.entry]).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'report types'::key`; }
}
export type Treport_types__client_state = {
};
export class Creport_types__client_state extends AlanDictionaryEntry {
	public key:Kreport_types__client_state;
	public get key_value() { return this.key.entry; }
	constructor(key:string, init:Treport_types__client_state, public parent:Cclient_state) {
		super();
		const $this = this;
		this.key = new Kreport_types__client_state(key, $this);
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'report types'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Treports = {
	'definition':['known', Tknown]|'unknown'|['unknown', {}];
	'permissions':'read only'|['read only', {}]|'writable'|['writable', {}];
};
export class Creports extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly definition:Creports.Ddefinition,
		readonly permissions:Creports.Dpermissions
	};
	constructor(key:string, init:Treports, public parent:Cclient_state) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			definition: new Creports.Ddefinition(init['definition'], $this),
			permissions: new Creports.Dpermissions(init['permissions'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.definition.destroy();
		this.properties.permissions.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'reports'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tknown = {
	'of type':string;
};
export class Cknown extends AlanNode {
	public readonly properties:{
		readonly of_type:Cknown.Dof_type
	};
	constructor(init:Tknown, public parent:Creports) {
		super();
		const $this = this;
		this.properties = {
			of_type: new Cknown.Dof_type(init['of type'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.of_type.destroy();
	}
	public set_of_type(value:string):void {
		this.properties.of_type.unlink();
		(<(detach?:boolean) => void>(this.properties.of_type as any).resolve)(true);
		(this.properties.of_type as Mutable<Cknown.Dof_type>).entry = value;
		super.notify('of type', value);
	}
	public subscribe_of_type(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('of type', observer);
		observer.on_update(this.properties.of_type.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'definition'?'known'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunknown__definition = {
};
export class Cunknown__definition extends AlanNode {
	constructor(init:Tunknown__definition, public parent:Creports) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'definition'?'unknown'`; }
	public get entity() { return this.parent.entity; }
}
export type Tread_only = {
};
export class Cread_only extends AlanNode {
	constructor(init:Tread_only, public parent:Creports) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'permissions'?'read only'`; }
	public get entity() { return this.parent.entity; }
}
export type Twritable = {
};
export class Cwritable extends AlanNode {
	constructor(init:Twritable, public parent:Creports) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'permissions'?'writable'`; }
	public get entity() { return this.parent.entity; }
}
export type Talive__server_connection_status = {
};
export class Calive__server_connection_status extends AlanNode {
	constructor(init:Talive__server_connection_status, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'server connection status'?'alive'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdead__server_connection_status = {
};
export class Cdead__server_connection_status extends AlanNode {
	constructor(init:Tdead__server_connection_status, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'server connection status'?'dead'`; }
	public get entity() { return this.parent.entity; }
}
export type Tsession = {
	'status':['authenticated', Tauthenticated]|'not authenticated'|['not authenticated', {}];
};
export class Csession extends AlanNode {
	public readonly properties:{
		readonly status:Csession.Dstatus
	};
	constructor(init:Tsession, public parent:Cclient_state) {
		super();
		const $this = this;
		this.properties = {
			status: new Csession.Dstatus(init['status'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.status.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'session'`; }
	public get entity() { return this.parent.entity; }
}
export type Tauthenticated = {
	'anonymous session':['no', Tno__anonymous_session]|'yes'|['yes', {}];
	'token':string;
};
export class Cauthenticated extends AlanNode {
	public readonly properties:{
		readonly anonymous_session:Cauthenticated.Danonymous_session,
		readonly token:string
	};
	constructor(init:Tauthenticated, public parent:Csession) {
		super();
		const $this = this;
		this.properties = {
			anonymous_session: new Cauthenticated.Danonymous_session(init['anonymous session'], $this),
			token: init['token']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.anonymous_session.destroy();
	}
	public set_token(value:string):void {
		(this.properties as Mutable<Cauthenticated['properties']>).token = value;
		super.notify('token', value);
	}
	public subscribe_token(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('token', observer);
		observer.on_update(this.properties.token, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'status'?'authenticated'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__anonymous_session = {
	'user found':'no'|['no', {}]|'yes'|['yes', {}];
	'username':string;
};
export class Cno__anonymous_session extends AlanNode {
	public readonly properties:{
		readonly user_found:Cno__anonymous_session.Duser_found,
		readonly username:string
	};
	constructor(init:Tno__anonymous_session, public parent:Cauthenticated) {
		super();
		const $this = this;
		this.properties = {
			user_found: new Cno__anonymous_session.Duser_found(init['user found'], $this),
			username: init['username']
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.user_found.destroy();
	}
	public set_username(value:string):void {
		(this.properties as Mutable<Cno__anonymous_session['properties']>).username = value;
		super.notify('username', value);
	}
	public subscribe_username(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('username', observer);
		observer.on_update(this.properties.username, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'anonymous session'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__user_found = {
};
export class Cno__user_found extends AlanNode {
	constructor(init:Tno__user_found, public parent:Cno__anonymous_session) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user found'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__user_found = {
};
export class Cyes__user_found extends AlanNode {
	constructor(init:Tyes__user_found, public parent:Cno__anonymous_session) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'user found'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__anonymous_session = {
};
export class Cyes__anonymous_session extends AlanNode {
	constructor(init:Tyes__anonymous_session, public parent:Cauthenticated) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'anonymous session'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnot_authenticated = {
};
export class Cnot_authenticated extends AlanNode {
	constructor(init:Tnot_authenticated, public parent:Csession) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'status'?'not authenticated'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__shift_key_pressed = {
};
export class Cno__shift_key_pressed extends AlanNode {
	constructor(init:Tno__shift_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'shift key pressed'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__shift_key_pressed = {
};
export class Cyes__shift_key_pressed extends AlanNode {
	constructor(init:Tyes__shift_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'shift key pressed'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__toggle_key_pressed = {
};
export class Cno__toggle_key_pressed extends AlanNode {
	constructor(init:Tno__toggle_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'toggle key pressed'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__toggle_key_pressed = {
};
export class Cyes__toggle_key_pressed extends AlanNode {
	constructor(init:Tyes__toggle_key_pressed, public parent:Cclient_state) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'toggle key pressed'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kwindows extends Reference<settings.Cwindows, string> {
	constructor(key:string, $this:Cwindows) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.root.input.settings)
			.then(context => {
				const entry = context?.properties.windows.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'windows'::key`; }
}
export type Twindows = {
	'has active view':'no'|['no', {}]|['yes', Tyes__has_active_view];
	'open views':Record<string, Topen_views>;
};
export class Cwindows extends AlanDictionaryEntry {
	public key:Kwindows;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly has_active_view:Cwindows.Dhas_active_view,
		readonly open_views:Cwindows.Dopen_views
	};
	constructor(key:string, init:Twindows, public parent:Cclient_state) {
		super();
		const $this = this;
		this.key = new Kwindows(key, $this);
		this.properties = {
			has_active_view: new Cwindows.Dhas_active_view(init['has active view'], $this),
			open_views: new Cwindows.Dopen_views(init['open views'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.key.destroy();
		this.properties.has_active_view.destroy();
		this.properties.open_views.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'windows'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__has_active_view = {
};
export class Cno__has_active_view extends AlanNode {
	constructor(init:Tno__has_active_view, public parent:Cwindows) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has active view'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_active_view = {
	'view':string;
};
export class Cyes__has_active_view extends AlanNode {
	public readonly properties:{
		readonly view:Cyes__has_active_view.Dview
	};
	constructor(init:Tyes__has_active_view, public parent:Cwindows) {
		super();
		const $this = this;
		this.properties = {
			view: new Cyes__has_active_view.Dview(init['view'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.view.destroy();
	}
	public set_view(value:string):void {
		this.properties.view.unlink();
		(<(detach?:boolean) => void>(this.properties.view as any).resolve)(true);
		(this.properties.view as Mutable<Cyes__has_active_view.Dview>).entry = value;
		super.notify('view', value);
	}
	public subscribe_view(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('view', observer);
		observer.on_update(this.properties.view.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has active view'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Topen_views = {
	'action active':'no'|['no', {}]|['yes', Tyes__action_active];
	'is active view':'no'|['no', {}]|'yes'|['yes', {}];
	'is entry view':['no', Tno__is_entry_view]|['yes', Tyes__is_entry_view];
	'subscribe':'no'|['no', {}]|'yes'|['yes', {}];
	'view context':Tclient_id_path;
	'view definition':string;
	'view definition context':string;
};
export class Copen_views extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public definitions:{
		id_path_location: Cid_path_location;
	} = {
		id_path_location: new Cid_path_location({name:'view', definition: this})
	}
	public readonly properties:{
		readonly action_active:Copen_views.Daction_active,
		readonly is_active_view:Copen_views.Dis_active_view,
		readonly is_entry_view:Copen_views.Dis_entry_view,
		readonly subscribe:Copen_views.Dsubscribe,
		readonly view_context:Cclient_id_path,
		readonly view_definition:Copen_views.Dview_definition,
		readonly view_definition_context:Copen_views.Dview_definition_context
	};
	constructor(key:string, init:Topen_views, public parent:Cwindows) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			action_active: new Copen_views.Daction_active(init['action active'], $this),
			is_active_view: new Copen_views.Dis_active_view(init['is active view'], $this),
			is_entry_view: new Copen_views.Dis_entry_view(init['is entry view'], $this),
			subscribe: new Copen_views.Dsubscribe(init['subscribe'], $this),
			view_context: new Copen_views.Dview_context(init['view context'], $this),
			view_definition: new Copen_views.Dview_definition(init['view definition'], $this),
			view_definition_context: new Copen_views.Dview_definition_context(init['view definition context'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.action_active.destroy();
		this.properties.is_active_view.destroy();
		this.properties.is_entry_view.destroy();
		this.properties.subscribe.destroy();
		this.properties.view_context.destroy();
		this.properties.view_definition.destroy();
		this.properties.view_definition_context.destroy();
	}
	public set_view_definition(value:string):void {
		this.properties.view_definition.unlink();
		(<(detach?:boolean) => void>(this.properties.view_definition as any).resolve)(true);
		(this.properties.view_definition as Mutable<Copen_views.Dview_definition>).entry = value;
		super.notify('view definition', value);
	}
	public subscribe_view_definition(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('view definition', observer);
		observer.on_update(this.properties.view_definition.entry, subscription);
		return subscription;
	}
	public set_view_definition_context(value:string):void {
		this.properties.view_definition_context.unlink();
		(<(detach?:boolean) => void>(this.properties.view_definition_context as any).resolve)(true);
		(this.properties.view_definition_context as Mutable<Copen_views.Dview_definition_context>).entry = value;
		super.notify('view definition context', value);
	}
	public subscribe_view_definition_context(observer:TextObserver):Subscription { 
		const subscription = super._subscribe_property('view definition context', observer);
		observer.on_update(this.properties.view_definition_context.entry, subscription);
		return subscription;
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'open views'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tno__action_active = {
};
export class Cno__action_active extends AlanNode {
	constructor(init:Tno__action_active, public parent:Copen_views) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'action active'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__action_active = {
	'can finish step':'no'|['no', {}]|'yes'|['yes', {}];
	'last step':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__action_active extends AlanNode {
	public readonly properties:{
		readonly can_finish_step:Cyes__action_active.Dcan_finish_step,
		readonly last_step:Cyes__action_active.Dlast_step
	};
	constructor(init:Tyes__action_active, public parent:Copen_views) {
		super();
		const $this = this;
		this.properties = {
			can_finish_step: new Cyes__action_active.Dcan_finish_step(init['can finish step'], $this),
			last_step: new Cyes__action_active.Dlast_step(init['last step'], $this)
		};
	}
	public destroy(): void {
		super.destroy();
		this.properties.can_finish_step.destroy();
		this.properties.last_step.destroy();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'action active'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__can_finish_step = {
};
export class Cno__can_finish_step extends AlanNode {
	constructor(init:Tno__can_finish_step, public parent:Cyes__action_active) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can finish step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__can_finish_step = {
};
export class Cyes__can_finish_step extends AlanNode {
	constructor(init:Tyes__can_finish_step, public parent:Cyes__action_active) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'can finish step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__last_step = {
};
export class Cno__last_step extends AlanNode {
	constructor(init:Tno__last_step, public parent:Cyes__action_active) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last step'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__last_step = {
};
export class Cyes__last_step extends AlanNode {
	constructor(init:Tyes__last_step, public parent:Cyes__action_active) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'last step'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_active_view = {
};
export class Cno__is_active_view extends AlanNode {
	constructor(init:Tno__is_active_view, public parent:Copen_views) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is active view'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_active_view = {
};
export class Cyes__is_active_view extends AlanNode {
	constructor(init:Tyes__is_active_view, public parent:Copen_views) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is active view'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__is_entry_view = {
	'title':string;
};
export class Cno__is_entry_view extends AlanNode {
	public readonly properties:{
		readonly title:string
	};
	constructor(init:Tno__is_entry_view, public parent:Copen_views) {
		super();
		const $this = this;
		this.properties = {
			title: init['title']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_title(value:string):void {
		(this.properties as Mutable<Cno__is_entry_view['properties']>).title = value;
		super.notify('title', value);
	}
	public subscribe_title(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('title', observer);
		observer.on_update(this.properties.title, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is entry view'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__is_entry_view = {
	'entry key':string;
	'entry path':string;
};
export class Cyes__is_entry_view extends AlanNode {
	public readonly properties:{
		readonly entry_key:string,
		readonly entry_path:string
	};
	constructor(init:Tyes__is_entry_view, public parent:Copen_views) {
		super();
		const $this = this;
		this.properties = {
			entry_key: init['entry key'],
			entry_path: init['entry path']
		};
	}
	public destroy(): void {
		super.destroy();
	}
	public set_entry_key(value:string):void {
		(this.properties as Mutable<Cyes__is_entry_view['properties']>).entry_key = value;
		super.notify('entry key', value);
	}
	public subscribe_entry_key(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('entry key', observer);
		observer.on_update(this.properties.entry_key, subscription);
		return subscription
	}
	public set_entry_path(value:string):void {
		(this.properties as Mutable<Cyes__is_entry_view['properties']>).entry_path = value;
		super.notify('entry path', value);
	}
	public subscribe_entry_path(observer:TextObserver):Subscription {
		const subscription = super._subscribe_property('entry path', observer);
		observer.on_update(this.properties.entry_path, subscription);
		return subscription
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'is entry view'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__subscribe = {
};
export class Cno__subscribe extends AlanNode {
	constructor(init:Tno__subscribe, public parent:Copen_views) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'subscribe'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__subscribe = {
};
export class Cyes__subscribe extends AlanNode {
	constructor(init:Tyes__subscribe, public parent:Copen_views) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'subscribe'?'yes'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cclient_id_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__client_id_path, init:Tno__has_steps__client_id_path}|
		{ name: 'yes', node:Cyes__has_steps__client_id_path, init:Tyes__has_steps__client_id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__client_id_path, parent:Cclient_id_path) => new Cno__has_steps__client_id_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__client_id_path, parent:Cclient_id_path) => new Cyes__has_steps__client_id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_id_path['has steps'], parent:Cclient_id_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__client_id_path {
	export class Dtail extends Cclient_id_path {
		constructor(data:Tyes__has_steps__client_id_path['tail'], parent:Cyes__has_steps__client_id_path) {
			super(data, parent, {
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.location()).result!),
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry__type__yes__has_steps__client_id_path, init:Tcollection_entry__type__yes__has_steps__client_id_path}|
		{ name: 'group', node:Cgroup__type__yes__has_steps__client_id_path, init:Tgroup__type__yes__has_steps__client_id_path}|
		{ name: 'operation', node:Coperation__type__yes, init:Toperation__type__yes}|
		{ name: 'state', node:Cstate__type__yes__has_steps__client_id_path, init:Tstate__type__yes__has_steps__client_id_path}> {
		protected initializer(state: 'collection entry'|'group'|'operation'|'state') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry__type__yes__has_steps__client_id_path, parent:Cyes__has_steps__client_id_path) => new Ccollection_entry__type__yes__has_steps__client_id_path(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_steps__client_id_path, parent:Cyes__has_steps__client_id_path) => new Cgroup__type__yes__has_steps__client_id_path(init, parent);
				case 'operation': return (init:Toperation__type__yes, parent:Cyes__has_steps__client_id_path) => new Coperation__type__yes(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__client_id_path, parent:Cyes__has_steps__client_id_path) => new Cstate__type__yes__has_steps__client_id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__client_id_path['type'], parent:Cyes__has_steps__client_id_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry__type__yes__has_steps__client_id_path {
	export class Dcollection extends Reference<client_state.Ccollection,string> {

		constructor(data:string, $this:Ccollection_entry__type__yes__has_steps__client_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as client_state.Cproperty;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_state.Ccollection;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dentry extends Reference<client_state.Centries__collection,string> {

		constructor(data:string, $this:Ccollection_entry__type__yes__has_steps__client_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.collection?.ref)
				.then(context => {
					const entry = context?.properties.entries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entry'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__client_id_path {
	export class Dgroup extends Reference<client_state.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__client_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as client_state.Cproperty;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as client_state.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Coperation__type__yes {
	export class Doperation extends Reference<client_state.Cyes__started,string> {

		constructor(data:string, $this:Coperation__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'operation') {
							return context.properties.type.state.node as client_state.Coperation__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.started.state.name === 'yes') {
							return context.properties.started.state.node as client_state.Cyes__started;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cstate__type__yes__has_steps__client_id_path {
	export class Dstate extends Reference<client_state.Cstates__state_group__type__property,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__client_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<client_state.Cstate_group__type__property,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__client_id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as client_state.Cproperty;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as client_state.Cstate_group__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cid_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__id_path, init:Tno__has_steps__id_path}|
		{ name: 'yes', node:Cyes__has_steps__id_path, init:Tyes__has_steps__id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__id_path, parent:Cid_path) => new Cno__has_steps__id_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__id_path, parent:Cid_path) => new Cyes__has_steps__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tid_path['has steps'], parent:Cid_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__id_path {
	export class Dtail extends Cid_path {
		constructor(data:Tyes__has_steps__id_path['tail'], parent:Cyes__has_steps__id_path) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'collection entry', node:Ccollection_entry__type__yes__has_steps__id_path, init:Tcollection_entry__type__yes__has_steps__id_path}|
		{ name: 'group', node:Cgroup__type__yes__has_steps__id_path, init:Tgroup__type__yes__has_steps__id_path}|
		{ name: 'state', node:Cstate__type__yes__has_steps__id_path, init:Tstate__type__yes__has_steps__id_path}> {
		protected initializer(state: 'collection entry'|'group'|'state') {
			switch (state) {
				case 'collection entry': return (init:Tcollection_entry__type__yes__has_steps__id_path, parent:Cyes__has_steps__id_path) => new Ccollection_entry__type__yes__has_steps__id_path(init, parent);
				case 'group': return (init:Tgroup__type__yes__has_steps__id_path, parent:Cyes__has_steps__id_path) => new Cgroup__type__yes__has_steps__id_path(init, parent);
				case 'state': return (init:Tstate__type__yes__has_steps__id_path, parent:Cyes__has_steps__id_path) => new Cstate__type__yes__has_steps__id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__id_path['type'], parent:Cyes__has_steps__id_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection_entry__type__yes__has_steps__id_path {
	export class Dcollection extends Reference<application.Ccollection__type__property,string> {

		constructor(data:string, $this:Ccollection_entry__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as application.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cgroup__type__yes__has_steps__id_path {
	export class Dgroup extends Reference<application.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as application.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate__type__yes__has_steps__id_path {
	export class Dstate extends Reference<application.Cstates__state_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.state_group?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstate_group extends Reference<application.Cstate_group,string> {

		constructor(data:string, $this:Cstate__type__yes__has_steps__id_path) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as application.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as application.Cstate_group;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
}
export namespace Cnode {
	export class Dattributes extends AlanDictionary<{ node:Cattributes, init:Tattributes},Cnode> {
		protected initialize(parent:Cnode, key:string, entry_init:Tattributes) { return new Cattributes(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tnode['attributes'], parent:Cnode) {
			super(data, parent);
		}
	}
	export class Dcan_be_deleted extends StateGroup<
		{ name: 'no', node:Cno__can_be_deleted, init:Tno__can_be_deleted}|
		{ name: 'yes', node:Cyes__can_be_deleted, init:Tyes__can_be_deleted}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_be_deleted, parent:Cnode) => new Cno__can_be_deleted(init, parent);
				case 'yes': return (init:Tyes__can_be_deleted, parent:Cnode) => new Cyes__can_be_deleted(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['can be deleted'], parent:Cnode) {
			super(data, parent, 'can be deleted');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can be deleted'`; }
	}
	export class Dis_busy_discarding extends StateGroup<
		{ name: 'no', node:Cno__is_busy_discarding, init:Tno__is_busy_discarding}|
		{ name: 'yes', node:Cyes__is_busy_discarding, init:Tyes__is_busy_discarding}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_busy_discarding, parent:Cnode) => new Cno__is_busy_discarding(init, parent);
				case 'yes': return (init:Tyes__is_busy_discarding, parent:Cnode) => new Cyes__is_busy_discarding(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['is busy discarding'], parent:Cnode) {
			super(data, parent, 'is busy discarding');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is busy discarding'`; }
	}
	export class Dis_busy_saving extends StateGroup<
		{ name: 'no', node:Cno__is_busy_saving, init:Tno__is_busy_saving}|
		{ name: 'yes', node:Cyes__is_busy_saving, init:Tyes__is_busy_saving}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_busy_saving, parent:Cnode) => new Cno__is_busy_saving(init, parent);
				case 'yes': return (init:Tyes__is_busy_saving, parent:Cnode) => new Cyes__is_busy_saving(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['is busy saving'], parent:Cnode) {
			super(data, parent, 'is busy saving');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is busy saving'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__node, init:Tno__is_valid__node}|
		{ name: 'yes', node:Cyes__is_valid__node, init:Tyes__is_valid__node}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__node, parent:Cnode) => new Cno__is_valid__node(init, parent);
				case 'yes': return (init:Tyes__is_valid__node, parent:Cnode) => new Cyes__is_valid__node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['is valid'], parent:Cnode) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dlocation extends StateGroup<
		{ name: 'local', node:Clocal__location__node, init:Tlocal__location__node}|
		{ name: 'remote', node:Cremote__location__node, init:Tremote__location__node}> {
		protected initializer(state: 'local'|'remote') {
			switch (state) {
				case 'local': return (init:Tlocal__location__node, parent:Cnode) => new Clocal__location__node(init, parent);
				case 'remote': return (init:Tremote__location__node, parent:Cnode) => new Cremote__location__node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['location'], parent:Cnode) {
			super(data, parent, 'location');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'location'`; }
	}
	export class Dmodified extends StateGroup<
		{ name: 'no', node:Cno__modified__node, init:Tno__modified__node}|
		{ name: 'yes', node:Cyes__modified__node, init:Tyes__modified__node}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__modified__node, parent:Cnode) => new Cno__modified__node(init, parent);
				case 'yes': return (init:Tyes__modified__node, parent:Cnode) => new Cyes__modified__node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['modified'], parent:Cnode) {
			super(data, parent, 'modified');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modified'`; }
	}
	export class Dqueries extends AlanDictionary<{ node:Cqueries__node, init:Tqueries__node},Cnode> {
		protected initialize(parent:Cnode, key:string, entry_init:Tqueries__node) { return new Cqueries__node(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'queries'`; }
		constructor(data:Tnode['queries'], parent:Cnode) {
			super(data, parent);
		}
	}
	export class Dread_permitted extends StateGroup<
		{ name: 'no', node:Cno__read_permitted, init:Tno__read_permitted}|
		{ name: 'yes', node:Cyes__read_permitted, init:Tyes__read_permitted}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__read_permitted, parent:Cnode) => new Cno__read_permitted(init, parent);
				case 'yes': return (init:Tyes__read_permitted, parent:Cnode) => new Cyes__read_permitted(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['read permitted'], parent:Cnode) {
			super(data, parent, 'read permitted');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'read permitted'`; }
	}
	export class Dreport_types extends AlanDictionary<{ node:Creport_types__node, init:Treport_types__node},Cnode> {
		protected initialize(parent:Cnode, key:string, entry_init:Treport_types__node) { return new Creport_types__node(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'report types'`; }
		constructor(data:Tnode['report types'], parent:Cnode) {
			super(data, parent);
		}
	}
	export class Dtodo extends StateGroup<
		{ name: 'no', node:Cno__todo, init:Tno__todo}|
		{ name: 'yes', node:Cyes__todo, init:Tyes__todo}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__todo, parent:Cnode) => new Cno__todo(init, parent);
				case 'yes': return (init:Tyes__todo, parent:Cnode) => new Cyes__todo(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnode['todo'], parent:Cnode) {
			super(data, parent, 'todo');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'todo'`; }
	}
}
export namespace Cattributes {
	export class Dtype extends StateGroup<
		{ name: 'operation', node:Coperation__type__attributes, init:Toperation__type__attributes}|
		{ name: 'property', node:Cproperty, init:Tproperty}> {
		protected initializer(state: 'operation'|'property') {
			switch (state) {
				case 'operation': return (init:Toperation__type__attributes, parent:Cattributes) => new Coperation__type__attributes(init, parent);
				case 'property': return (init:Tproperty, parent:Cattributes) => new Cproperty(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['type'], parent:Cattributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Coperation__type__attributes {
	export class Dstarted extends StateGroup<
		{ name: 'no', node:Cno__started, init:Tno__started}|
		{ name: 'yes', node:Cyes__started, init:Tyes__started}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__started, parent:Coperation__type__attributes) => new Cno__started(init, parent);
				case 'yes': return (init:Tyes__started, parent:Coperation__type__attributes) => new Cyes__started(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Toperation__type__attributes['started'], parent:Coperation__type__attributes) {
			super(data, parent, 'started');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'started'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'action', node:Caction, init:Taction}|
		{ name: 'command', node:Ccommand, init:Tcommand}> {
		protected initializer(state: 'action'|'command') {
			switch (state) {
				case 'action': return (init:Taction, parent:Coperation__type__attributes) => new Caction(init, parent);
				case 'command': return (init:Tcommand, parent:Coperation__type__attributes) => new Ccommand(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Toperation__type__attributes['type'], parent:Coperation__type__attributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__started {
	export class Dexecutable extends StateGroup<
		{ name: 'no', node:Cno__executable, init:Tno__executable}|
		{ name: 'yes', node:Cyes__executable, init:Tyes__executable}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__executable, parent:Cyes__started) => new Cno__executable(init, parent);
				case 'yes': return (init:Tyes__executable, parent:Cyes__started) => new Cyes__executable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__started['executable'], parent:Cyes__started) {
			super(data, parent, 'executable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'executable'`; }
	}
	export class Dexecuting extends StateGroup<
		{ name: 'no', node:Cno__executing, init:Tno__executing}|
		{ name: 'yes', node:Cyes__executing, init:Tyes__executing}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__executing, parent:Cyes__started) => new Cno__executing(init, parent);
				case 'yes': return (init:Tyes__executing, parent:Cyes__started) => new Cyes__executing(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__started['executing'], parent:Cyes__started) {
			super(data, parent, 'executing');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'executing'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__yes, init:Tno__is_valid__yes}|
		{ name: 'yes', node:Cyes__is_valid__yes, init:Tyes__is_valid__yes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__yes, parent:Cyes__started) => new Cno__is_valid__yes(init, parent);
				case 'yes': return (init:Tyes__is_valid__yes, parent:Cyes__started) => new Cyes__is_valid__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__started['is valid'], parent:Cyes__started) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dnode extends Cnode {
		constructor(data:Tyes__started['node'], parent:Cyes__started) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => client_state.Clocation.Plocal).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.type?.state.node.output.parameter_definition()).result!),
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.root_position)
					.then(context => context?.definitions.node_position).result!)
			}, 'node')
		}
	}
}
export namespace Cno__executing {
	export class Dlast_result extends StateGroup<
		{ name: 'failure', node:Cfailure__last_result, init:Tfailure__last_result}|
		{ name: 'not invoked', node:Cnot_invoked, init:Tnot_invoked}|
		{ name: 'success', node:Csuccess__last_result, init:Tsuccess__last_result}> {
		protected initializer(state: 'failure'|'not invoked'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__last_result, parent:Cno__executing) => new Cfailure__last_result(init, parent);
				case 'not invoked': return (init:Tnot_invoked, parent:Cno__executing) => new Cnot_invoked(init, parent);
				case 'success': return (init:Tsuccess__last_result, parent:Cno__executing) => new Csuccess__last_result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__executing['last result'], parent:Cno__executing) {
			super(data, parent, 'last result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'last result'`; }
	}
}
export namespace Cyes__executing {
	export class Dcan_continue extends StateGroup<
		{ name: 'no', node:Cno__can_continue, init:Tno__can_continue}|
		{ name: 'yes', node:Cyes__can_continue, init:Tyes__can_continue}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_continue, parent:Cyes__executing) => new Cno__can_continue(init, parent);
				case 'yes': return (init:Tyes__can_continue, parent:Cyes__executing) => new Cyes__can_continue(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__executing['can continue'], parent:Cyes__executing) {
			super(data, parent, 'can continue');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can continue'`; }
	}
}
export namespace Ccommand {
	export class Dexecute_permitted extends StateGroup<
		{ name: 'no', node:Cno__execute_permitted, init:Tno__execute_permitted}|
		{ name: 'yes', node:Cyes__execute_permitted, init:Tyes__execute_permitted}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__execute_permitted, parent:Ccommand) => new Cno__execute_permitted(init, parent);
				case 'yes': return (init:Tyes__execute_permitted, parent:Ccommand) => new Cyes__execute_permitted(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand['execute permitted'], parent:Ccommand) {
			super(data, parent, 'execute permitted');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'execute permitted'`; }
	}
}
export namespace Cproperty {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection, init:Tcollection}|
		{ name: 'file', node:Cfile__type__property, init:Tfile__type__property}|
		{ name: 'group', node:Cgroup__type__property, init:Tgroup__type__property}|
		{ name: 'number', node:Cnumber__type__property, init:Tnumber__type__property}|
		{ name: 'state group', node:Cstate_group__type__property, init:Tstate_group__type__property}|
		{ name: 'text', node:Ctext__type__property, init:Ttext__type__property}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection, parent:Cproperty) => new Ccollection(init, parent);
				case 'file': return (init:Tfile__type__property, parent:Cproperty) => new Cfile__type__property(init, parent);
				case 'group': return (init:Tgroup__type__property, parent:Cproperty) => new Cgroup__type__property(init, parent);
				case 'number': return (init:Tnumber__type__property, parent:Cproperty) => new Cnumber__type__property(init, parent);
				case 'state group': return (init:Tstate_group__type__property, parent:Cproperty) => new Cstate_group__type__property(init, parent);
				case 'text': return (init:Ttext__type__property, parent:Cproperty) => new Ctext__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty['type'], parent:Cproperty) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection {
	export class Damount_of_entries extends AlanInteger {
		constructor(data:Tcollection['amount of entries'], parent:Ccollection) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'amount of entries'`; }
	}
	export class Dcan_create_entry extends StateGroup<
		{ name: 'no', node:Cno__can_create_entry__collection, init:Tno__can_create_entry__collection}|
		{ name: 'yes', node:Cyes__can_create_entry__collection, init:Tyes__can_create_entry__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_create_entry__collection, parent:Ccollection) => new Cno__can_create_entry__collection(init, parent);
				case 'yes': return (init:Tyes__can_create_entry__collection, parent:Ccollection) => new Cyes__can_create_entry__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['can create entry'], parent:Ccollection) {
			super(data, parent, 'can create entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can create entry'`; }
	}
	export class Dcan_download_files extends StateGroup<
		{ name: 'no', node:Cno__can_download_files__collection, init:Tno__can_download_files__collection}|
		{ name: 'yes', node:Cyes__can_download_files__collection, init:Tyes__can_download_files__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_download_files__collection, parent:Ccollection) => new Cno__can_download_files__collection(init, parent);
				case 'yes': return (init:Tyes__can_download_files__collection, parent:Ccollection) => new Cyes__can_download_files__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['can download files'], parent:Ccollection) {
			super(data, parent, 'can download files');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can download files'`; }
	}
	export class Dentries extends AlanDictionary<{ node:Centries__collection, init:Tentries__collection},Ccollection> {
		protected initialize(parent:Ccollection, key:string, entry_init:Tentries__collection) { return new Centries__collection(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tcollection['entries'], parent:Ccollection) {
			super(data, parent);
		}
	}
	export class Dhas_ordered_graphs extends StateGroup<
		{ name: 'no', node:Cno__has_ordered_graphs, init:Tno__has_ordered_graphs}|
		{ name: 'yes', node:Cyes__has_ordered_graphs, init:Tyes__has_ordered_graphs}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_ordered_graphs, parent:Ccollection) => new Cno__has_ordered_graphs(init, parent);
				case 'yes': return (init:Tyes__has_ordered_graphs, parent:Ccollection) => new Cyes__has_ordered_graphs(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['has ordered graphs'], parent:Ccollection) {
			super(data, parent, 'has ordered graphs');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has ordered graphs'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__collection, init:Tno__is_editable__collection}|
		{ name: 'yes', node:Cyes__is_editable__collection, init:Tyes__is_editable__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__collection, parent:Ccollection) => new Cno__is_editable__collection(init, parent);
				case 'yes': return (init:Tyes__is_editable__collection, parent:Ccollection) => new Cyes__is_editable__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['is editable'], parent:Ccollection) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dis_empty extends StateGroup<
		{ name: 'no', node:Cno__is_empty__collection, init:Tno__is_empty__collection}|
		{ name: 'yes', node:Cyes__is_empty__collection, init:Tyes__is_empty__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_empty__collection, parent:Ccollection) => new Cno__is_empty__collection(init, parent);
				case 'yes': return (init:Tyes__is_empty__collection, parent:Ccollection) => new Cyes__is_empty__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['is empty'], parent:Ccollection) {
			super(data, parent, 'is empty');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is empty'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__collection, init:Tno__is_valid__collection}|
		{ name: 'yes', node:Cyes__is_valid__collection, init:Tyes__is_valid__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__collection, parent:Ccollection) => new Cno__is_valid__collection(init, parent);
				case 'yes': return (init:Tyes__is_valid__collection, parent:Ccollection) => new Cyes__is_valid__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['is valid'], parent:Ccollection) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Djoined extends StateGroup<
		{ name: 'no', node:Cno__joined, init:Tno__joined}|
		{ name: 'yes', node:Cyes__joined, init:Tyes__joined}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__joined, parent:Ccollection) => new Cno__joined(init, parent);
				case 'yes': return (init:Tyes__joined, parent:Ccollection) => new Cyes__joined(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['joined'], parent:Ccollection) {
			super(data, parent, 'joined');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'joined'`; }
	}
	export class Dlast_created_entry extends StateGroup<
		{ name: 'entry', node:Centry__last_created_entry, init:Tentry__last_created_entry}|
		{ name: 'none', node:Cnone__last_created_entry, init:Tnone__last_created_entry}> {
		protected initializer(state: 'entry'|'none') {
			switch (state) {
				case 'entry': return (init:Tentry__last_created_entry, parent:Ccollection) => new Centry__last_created_entry(init, parent);
				case 'none': return (init:Tnone__last_created_entry, parent:Ccollection) => new Cnone__last_created_entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['last created entry'], parent:Ccollection) {
			super(data, parent, 'last created entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'last created entry'`; }
	}
	export class Dmodified extends StateGroup<
		{ name: 'no', node:Cno__modified__collection, init:Tno__modified__collection}|
		{ name: 'yes', node:Cyes__modified__collection, init:Tyes__modified__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__modified__collection, parent:Ccollection) => new Cno__modified__collection(init, parent);
				case 'yes': return (init:Tyes__modified__collection, parent:Ccollection) => new Cyes__modified__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection['modified'], parent:Ccollection) {
			super(data, parent, 'modified');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modified'`; }
	}
}
export namespace Centries__collection {
	export class Dkey extends Reference<client_state.Ctext__type__property,string> {
		public readonly inferences:{
			key: () => client_state.Cyes__is_key
		}

		constructor(data:string, $this:Centries__collection) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.node)
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as client_state.Cproperty;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as client_state.Ctext__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				key: cache((detach:boolean) => {
					const client_state__node__attributes__type__property__type__collection__entries__key_nval = $this.properties.key.ref;
					return resolve($this.properties.key.ref)
						.then(context => client_state__node__attributes__type__property__type__collection__entries__key_nval)
						.then(context => {
							if (context?.properties.is_key.state.name === 'yes') {
								return resolve(context.properties.is_key.state.node as client_state.Cyes__is_key).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'key'`; }
	}
	export class Dlocation extends StateGroup<
		{ name: 'local', node:Clocal__location__entries, init:Tlocal__location__entries}|
		{ name: 'remote', node:Cremote__location__entries, init:Tremote__location__entries}> {
		protected initializer(state: 'local'|'remote') {
			switch (state) {
				case 'local': return (init:Tlocal__location__entries, parent:Centries__collection) => new Clocal__location__entries(init, parent);
				case 'remote': return (init:Tremote__location__entries, parent:Centries__collection) => new Cremote__location__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__collection['location'], parent:Centries__collection) {
			super(data, parent, 'location');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'location'`; }
	}
	export class Dnode extends Cnode {
		constructor(data:Tentries__collection['node'], parent:Centries__collection) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entity).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.location?.state.node.output.entry_node_location()).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.collection_type())
					.then(context => context?.properties.node).result!),
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entity)
					.then(context => context?.definitions.node_position).result!)
			}, 'node')
		}
	}
	export class Dstyle extends Cstyle {
		constructor(data:Tentries__collection['style'], parent:Centries__collection) {
			super(data, parent, 'style')
		}
	}
}
export namespace Clocal__location__entries {
	export class Dmodified_by extends StateGroup<
		{ name: 'engine', node:Cengine__modified_by, init:Tengine__modified_by}|
		{ name: 'user', node:Cuser__modified_by, init:Tuser__modified_by}> {
		protected initializer(state: 'engine'|'user') {
			switch (state) {
				case 'engine': return (init:Tengine__modified_by, parent:Clocal__location__entries) => new Cengine__modified_by(init, parent);
				case 'user': return (init:Tuser__modified_by, parent:Clocal__location__entries) => new Cuser__modified_by(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlocal__location__entries['modified by'], parent:Clocal__location__entries) {
			super(data, parent, 'modified by');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modified by'`; }
	}
}
export namespace Cremote__location__entries {
	export class Dmarked_for_deletion extends StateGroup<
		{ name: 'no', node:Cno__marked_for_deletion, init:Tno__marked_for_deletion}|
		{ name: 'yes', node:Cyes__marked_for_deletion, init:Tyes__marked_for_deletion}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__marked_for_deletion, parent:Cremote__location__entries) => new Cno__marked_for_deletion(init, parent);
				case 'yes': return (init:Tyes__marked_for_deletion, parent:Cremote__location__entries) => new Cyes__marked_for_deletion(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tremote__location__entries['marked for deletion'], parent:Cremote__location__entries) {
			super(data, parent, 'marked for deletion');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'marked for deletion'`; }
	}
}
export namespace Cyes__has_ordered_graphs {
	export class Dordered_graphs extends AlanDictionary<{ node:Cordered_graphs, init:Tordered_graphs},Cyes__has_ordered_graphs> {
		protected initialize(parent:Cyes__has_ordered_graphs, key:string, entry_init:Tordered_graphs) { return new Cordered_graphs(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'`; }
		constructor(data:Tyes__has_ordered_graphs['ordered graphs'], parent:Cyes__has_ordered_graphs) {
			super(data, parent);
		}
	}
}
export namespace Cordered_graphs {
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__ordered_graphs, init:Tno__is_valid__ordered_graphs}|
		{ name: 'yes', node:Cyes__is_valid__ordered_graphs, init:Tyes__is_valid__ordered_graphs}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__ordered_graphs, parent:Cordered_graphs) => new Cno__is_valid__ordered_graphs(init, parent);
				case 'yes': return (init:Tyes__is_valid__ordered_graphs, parent:Cordered_graphs) => new Cyes__is_valid__ordered_graphs(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tordered_graphs['is valid'], parent:Cordered_graphs) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
}
export namespace Cyes__is_editable__collection {
	export class Dedit_mode extends StateGroup<
		{ name: 'off', node:Coff__edit_mode__yes__is_editable__collection, init:Toff__edit_mode__yes__is_editable__collection}|
		{ name: 'on', node:Con__edit_mode__yes__is_editable__collection, init:Ton__edit_mode__yes__is_editable__collection}> {
		protected initializer(state: 'off'|'on') {
			switch (state) {
				case 'off': return (init:Toff__edit_mode__yes__is_editable__collection, parent:Cyes__is_editable__collection) => new Coff__edit_mode__yes__is_editable__collection(init, parent);
				case 'on': return (init:Ton__edit_mode__yes__is_editable__collection, parent:Cyes__is_editable__collection) => new Con__edit_mode__yes__is_editable__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__collection['edit mode'], parent:Cyes__is_editable__collection) {
			super(data, parent, 'edit mode');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edit mode'`; }
	}
}
export namespace Cno__is_empty__collection {
	export class Dsize extends StateGroup<
		{ name: 'multiple', node:Cmultiple__size__no__is_empty__collection, init:Tmultiple__size__no__is_empty__collection}|
		{ name: 'single', node:Csingle__size__no__is_empty__collection, init:Tsingle__size__no__is_empty__collection}> {
		protected initializer(state: 'multiple'|'single') {
			switch (state) {
				case 'multiple': return (init:Tmultiple__size__no__is_empty__collection, parent:Cno__is_empty__collection) => new Cmultiple__size__no__is_empty__collection(init, parent);
				case 'single': return (init:Tsingle__size__no__is_empty__collection, parent:Cno__is_empty__collection) => new Csingle__size__no__is_empty__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_empty__collection['size'], parent:Cno__is_empty__collection) {
			super(data, parent, 'size');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'size'`; }
	}
}
export namespace Cno__is_valid__collection {
	export class Dcardinality_constraint_violation extends StateGroup<
		{ name: 'no', node:Cno__cardinality_constraint_violation, init:Tno__cardinality_constraint_violation}|
		{ name: 'yes', node:Cyes__cardinality_constraint_violation, init:Tyes__cardinality_constraint_violation}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__cardinality_constraint_violation, parent:Cno__is_valid__collection) => new Cno__cardinality_constraint_violation(init, parent);
				case 'yes': return (init:Tyes__cardinality_constraint_violation, parent:Cno__is_valid__collection) => new Cyes__cardinality_constraint_violation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__collection['cardinality constraint violation'], parent:Cno__is_valid__collection) {
			super(data, parent, 'cardinality constraint violation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cardinality constraint violation'`; }
	}
	export class Dentries_are_invalid extends StateGroup<
		{ name: 'no', node:Cno__entries_are_invalid, init:Tno__entries_are_invalid}|
		{ name: 'yes', node:Cyes__entries_are_invalid, init:Tyes__entries_are_invalid}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__entries_are_invalid, parent:Cno__is_valid__collection) => new Cno__entries_are_invalid(init, parent);
				case 'yes': return (init:Tyes__entries_are_invalid, parent:Cno__is_valid__collection) => new Cyes__entries_are_invalid(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__collection['entries are invalid'], parent:Cno__is_valid__collection) {
			super(data, parent, 'entries are invalid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries are invalid'`; }
	}
}
export namespace Cyes__joined {
	export class Damount_of_entries extends AlanInteger {
		constructor(data:Tyes__joined['amount of entries'], parent:Cyes__joined) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'amount of entries'`; }
	}
	export class Dentries extends AlanDictionary<{ node:Centries__yes__joined, init:Tentries__yes__joined},Cyes__joined> {
		protected initialize(parent:Cyes__joined, key:string, entry_init:Tentries__yes__joined) { return new Centries__yes__joined(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tyes__joined['entries'], parent:Cyes__joined) {
			super(data, parent);
		}
	}
	export class Dis_empty extends StateGroup<
		{ name: 'no', node:Cno__is_empty__yes__joined, init:Tno__is_empty__yes__joined}|
		{ name: 'yes', node:Cyes__is_empty__yes__joined, init:Tyes__is_empty__yes__joined}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_empty__yes__joined, parent:Cyes__joined) => new Cno__is_empty__yes__joined(init, parent);
				case 'yes': return (init:Tyes__is_empty__yes__joined, parent:Cyes__joined) => new Cyes__is_empty__yes__joined(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__joined['is empty'], parent:Cyes__joined) {
			super(data, parent, 'is empty');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is empty'`; }
	}
	export class Djoined_collection extends StateGroup<
		{ name: 'resolved', node:Cresolved__joined_collection, init:Tresolved__joined_collection}|
		{ name: 'unresolved', node:Cunresolved__joined_collection, init:Tunresolved__joined_collection}> {
		protected initializer(state: 'resolved'|'unresolved') {
			switch (state) {
				case 'resolved': return (init:Tresolved__joined_collection, parent:Cyes__joined) => new Cresolved__joined_collection(init, parent);
				case 'unresolved': return (init:Tunresolved__joined_collection, parent:Cyes__joined) => new Cunresolved__joined_collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__joined['joined collection'], parent:Cyes__joined) {
			super(data, parent, 'joined collection');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'joined collection'`; }
	}
}
export namespace Centries__yes__joined {
	export class Dcollection_entry extends StateGroup<
		{ name: 'entry', node:Centry__collection_entry, init:Tentry__collection_entry}|
		{ name: 'none', node:Cnone__collection_entry, init:Tnone__collection_entry}> {
		protected initializer(state: 'entry'|'none') {
			switch (state) {
				case 'entry': return (init:Tentry__collection_entry, parent:Centries__yes__joined) => new Centry__collection_entry(init, parent);
				case 'none': return (init:Tnone__collection_entry, parent:Centries__yes__joined) => new Cnone__collection_entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__joined['collection entry'], parent:Centries__yes__joined) {
			super(data, parent, 'collection entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection entry'`; }
	}
	export class Dreferenced_entry extends StateGroup<
		{ name: 'entry', node:Centry__referenced_entry, init:Tentry__referenced_entry}|
		{ name: 'none', node:Cnone__referenced_entry, init:Tnone__referenced_entry}> {
		protected initializer(state: 'entry'|'none') {
			switch (state) {
				case 'entry': return (init:Tentry__referenced_entry, parent:Centries__yes__joined) => new Centry__referenced_entry(init, parent);
				case 'none': return (init:Tnone__referenced_entry, parent:Centries__yes__joined) => new Cnone__referenced_entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__joined['referenced entry'], parent:Centries__yes__joined) {
			super(data, parent, 'referenced entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'referenced entry'`; }
	}
}
export namespace Centry__collection_entry {
	export class Dentry extends Reference<client_state.Centries__collection,string> {

		constructor(data:string, $this:Centry__collection_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.entries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entry'`; }
	}
}
export namespace Centry__referenced_entry {
	export class Dcan_create_entry extends StateGroup<
		{ name: 'no', node:Cno__can_create_entry__entry, init:Tno__can_create_entry__entry}|
		{ name: 'yes', node:Cyes__can_create_entry__entry, init:Tyes__can_create_entry__entry}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_create_entry__entry, parent:Centry__referenced_entry) => new Cno__can_create_entry__entry(init, parent);
				case 'yes': return (init:Tyes__can_create_entry__entry, parent:Centry__referenced_entry) => new Cyes__can_create_entry__entry(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry__referenced_entry['can create entry'], parent:Centry__referenced_entry) {
			super(data, parent, 'can create entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can create entry'`; }
	}
	export class Dentry extends Reference<client_state.Centries__collection,string> {

		constructor(data:string, $this:Centry__referenced_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.joined_collection())
				.then(context => context?.properties.collection?.ref)
				.then(context => {
					const entry = context?.properties.entries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entry'`; }
	}
}
export namespace Cno__is_empty__yes__joined {
	export class Dsize extends StateGroup<
		{ name: 'multiple', node:Cmultiple__size__no__is_empty__yes, init:Tmultiple__size__no__is_empty__yes}|
		{ name: 'single', node:Csingle__size__no__is_empty__yes, init:Tsingle__size__no__is_empty__yes}> {
		protected initializer(state: 'multiple'|'single') {
			switch (state) {
				case 'multiple': return (init:Tmultiple__size__no__is_empty__yes, parent:Cno__is_empty__yes__joined) => new Cmultiple__size__no__is_empty__yes(init, parent);
				case 'single': return (init:Tsingle__size__no__is_empty__yes, parent:Cno__is_empty__yes__joined) => new Csingle__size__no__is_empty__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_empty__yes__joined['size'], parent:Cno__is_empty__yes__joined) {
			super(data, parent, 'size');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'size'`; }
	}
}
export namespace Cresolved__joined_collection {
	export class Dcollection extends Reference<client_state.Ccollection,string> {

		constructor(data:string, $this:Cresolved__joined_collection) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.referenced_node)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as client_state.Cproperty;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_state.Ccollection;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
	export class Dreferenced_node extends Cclient_id_path {
		constructor(data:Tresolved__joined_collection['referenced node'], parent:Cresolved__joined_collection) {
			super(data, parent, {
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.id_path_location).result!),
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!)
			}, 'referenced node')
		}
	}
}
export namespace Centry__last_created_entry {
	export class Dentry extends Reference<client_state.Centries__collection,string> {

		constructor(data:string, $this:Centry__last_created_entry) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.entries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entry'`; }
	}
}
export namespace Cyes__modified__collection {
	export class Dby extends StateGroup<
		{ name: 'engine', node:Cengine__by__yes__modified, init:Tengine__by__yes__modified}|
		{ name: 'user', node:Cuser__by__yes__modified, init:Tuser__by__yes__modified}> {
		protected initializer(state: 'engine'|'user') {
			switch (state) {
				case 'engine': return (init:Tengine__by__yes__modified, parent:Cyes__modified__collection) => new Cengine__by__yes__modified(init, parent);
				case 'user': return (init:Tuser__by__yes__modified, parent:Cyes__modified__collection) => new Cuser__by__yes__modified(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__modified__collection['by'], parent:Cyes__modified__collection) {
			super(data, parent, 'by');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'by'`; }
	}
}
export namespace Cfile__type__property {
	export class Dbackend_known extends StateGroup<
		{ name: 'no', node:Cno__backend_known__file, init:Tno__backend_known__file}|
		{ name: 'yes', node:Cyes__backend_known__file, init:Tyes__backend_known__file}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__backend_known__file, parent:Cfile__type__property) => new Cno__backend_known__file(init, parent);
				case 'yes': return (init:Tyes__backend_known__file, parent:Cfile__type__property) => new Cyes__backend_known__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__property['backend known'], parent:Cfile__type__property) {
			super(data, parent, 'backend known');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'backend known'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__file, init:Tno__is_editable__file}|
		{ name: 'yes', node:Cyes__is_editable__file, init:Tyes__is_editable__file}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__file, parent:Cfile__type__property) => new Cno__is_editable__file(init, parent);
				case 'yes': return (init:Tyes__is_editable__file, parent:Cfile__type__property) => new Cyes__is_editable__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__property['is editable'], parent:Cfile__type__property) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__file__type__property, init:Tno__is_set__file__type__property}|
		{ name: 'yes', node:Cyes__is_set__file__type__property, init:Tyes__is_set__file__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__file__type__property, parent:Cfile__type__property) => new Cno__is_set__file__type__property(init, parent);
				case 'yes': return (init:Tyes__is_set__file__type__property, parent:Cfile__type__property) => new Cyes__is_set__file__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__property['is set'], parent:Cfile__type__property) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__file, init:Tno__is_valid__file}|
		{ name: 'yes', node:Cyes__is_valid__file, init:Tyes__is_valid__file}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__file, parent:Cfile__type__property) => new Cno__is_valid__file(init, parent);
				case 'yes': return (init:Tyes__is_valid__file, parent:Cfile__type__property) => new Cyes__is_valid__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__property['is valid'], parent:Cfile__type__property) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
}
export namespace Cyes__backend_known__file {
	export class Dis_readable extends StateGroup<
		{ name: 'no', node:Cno__is_readable__yes__backend_known__file, init:Tno__is_readable__yes__backend_known__file}|
		{ name: 'yes', node:Cyes__is_readable__yes__backend_known__file, init:Tyes__is_readable__yes__backend_known__file}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_readable__yes__backend_known__file, parent:Cyes__backend_known__file) => new Cno__is_readable__yes__backend_known__file(init, parent);
				case 'yes': return (init:Tyes__is_readable__yes__backend_known__file, parent:Cyes__backend_known__file) => new Cyes__is_readable__yes__backend_known__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__backend_known__file['is readable'], parent:Cyes__backend_known__file) {
			super(data, parent, 'is readable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is readable'`; }
	}
}
export namespace Cyes__is_readable__yes__backend_known__file {
}
export namespace Cyes__is_editable__file {
	export class Dedit_mode extends StateGroup<
		{ name: 'off', node:Coff__edit_mode__yes__is_editable__file, init:Toff__edit_mode__yes__is_editable__file}|
		{ name: 'on', node:Con__edit_mode__yes__is_editable__file, init:Ton__edit_mode__yes__is_editable__file}> {
		protected initializer(state: 'off'|'on') {
			switch (state) {
				case 'off': return (init:Toff__edit_mode__yes__is_editable__file, parent:Cyes__is_editable__file) => new Coff__edit_mode__yes__is_editable__file(init, parent);
				case 'on': return (init:Ton__edit_mode__yes__is_editable__file, parent:Cyes__is_editable__file) => new Con__edit_mode__yes__is_editable__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__file['edit mode'], parent:Cyes__is_editable__file) {
			super(data, parent, 'edit mode');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edit mode'`; }
	}
	export class Dmodified extends StateGroup<
		{ name: 'no', node:Cno__modified__yes__is_editable__file, init:Tno__modified__yes__is_editable__file}|
		{ name: 'yes', node:Cyes__modified__yes__is_editable__file, init:Tyes__modified__yes__is_editable__file}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__modified__yes__is_editable__file, parent:Cyes__is_editable__file) => new Cno__modified__yes__is_editable__file(init, parent);
				case 'yes': return (init:Tyes__modified__yes__is_editable__file, parent:Cyes__is_editable__file) => new Cyes__modified__yes__is_editable__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__file['modified'], parent:Cyes__is_editable__file) {
			super(data, parent, 'modified');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modified'`; }
	}
}
export namespace Cyes__modified__yes__is_editable__file {
	export class Dupload_status extends StateGroup<
		{ name: 'done', node:Cdone, init:Tdone}|
		{ name: 'not uploaded', node:Cnot_uploaded, init:Tnot_uploaded}|
		{ name: 'uploading', node:Cuploading, init:Tuploading}> {
		protected initializer(state: 'done'|'not uploaded'|'uploading') {
			switch (state) {
				case 'done': return (init:Tdone, parent:Cyes__modified__yes__is_editable__file) => new Cdone(init, parent);
				case 'not uploaded': return (init:Tnot_uploaded, parent:Cyes__modified__yes__is_editable__file) => new Cnot_uploaded(init, parent);
				case 'uploading': return (init:Tuploading, parent:Cyes__modified__yes__is_editable__file) => new Cuploading(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__modified__yes__is_editable__file['upload status'], parent:Cyes__modified__yes__is_editable__file) {
			super(data, parent, 'upload status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'upload status'`; }
	}
}
export namespace Cdone {
}
export namespace Cyes__is_set__file__type__property {
}
export namespace Cno__is_valid__file {
	export class Dextension_not_valid extends StateGroup<
		{ name: 'no', node:Cno__extension_not_valid, init:Tno__extension_not_valid}|
		{ name: 'yes', node:Cyes__extension_not_valid, init:Tyes__extension_not_valid}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__extension_not_valid, parent:Cno__is_valid__file) => new Cno__extension_not_valid(init, parent);
				case 'yes': return (init:Tyes__extension_not_valid, parent:Cno__is_valid__file) => new Cyes__extension_not_valid(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__file['extension not valid'], parent:Cno__is_valid__file) {
			super(data, parent, 'extension not valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'extension not valid'`; }
	}
	export class Dnot_set extends StateGroup<
		{ name: 'no', node:Cno__not_set__no__is_valid__file, init:Tno__not_set__no__is_valid__file}|
		{ name: 'yes', node:Cyes__not_set__no__is_valid__file, init:Tyes__not_set__no__is_valid__file}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__not_set__no__is_valid__file, parent:Cno__is_valid__file) => new Cno__not_set__no__is_valid__file(init, parent);
				case 'yes': return (init:Tyes__not_set__no__is_valid__file, parent:Cno__is_valid__file) => new Cyes__not_set__no__is_valid__file(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__file['not set'], parent:Cno__is_valid__file) {
			super(data, parent, 'not set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'not set'`; }
	}
}
export namespace Cyes__extension_not_valid {
}
export namespace Cgroup__type__property {
	export class Dnode extends Cnode {
		constructor(data:Tgroup__type__property['node'], parent:Cgroup__type__property) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.location()).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group_type())
					.then(context => context?.properties.node).result!),
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_position).result!)
			}, 'node')
		}
	}
}
export namespace Cnumber__type__property {
	export class Dbackend_known extends StateGroup<
		{ name: 'no', node:Cno__backend_known__number, init:Tno__backend_known__number}|
		{ name: 'yes', node:Cyes__backend_known__number, init:Tyes__backend_known__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__backend_known__number, parent:Cnumber__type__property) => new Cno__backend_known__number(init, parent);
				case 'yes': return (init:Tyes__backend_known__number, parent:Cnumber__type__property) => new Cyes__backend_known__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['backend known'], parent:Cnumber__type__property) {
			super(data, parent, 'backend known');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'backend known'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__number, init:Tno__is_editable__number}|
		{ name: 'yes', node:Cyes__is_editable__number, init:Tyes__is_editable__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__number, parent:Cnumber__type__property) => new Cno__is_editable__number(init, parent);
				case 'yes': return (init:Tyes__is_editable__number, parent:Cnumber__type__property) => new Cyes__is_editable__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['is editable'], parent:Cnumber__type__property) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__number__type__property, init:Tno__is_set__number__type__property}|
		{ name: 'yes', node:Cyes__is_set__number__type__property, init:Tyes__is_set__number__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__number__type__property, parent:Cnumber__type__property) => new Cno__is_set__number__type__property(init, parent);
				case 'yes': return (init:Tyes__is_set__number__type__property, parent:Cnumber__type__property) => new Cyes__is_set__number__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['is set'], parent:Cnumber__type__property) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__number__type__property, init:Tno__is_valid__number__type__property}|
		{ name: 'yes', node:Cyes__is_valid__number__type__property, init:Tyes__is_valid__number__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__number__type__property, parent:Cnumber__type__property) => new Cno__is_valid__number__type__property(init, parent);
				case 'yes': return (init:Tyes__is_valid__number__type__property, parent:Cnumber__type__property) => new Cyes__is_valid__number__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['is valid'], parent:Cnumber__type__property) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dnumber_type extends StateGroup<
		{ name: 'integer', node:Cinteger, init:Tinteger}|
		{ name: 'positive', node:Cpositive, init:Tpositive}> {
		protected initializer(state: 'integer'|'positive') {
			switch (state) {
				case 'integer': return (init:Tinteger, parent:Cnumber__type__property) => new Cinteger(init, parent);
				case 'positive': return (init:Tpositive, parent:Cnumber__type__property) => new Cpositive(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property['number type'], parent:Cnumber__type__property) {
			super(data, parent, 'number type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number type'`; }
	}
	export class Dnumerical_type_properties extends Cnumerical_type_properties {
		constructor(data:Tnumber__type__property['numerical type properties'], parent:Cnumber__type__property) {
			super(data, parent, 'numerical type properties')
		}
	}
}
export namespace Cyes__backend_known__number {
	export class Dis_readable extends StateGroup<
		{ name: 'no', node:Cno__is_readable__yes__backend_known__number, init:Tno__is_readable__yes__backend_known__number}|
		{ name: 'yes', node:Cyes__is_readable__yes__backend_known__number, init:Tyes__is_readable__yes__backend_known__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_readable__yes__backend_known__number, parent:Cyes__backend_known__number) => new Cno__is_readable__yes__backend_known__number(init, parent);
				case 'yes': return (init:Tyes__is_readable__yes__backend_known__number, parent:Cyes__backend_known__number) => new Cyes__is_readable__yes__backend_known__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__backend_known__number['is readable'], parent:Cyes__backend_known__number) {
			super(data, parent, 'is readable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is readable'`; }
	}
}
export namespace Cyes__is_readable__yes__backend_known__number {
	export class Dnumber extends AlanInteger {
		constructor(data:Tyes__is_readable__yes__backend_known__number['number'], parent:Cyes__is_readable__yes__backend_known__number) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cyes__is_editable__number {
	export class Dedit_mode extends StateGroup<
		{ name: 'off', node:Coff__edit_mode__yes__is_editable__number, init:Toff__edit_mode__yes__is_editable__number}|
		{ name: 'on', node:Con__edit_mode__yes__is_editable__number, init:Ton__edit_mode__yes__is_editable__number}> {
		protected initializer(state: 'off'|'on') {
			switch (state) {
				case 'off': return (init:Toff__edit_mode__yes__is_editable__number, parent:Cyes__is_editable__number) => new Coff__edit_mode__yes__is_editable__number(init, parent);
				case 'on': return (init:Ton__edit_mode__yes__is_editable__number, parent:Cyes__is_editable__number) => new Con__edit_mode__yes__is_editable__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__number['edit mode'], parent:Cyes__is_editable__number) {
			super(data, parent, 'edit mode');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edit mode'`; }
	}
	export class Dmodified extends StateGroup<
		{ name: 'no', node:Cno__modified__yes__is_editable__number, init:Tno__modified__yes__is_editable__number}|
		{ name: 'yes', node:Cyes__modified__yes__is_editable__number, init:Tyes__modified__yes__is_editable__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__modified__yes__is_editable__number, parent:Cyes__is_editable__number) => new Cno__modified__yes__is_editable__number(init, parent);
				case 'yes': return (init:Tyes__modified__yes__is_editable__number, parent:Cyes__is_editable__number) => new Cyes__modified__yes__is_editable__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__number['modified'], parent:Cyes__is_editable__number) {
			super(data, parent, 'modified');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modified'`; }
	}
}
export namespace Cyes__modified__yes__is_editable__number {
	export class Dnumber extends AlanInteger {
		constructor(data:Tyes__modified__yes__is_editable__number['number'], parent:Cyes__modified__yes__is_editable__number) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cyes__is_set__number__type__property {
	export class Dby extends StateGroup<
		{ name: 'engine', node:Cengine__by__yes__is_set__number, init:Tengine__by__yes__is_set__number}|
		{ name: 'user', node:Cuser__by__yes__is_set__number, init:Tuser__by__yes__is_set__number}> {
		protected initializer(state: 'engine'|'user') {
			switch (state) {
				case 'engine': return (init:Tengine__by__yes__is_set__number, parent:Cyes__is_set__number__type__property) => new Cengine__by__yes__is_set__number(init, parent);
				case 'user': return (init:Tuser__by__yes__is_set__number, parent:Cyes__is_set__number__type__property) => new Cuser__by__yes__is_set__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_set__number__type__property['by'], parent:Cyes__is_set__number__type__property) {
			super(data, parent, 'by');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'by'`; }
	}
	export class Dnumber extends AlanInteger {
		constructor(data:Tyes__is_set__number__type__property['number'], parent:Cyes__is_set__number__type__property) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cno__is_valid__number__type__property {
	export class Dexceeds_maximum extends StateGroup<
		{ name: 'no', node:Cno__exceeds_maximum, init:Tno__exceeds_maximum}|
		{ name: 'yes', node:Cyes__exceeds_maximum, init:Tyes__exceeds_maximum}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__exceeds_maximum, parent:Cno__is_valid__number__type__property) => new Cno__exceeds_maximum(init, parent);
				case 'yes': return (init:Tyes__exceeds_maximum, parent:Cno__is_valid__number__type__property) => new Cyes__exceeds_maximum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__number__type__property['exceeds maximum'], parent:Cno__is_valid__number__type__property) {
			super(data, parent, 'exceeds maximum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'exceeds maximum'`; }
	}
	export class Dexceeds_minimum extends StateGroup<
		{ name: 'no', node:Cno__exceeds_minimum, init:Tno__exceeds_minimum}|
		{ name: 'yes', node:Cyes__exceeds_minimum, init:Tyes__exceeds_minimum}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__exceeds_minimum, parent:Cno__is_valid__number__type__property) => new Cno__exceeds_minimum(init, parent);
				case 'yes': return (init:Tyes__exceeds_minimum, parent:Cno__is_valid__number__type__property) => new Cyes__exceeds_minimum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__number__type__property['exceeds minimum'], parent:Cno__is_valid__number__type__property) {
			super(data, parent, 'exceeds minimum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'exceeds minimum'`; }
	}
	export class Dnot_a_number extends StateGroup<
		{ name: 'no', node:Cno__not_a_number, init:Tno__not_a_number}|
		{ name: 'yes', node:Cyes__not_a_number, init:Tyes__not_a_number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__not_a_number, parent:Cno__is_valid__number__type__property) => new Cno__not_a_number(init, parent);
				case 'yes': return (init:Tyes__not_a_number, parent:Cno__is_valid__number__type__property) => new Cyes__not_a_number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__number__type__property['not a number'], parent:Cno__is_valid__number__type__property) {
			super(data, parent, 'not a number');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'not a number'`; }
	}
	export class Dnot_set extends StateGroup<
		{ name: 'no', node:Cno__not_set__no__is_valid__number, init:Tno__not_set__no__is_valid__number}|
		{ name: 'yes', node:Cyes__not_set__no__is_valid__number, init:Tyes__not_set__no__is_valid__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__not_set__no__is_valid__number, parent:Cno__is_valid__number__type__property) => new Cno__not_set__no__is_valid__number(init, parent);
				case 'yes': return (init:Tyes__not_set__no__is_valid__number, parent:Cno__is_valid__number__type__property) => new Cyes__not_set__no__is_valid__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__number__type__property['not set'], parent:Cno__is_valid__number__type__property) {
			super(data, parent, 'not set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'not set'`; }
	}
	export class Dshould_be_positive extends StateGroup<
		{ name: 'no', node:Cno__should_be_positive, init:Tno__should_be_positive}|
		{ name: 'yes', node:Cyes__should_be_positive, init:Tyes__should_be_positive}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__should_be_positive, parent:Cno__is_valid__number__type__property) => new Cno__should_be_positive(init, parent);
				case 'yes': return (init:Tyes__should_be_positive, parent:Cno__is_valid__number__type__property) => new Cyes__should_be_positive(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__number__type__property['should be positive'], parent:Cno__is_valid__number__type__property) {
			super(data, parent, 'should be positive');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'should be positive'`; }
	}
}
export namespace Cyes__exceeds_maximum {
	export class Dmaximum extends AlanInteger {
		constructor(data:Tyes__exceeds_maximum['maximum'], parent:Cyes__exceeds_maximum) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'maximum'`; }
	}
}
export namespace Cyes__exceeds_minimum {
	export class Dminimum extends AlanInteger {
		constructor(data:Tyes__exceeds_minimum['minimum'], parent:Cyes__exceeds_minimum) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'minimum'`; }
	}
}
export namespace Cstate_group__type__property {
	export class Dinitialized extends StateGroup<
		{ name: 'no', node:Cno__initialized__state_group, init:Tno__initialized__state_group}|
		{ name: 'yes', node:Cyes__initialized__state_group, init:Tyes__initialized__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__initialized__state_group, parent:Cstate_group__type__property) => new Cno__initialized__state_group(init, parent);
				case 'yes': return (init:Tyes__initialized__state_group, parent:Cstate_group__type__property) => new Cyes__initialized__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property['initialized'], parent:Cstate_group__type__property) {
			super(data, parent, 'initialized');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initialized'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__state_group, init:Tno__is_editable__state_group}|
		{ name: 'yes', node:Cyes__is_editable__state_group, init:Tyes__is_editable__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__state_group, parent:Cstate_group__type__property) => new Cno__is_editable__state_group(init, parent);
				case 'yes': return (init:Tyes__is_editable__state_group, parent:Cstate_group__type__property) => new Cyes__is_editable__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property['is editable'], parent:Cstate_group__type__property) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dis_modified extends StateGroup<
		{ name: 'no', node:Cno__is_modified, init:Tno__is_modified}|
		{ name: 'yes', node:Cyes__is_modified, init:Tyes__is_modified}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_modified, parent:Cstate_group__type__property) => new Cno__is_modified(init, parent);
				case 'yes': return (init:Tyes__is_modified, parent:Cstate_group__type__property) => new Cyes__is_modified(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property['is modified'], parent:Cstate_group__type__property) {
			super(data, parent, 'is modified');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is modified'`; }
	}
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__state_group__type__property, init:Tno__is_set__state_group__type__property}|
		{ name: 'yes', node:Cyes__is_set__state_group__type__property, init:Tyes__is_set__state_group__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__state_group__type__property, parent:Cstate_group__type__property) => new Cno__is_set__state_group__type__property(init, parent);
				case 'yes': return (init:Tyes__is_set__state_group__type__property, parent:Cstate_group__type__property) => new Cyes__is_set__state_group__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property['is set'], parent:Cstate_group__type__property) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__state_group, init:Tno__is_valid__state_group}|
		{ name: 'yes', node:Cyes__is_valid__state_group, init:Tyes__is_valid__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__state_group, parent:Cstate_group__type__property) => new Cno__is_valid__state_group(init, parent);
				case 'yes': return (init:Tyes__is_valid__state_group, parent:Cstate_group__type__property) => new Cyes__is_valid__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property['is valid'], parent:Cstate_group__type__property) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__state_group__type__property, init:Tstates__state_group__type__property},Cstate_group__type__property> {
		protected initialize(parent:Cstate_group__type__property, key:string, entry_init:Tstates__state_group__type__property) { return new Cstates__state_group__type__property(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__property['states'], parent:Cstate_group__type__property) {
			super(data, parent);
		}
	}
}
export namespace Cyes__initialized__state_group {
	export class Dstate_in_backend extends Reference<client_state.Cstates__state_group__type__property,string> {
		public readonly inferences:{
			state_in_backend: () => client_state.Cremote__location__states
		}

		constructor(data:string, $this:Cyes__initialized__state_group) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				state_in_backend: cache((detach:boolean) => {
					const client_state__node__attributes__type__property__type__state_group__initialized__yes__state_in_backend_nval = $this.properties.state_in_backend.ref;
					return resolve($this.properties.state_in_backend.ref)
						.then(context => client_state__node__attributes__type__property__type__state_group__initialized__yes__state_in_backend_nval)
						.then(context => {
							if (context?.properties.location.state.name === 'remote') {
								return resolve(context.properties.location.state.node as client_state.Cremote__location__states).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state in backend'`; }
	}
}
export namespace Cyes__is_editable__state_group {
	export class Dedit_mode extends StateGroup<
		{ name: 'off', node:Coff__edit_mode__yes__is_editable__state_group, init:Toff__edit_mode__yes__is_editable__state_group}|
		{ name: 'on', node:Con__edit_mode__yes__is_editable__state_group, init:Ton__edit_mode__yes__is_editable__state_group}> {
		protected initializer(state: 'off'|'on') {
			switch (state) {
				case 'off': return (init:Toff__edit_mode__yes__is_editable__state_group, parent:Cyes__is_editable__state_group) => new Coff__edit_mode__yes__is_editable__state_group(init, parent);
				case 'on': return (init:Ton__edit_mode__yes__is_editable__state_group, parent:Cyes__is_editable__state_group) => new Con__edit_mode__yes__is_editable__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__state_group['edit mode'], parent:Cyes__is_editable__state_group) {
			super(data, parent, 'edit mode');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edit mode'`; }
	}
}
export namespace Cyes__is_set__state_group__type__property {
	export class Dby extends StateGroup<
		{ name: 'engine', node:Cengine__by__yes__is_set__state_group, init:Tengine__by__yes__is_set__state_group}|
		{ name: 'user', node:Cuser__by__yes__is_set__state_group, init:Tuser__by__yes__is_set__state_group}> {
		protected initializer(state: 'engine'|'user') {
			switch (state) {
				case 'engine': return (init:Tengine__by__yes__is_set__state_group, parent:Cyes__is_set__state_group__type__property) => new Cengine__by__yes__is_set__state_group(init, parent);
				case 'user': return (init:Tuser__by__yes__is_set__state_group, parent:Cyes__is_set__state_group__type__property) => new Cuser__by__yes__is_set__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_set__state_group__type__property['by'], parent:Cyes__is_set__state_group__type__property) {
			super(data, parent, 'by');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'by'`; }
	}
	export class Dstate extends Reference<client_state.Cstates__state_group__type__property,string> {
		public readonly inferences:{
			state: () => client_state.Cyes__is_selected__states__state_group__type__property
		}

		constructor(data:string, $this:Cyes__is_set__state_group__type__property) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				state: cache((detach:boolean) => {
					const client_state__node__attributes__type__property__type__state_group__is_set__yes__state_nval = $this.properties.state.ref;
					return resolve($this.properties.state.ref)
						.then(context => client_state__node__attributes__type__property__type__state_group__is_set__yes__state_nval)
						.then(context => {
							if (context?.properties.is_selected.state.name === 'yes') {
								return resolve(context.properties.is_selected.state.node as client_state.Cyes__is_selected__states__state_group__type__property).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cno__is_valid__state_group {
	export class Dinvalid_rules extends StateGroup<
		{ name: 'no', node:Cno__invalid_rules__no__is_valid__state_group, init:Tno__invalid_rules__no__is_valid__state_group}|
		{ name: 'yes', node:Cyes__invalid_rules__no__is_valid__state_group, init:Tyes__invalid_rules__no__is_valid__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__invalid_rules__no__is_valid__state_group, parent:Cno__is_valid__state_group) => new Cno__invalid_rules__no__is_valid__state_group(init, parent);
				case 'yes': return (init:Tyes__invalid_rules__no__is_valid__state_group, parent:Cno__is_valid__state_group) => new Cyes__invalid_rules__no__is_valid__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__state_group['invalid rules'], parent:Cno__is_valid__state_group) {
			super(data, parent, 'invalid rules');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'invalid rules'`; }
	}
	export class Dno_state_selected extends StateGroup<
		{ name: 'no', node:Cno__no_state_selected, init:Tno__no_state_selected}|
		{ name: 'yes', node:Cyes__no_state_selected, init:Tyes__no_state_selected}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__no_state_selected, parent:Cno__is_valid__state_group) => new Cno__no_state_selected(init, parent);
				case 'yes': return (init:Tyes__no_state_selected, parent:Cno__is_valid__state_group) => new Cyes__no_state_selected(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__state_group['no state selected'], parent:Cno__is_valid__state_group) {
			super(data, parent, 'no state selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'no state selected'`; }
	}
}
export namespace Cstates__state_group__type__property {
	export class Dcreate_permitted extends StateGroup<
		{ name: 'no', node:Cno__create_permitted, init:Tno__create_permitted}|
		{ name: 'yes', node:Cyes__create_permitted, init:Tyes__create_permitted}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__create_permitted, parent:Cstates__state_group__type__property) => new Cno__create_permitted(init, parent);
				case 'yes': return (init:Tyes__create_permitted, parent:Cstates__state_group__type__property) => new Cyes__create_permitted(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group__type__property['create permitted'], parent:Cstates__state_group__type__property) {
			super(data, parent, 'create permitted');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'create permitted'`; }
	}
	export class Dis_selected extends StateGroup<
		{ name: 'no', node:Cno__is_selected__states__state_group__type__property, init:Tno__is_selected__states__state_group__type__property}|
		{ name: 'yes', node:Cyes__is_selected__states__state_group__type__property, init:Tyes__is_selected__states__state_group__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_selected__states__state_group__type__property, parent:Cstates__state_group__type__property) => new Cno__is_selected__states__state_group__type__property(init, parent);
				case 'yes': return (init:Tyes__is_selected__states__state_group__type__property, parent:Cstates__state_group__type__property) => new Cyes__is_selected__states__state_group__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group__type__property['is selected'], parent:Cstates__state_group__type__property) {
			super(data, parent, 'is selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is selected'`; }
	}
	export class Dlocation extends StateGroup<
		{ name: 'local', node:Clocal__location__states, init:Tlocal__location__states}|
		{ name: 'remote', node:Cremote__location__states, init:Tremote__location__states}> {
		protected initializer(state: 'local'|'remote') {
			switch (state) {
				case 'local': return (init:Tlocal__location__states, parent:Cstates__state_group__type__property) => new Clocal__location__states(init, parent);
				case 'remote': return (init:Tremote__location__states, parent:Cstates__state_group__type__property) => new Cremote__location__states(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group__type__property['location'], parent:Cstates__state_group__type__property) {
			super(data, parent, 'location');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'location'`; }
	}
	export class Dnode extends Cnode {
		constructor(data:Tstates__state_group__type__property['node'], parent:Cstates__state_group__type__property) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.location?.state.node.output.state_node_location()).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!),
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.node_position).result!)
			}, 'node')
		}
	}
	export class Drules extends Cwhere_rules {
		constructor(data:Tstates__state_group__type__property['rules'], parent:Cstates__state_group__type__property) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.where_rules_definer).result!),
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.rules).result!)
			}, 'rules')
		}
	}
	export class Dstyle extends Cstyle {
		constructor(data:Tstates__state_group__type__property['style'], parent:Cstates__state_group__type__property) {
			super(data, parent, 'style')
		}
	}
}
export namespace Ctext__type__property {
	export class Dbackend_known extends StateGroup<
		{ name: 'no', node:Cno__backend_known__text, init:Tno__backend_known__text}|
		{ name: 'yes', node:Cyes__backend_known__text, init:Tyes__backend_known__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__backend_known__text, parent:Ctext__type__property) => new Cno__backend_known__text(init, parent);
				case 'yes': return (init:Tyes__backend_known__text, parent:Ctext__type__property) => new Cyes__backend_known__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['backend known'], parent:Ctext__type__property) {
			super(data, parent, 'backend known');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'backend known'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__text, init:Tno__is_editable__text}|
		{ name: 'yes', node:Cyes__is_editable__text, init:Tyes__is_editable__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__text, parent:Ctext__type__property) => new Cno__is_editable__text(init, parent);
				case 'yes': return (init:Tyes__is_editable__text, parent:Ctext__type__property) => new Cyes__is_editable__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['is editable'], parent:Ctext__type__property) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dis_key extends StateGroup<
		{ name: 'no', node:Cno__is_key, init:Tno__is_key}|
		{ name: 'yes', node:Cyes__is_key, init:Tyes__is_key}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_key, parent:Ctext__type__property) => new Cno__is_key(init, parent);
				case 'yes': return (init:Tyes__is_key, parent:Ctext__type__property) => new Cyes__is_key(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['is key'], parent:Ctext__type__property) {
			super(data, parent, 'is key');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is key'`; }
	}
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__text__type__property, init:Tno__is_set__text__type__property}|
		{ name: 'yes', node:Cyes__is_set__text__type__property, init:Tyes__is_set__text__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__text__type__property, parent:Ctext__type__property) => new Cno__is_set__text__type__property(init, parent);
				case 'yes': return (init:Tyes__is_set__text__type__property, parent:Ctext__type__property) => new Cyes__is_set__text__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['is set'], parent:Ctext__type__property) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__text__type__property, init:Tno__is_valid__text__type__property}|
		{ name: 'yes', node:Cyes__is_valid__text__type__property, init:Tyes__is_valid__text__type__property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__text__type__property, parent:Ctext__type__property) => new Cno__is_valid__text__type__property(init, parent);
				case 'yes': return (init:Tyes__is_valid__text__type__property, parent:Ctext__type__property) => new Cyes__is_valid__text__type__property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['is valid'], parent:Ctext__type__property) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dnavigable extends StateGroup<
		{ name: 'no', node:Cno__navigable, init:Tno__navigable}|
		{ name: 'yes', node:Cyes__navigable, init:Tyes__navigable}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__navigable, parent:Ctext__type__property) => new Cno__navigable(init, parent);
				case 'yes': return (init:Tyes__navigable, parent:Ctext__type__property) => new Cyes__navigable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['navigable'], parent:Ctext__type__property) {
			super(data, parent, 'navigable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'navigable'`; }
	}
	export class Dvalidation_result extends StateGroup<
		{ name: 'failure', node:Cfailure__validation_result__text, init:Tfailure__validation_result__text}|
		{ name: 'success', node:Csuccess__validation_result__text, init:Tsuccess__validation_result__text}> {
		protected initializer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__validation_result__text, parent:Ctext__type__property) => new Cfailure__validation_result__text(init, parent);
				case 'success': return (init:Tsuccess__validation_result__text, parent:Ctext__type__property) => new Csuccess__validation_result__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property['validation result'], parent:Ctext__type__property) {
			super(data, parent, 'validation result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'validation result'`; }
	}
}
export namespace Cyes__backend_known__text {
}
export namespace Cyes__is_editable__text {
	export class Dedit_mode extends StateGroup<
		{ name: 'off', node:Coff__edit_mode__yes__is_editable__text, init:Toff__edit_mode__yes__is_editable__text}|
		{ name: 'on', node:Con__edit_mode__yes__is_editable__text, init:Ton__edit_mode__yes__is_editable__text}> {
		protected initializer(state: 'off'|'on') {
			switch (state) {
				case 'off': return (init:Toff__edit_mode__yes__is_editable__text, parent:Cyes__is_editable__text) => new Coff__edit_mode__yes__is_editable__text(init, parent);
				case 'on': return (init:Ton__edit_mode__yes__is_editable__text, parent:Cyes__is_editable__text) => new Con__edit_mode__yes__is_editable__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__text['edit mode'], parent:Cyes__is_editable__text) {
			super(data, parent, 'edit mode');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edit mode'`; }
	}
	export class Dmodified extends StateGroup<
		{ name: 'no', node:Cno__modified__yes__is_editable__text, init:Tno__modified__yes__is_editable__text}|
		{ name: 'yes', node:Cyes__modified__yes__is_editable__text, init:Tyes__modified__yes__is_editable__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__modified__yes__is_editable__text, parent:Cyes__is_editable__text) => new Cno__modified__yes__is_editable__text(init, parent);
				case 'yes': return (init:Tyes__modified__yes__is_editable__text, parent:Cyes__is_editable__text) => new Cyes__modified__yes__is_editable__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_editable__text['modified'], parent:Cyes__is_editable__text) {
			super(data, parent, 'modified');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modified'`; }
	}
}
export namespace Cyes__modified__yes__is_editable__text {
	export class Dsaving extends StateGroup<
		{ name: 'no', node:Cno__saving, init:Tno__saving}|
		{ name: 'yes', node:Cyes__saving, init:Tyes__saving}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__saving, parent:Cyes__modified__yes__is_editable__text) => new Cno__saving(init, parent);
				case 'yes': return (init:Tyes__saving, parent:Cyes__modified__yes__is_editable__text) => new Cyes__saving(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__modified__yes__is_editable__text['saving'], parent:Cyes__modified__yes__is_editable__text) {
			super(data, parent, 'saving');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'saving'`; }
	}
}
export namespace Cyes__is_key {
	export class Dkey_unique extends StateGroup<
		{ name: 'no', node:Cno__key_unique, init:Tno__key_unique}|
		{ name: 'yes', node:Cyes__key_unique, init:Tyes__key_unique}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__key_unique, parent:Cyes__is_key) => new Cno__key_unique(init, parent);
				case 'yes': return (init:Tyes__key_unique, parent:Cyes__is_key) => new Cyes__key_unique(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_key['key unique'], parent:Cyes__is_key) {
			super(data, parent, 'key unique');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'key unique'`; }
	}
}
export namespace Cyes__is_set__text__type__property {
	export class Dby extends StateGroup<
		{ name: 'engine', node:Cengine__by__yes__is_set__text, init:Tengine__by__yes__is_set__text}|
		{ name: 'user', node:Cuser__by__yes__is_set__text, init:Tuser__by__yes__is_set__text}> {
		protected initializer(state: 'engine'|'user') {
			switch (state) {
				case 'engine': return (init:Tengine__by__yes__is_set__text, parent:Cyes__is_set__text__type__property) => new Cengine__by__yes__is_set__text(init, parent);
				case 'user': return (init:Tuser__by__yes__is_set__text, parent:Cyes__is_set__text__type__property) => new Cuser__by__yes__is_set__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_set__text__type__property['by'], parent:Cyes__is_set__text__type__property) {
			super(data, parent, 'by');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'by'`; }
	}
}
export namespace Cno__is_valid__text__type__property {
	export class Dinvalid_rules extends StateGroup<
		{ name: 'no', node:Cno__invalid_rules__no__is_valid__text, init:Tno__invalid_rules__no__is_valid__text}|
		{ name: 'yes', node:Cyes__invalid_rules__no__is_valid__text, init:Tyes__invalid_rules__no__is_valid__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__invalid_rules__no__is_valid__text, parent:Cno__is_valid__text__type__property) => new Cno__invalid_rules__no__is_valid__text(init, parent);
				case 'yes': return (init:Tyes__invalid_rules__no__is_valid__text, parent:Cno__is_valid__text__type__property) => new Cyes__invalid_rules__no__is_valid__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__text__type__property['invalid rules'], parent:Cno__is_valid__text__type__property) {
			super(data, parent, 'invalid rules');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'invalid rules'`; }
	}
	export class Dkey_is_not_unique extends StateGroup<
		{ name: 'no', node:Cno__key_is_not_unique, init:Tno__key_is_not_unique}|
		{ name: 'yes', node:Cyes__key_is_not_unique, init:Tyes__key_is_not_unique}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__key_is_not_unique, parent:Cno__is_valid__text__type__property) => new Cno__key_is_not_unique(init, parent);
				case 'yes': return (init:Tyes__key_is_not_unique, parent:Cno__is_valid__text__type__property) => new Cyes__key_is_not_unique(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__text__type__property['key is not unique'], parent:Cno__is_valid__text__type__property) {
			super(data, parent, 'key is not unique');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'key is not unique'`; }
	}
	export class Dreference_not_resolved extends StateGroup<
		{ name: 'no', node:Cno__reference_not_resolved, init:Tno__reference_not_resolved}|
		{ name: 'yes', node:Cyes__reference_not_resolved, init:Tyes__reference_not_resolved}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__reference_not_resolved, parent:Cno__is_valid__text__type__property) => new Cno__reference_not_resolved(init, parent);
				case 'yes': return (init:Tyes__reference_not_resolved, parent:Cno__is_valid__text__type__property) => new Cyes__reference_not_resolved(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__text__type__property['reference not resolved'], parent:Cno__is_valid__text__type__property) {
			super(data, parent, 'reference not resolved');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reference not resolved'`; }
	}
	export class Dreferenced_node_not_valid extends StateGroup<
		{ name: 'no', node:Cno__referenced_node_not_valid, init:Tno__referenced_node_not_valid}|
		{ name: 'yes', node:Cyes__referenced_node_not_valid, init:Tyes__referenced_node_not_valid}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__referenced_node_not_valid, parent:Cno__is_valid__text__type__property) => new Cno__referenced_node_not_valid(init, parent);
				case 'yes': return (init:Tyes__referenced_node_not_valid, parent:Cno__is_valid__text__type__property) => new Cyes__referenced_node_not_valid(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__text__type__property['referenced node not valid'], parent:Cno__is_valid__text__type__property) {
			super(data, parent, 'referenced node not valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'referenced node not valid'`; }
	}
	export class Dtext_does_not_match_pattern extends StateGroup<
		{ name: 'no', node:Cno__text_does_not_match_pattern, init:Tno__text_does_not_match_pattern}|
		{ name: 'yes', node:Cyes__text_does_not_match_pattern, init:Tyes__text_does_not_match_pattern}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__text_does_not_match_pattern, parent:Cno__is_valid__text__type__property) => new Cno__text_does_not_match_pattern(init, parent);
				case 'yes': return (init:Tyes__text_does_not_match_pattern, parent:Cno__is_valid__text__type__property) => new Cyes__text_does_not_match_pattern(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_valid__text__type__property['text does not match pattern'], parent:Cno__is_valid__text__type__property) {
			super(data, parent, 'text does not match pattern');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text does not match pattern'`; }
	}
}
export namespace Cyes__text_does_not_match_pattern {
}
export namespace Cyes__navigable {
	export class Dqueries extends AlanDictionary<{ node:Cqueries__yes, init:Tqueries__yes},Cyes__navigable> {
		protected initialize(parent:Cyes__navigable, key:string, entry_init:Tqueries__yes) { return new Cqueries__yes(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'queries'`; }
		constructor(data:Tyes__navigable['queries'], parent:Cyes__navigable) {
			super(data, parent);
		}
	}
	export class Dresolve_status extends Cresolve_status {
		constructor(data:Tyes__navigable['resolve status'], parent:Cyes__navigable) {
			super(data, parent, {
				resolvable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.resolvable).result!)
			}, 'resolve status')
		}
	}
	export class Drules extends Cwhere_rules {
		constructor(data:Tyes__navigable['rules'], parent:Cyes__navigable) {
			super(data, parent, {
				definer: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.where_rules_definer).result!),
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.is_navigable())
					.then(context => context?.properties.rules).result!)
			}, 'rules')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'mandatory', node:Cmandatory, init:Tmandatory}|
		{ name: 'optional', node:Coptional, init:Toptional}> {
		protected initializer(state: 'mandatory'|'optional') {
			switch (state) {
				case 'mandatory': return (init:Tmandatory, parent:Cyes__navigable) => new Cmandatory(init, parent);
				case 'optional': return (init:Toptional, parent:Cyes__navigable) => new Coptional(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__navigable['type'], parent:Cyes__navigable) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cqueries__yes {
	export class Dquery extends Cquery__client_state {
		constructor(data:Tqueries__yes['query'], parent:Cqueries__yes) {
			super(data, parent, {
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.query_position).result!)
			}, 'query')
		}
	}
}
export namespace Clocal__location__node {
	export class Dparent_location extends StateGroup<
		{ name: 'local', node:Clocal__parent_location, init:Tlocal__parent_location}|
		{ name: 'remote', node:Cremote__parent_location, init:Tremote__parent_location}> {
		protected initializer(state: 'local'|'remote') {
			switch (state) {
				case 'local': return (init:Tlocal__parent_location, parent:Clocal__location__node) => new Clocal__parent_location(init, parent);
				case 'remote': return (init:Tremote__parent_location, parent:Clocal__location__node) => new Cremote__parent_location(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tlocal__location__node['parent location'], parent:Clocal__location__node) {
			super(data, parent, 'parent location');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'parent location'`; }
	}
}
export namespace Cremote__location__node {
	export class Dedit_mode extends StateGroup<
		{ name: 'off', node:Coff__edit_mode__remote, init:Toff__edit_mode__remote}|
		{ name: 'on', node:Con__edit_mode__remote, init:Ton__edit_mode__remote}> {
		protected initializer(state: 'off'|'on') {
			switch (state) {
				case 'off': return (init:Toff__edit_mode__remote, parent:Cremote__location__node) => new Coff__edit_mode__remote(init, parent);
				case 'on': return (init:Ton__edit_mode__remote, parent:Cremote__location__node) => new Con__edit_mode__remote(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tremote__location__node['edit mode'], parent:Cremote__location__node) {
			super(data, parent, 'edit mode');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'edit mode'`; }
	}
}
export namespace Cqueries__node {
	export class Dquery extends Cquery__client_state {
		constructor(data:Tqueries__node['query'], parent:Cqueries__node) {
			super(data, parent, {
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.query_position).result!)
			}, 'query')
		}
	}
}
export namespace Creport_types__node {
	export class Dinitialized extends StateGroup<
		{ name: 'no', node:Cno__initialized__report_types, init:Tno__initialized__report_types}|
		{ name: 'yes', node:Cyes__initialized__report_types, init:Tyes__initialized__report_types}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__initialized__report_types, parent:Creport_types__node) => new Cno__initialized__report_types(init, parent);
				case 'yes': return (init:Tyes__initialized__report_types, parent:Creport_types__node) => new Cyes__initialized__report_types(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treport_types__node['initialized'], parent:Creport_types__node) {
			super(data, parent, 'initialized');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initialized'`; }
	}
}
export namespace Cyes__initialized__report_types {
	export class Dparameters extends Creport_parameters {
		constructor(data:Tyes__initialized__report_types['parameters'], parent:Cyes__initialized__report_types) {
			super(data, parent, {
				report_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.key?.ref).result!)
			}, 'parameters')
		}
	}
	export class Dtemplates extends AlanDictionary<{ node:Ctemplates, init:Ttemplates},Cyes__initialized__report_types> {
		protected initialize(parent:Cyes__initialized__report_types, key:string) { return new Ctemplates(key, {}, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'templates'`; }
		constructor(data:Tyes__initialized__report_types['templates'], parent:Cyes__initialized__report_types) {
			super(data, parent);
		}
	}
}
export namespace Cyes__todo {
	export class Dfor_ extends StateGroup<
		{ name: 'current user', node:Ccurrent_user, init:Tcurrent_user}|
		{ name: 'someone else', node:Csomeone_else, init:Tsomeone_else}> {
		protected initializer(state: 'current user'|'someone else') {
			switch (state) {
				case 'current user': return (init:Tcurrent_user, parent:Cyes__todo) => new Ccurrent_user(init, parent);
				case 'someone else': return (init:Tsomeone_else, parent:Cyes__todo) => new Csomeone_else(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__todo['for'], parent:Cyes__todo) {
			super(data, parent, 'for');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'for'`; }
	}
}
export namespace Ccurrent_user {
	export class Dand extends StateGroup<
		{ name: 'no one else', node:Cno_one_else, init:Tno_one_else}|
		{ name: 'others', node:Cothers, init:Tothers}> {
		protected initializer(state: 'no one else'|'others') {
			switch (state) {
				case 'no one else': return (init:Tno_one_else, parent:Ccurrent_user) => new Cno_one_else(init, parent);
				case 'others': return (init:Tothers, parent:Ccurrent_user) => new Cothers(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcurrent_user['and'], parent:Ccurrent_user) {
			super(data, parent, 'and');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'and'`; }
	}
}
export namespace Cnumerical_type_properties {
	export class Dinitialized extends StateGroup<
		{ name: 'no', node:Cno__initialized__numerical_type_properties, init:Tno__initialized__numerical_type_properties}|
		{ name: 'yes', node:Cyes__initialized__numerical_type_properties, init:Tyes__initialized__numerical_type_properties}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__initialized__numerical_type_properties, parent:Cnumerical_type_properties) => new Cno__initialized__numerical_type_properties(init, parent);
				case 'yes': return (init:Tyes__initialized__numerical_type_properties, parent:Cnumerical_type_properties) => new Cyes__initialized__numerical_type_properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumerical_type_properties['initialized'], parent:Cnumerical_type_properties) {
			super(data, parent, 'initialized');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initialized'`; }
	}
}
export namespace Cyes__initialized__numerical_type_properties {
	export class Ddecimals extends AlanInteger {
		constructor(data:Tyes__initialized__numerical_type_properties['decimals'], parent:Cyes__initialized__numerical_type_properties) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'decimals'`; }
	}
}
export namespace Cquery__client_state {
	export class Dcan_download_files extends StateGroup<
		{ name: 'no', node:Cno__can_download_files__query, init:Tno__can_download_files__query}|
		{ name: 'yes', node:Cyes__can_download_files__query, init:Tyes__can_download_files__query}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_download_files__query, parent:Cquery__client_state) => new Cno__can_download_files__query(init, parent);
				case 'yes': return (init:Tyes__can_download_files__query, parent:Cquery__client_state) => new Cyes__can_download_files__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['can download files'], parent:Cquery__client_state) {
			super(data, parent, 'can download files');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can download files'`; }
	}
	export class Dcolumns extends AlanTopology<{ node:Ccolumns, init:Tcolumns},Cquery__client_state,'column order'> {
		protected graph_iterator(graph:('column order')):(node:Ccolumns) => Ccolumns {
			switch (graph) {
				case 'column order': return (entry:Ccolumns) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as client_state.Cyes__has_successor__columns;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cquery__client_state, key:string, entry_init:Tcolumns) { return new Ccolumns(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'columns'`; }
		constructor(data:Tquery__client_state['columns'], parent:Cquery__client_state) {
			super(data, parent);
		}
	}
	export class Ddefinition extends Reference<views.Cqueries,string> {

		constructor(data:string, $this:Cquery__client_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.view_definition?.ref)
				.then(context => {
					const entry = context?.properties.queries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'definition'`; }
	}
	export class Ddelay_query extends StateGroup<
		{ name: 'no', node:Cno__delay_query, init:Tno__delay_query}|
		{ name: 'yes', node:Cyes__delay_query, init:Tyes__delay_query}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__delay_query, parent:Cquery__client_state) => new Cno__delay_query(init, parent);
				case 'yes': return (init:Tyes__delay_query, parent:Cquery__client_state) => new Cyes__delay_query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['delay query'], parent:Cquery__client_state) {
			super(data, parent, 'delay query');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'delay query'`; }
	}
	export class Dentries_are_editable extends StateGroup<
		{ name: 'no', node:Cno__entries_are_editable, init:Tno__entries_are_editable}|
		{ name: 'yes', node:Cyes__entries_are_editable, init:Tyes__entries_are_editable}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__entries_are_editable, parent:Cquery__client_state) => new Cno__entries_are_editable(init, parent);
				case 'yes': return (init:Tyes__entries_are_editable, parent:Cquery__client_state) => new Cyes__entries_are_editable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['entries are editable'], parent:Cquery__client_state) {
			super(data, parent, 'entries are editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries are editable'`; }
	}
	export class Dfilters extends AlanDictionary<{ node:Cfilters, init:Tfilters},Cquery__client_state> {
		protected initialize(parent:Cquery__client_state, key:string, entry_init:Tfilters) { return new Cfilters(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filters'`; }
		constructor(data:Tquery__client_state['filters'], parent:Cquery__client_state) {
			super(data, parent);
		}
	}
	export class Dhas_columns extends StateGroup<
		{ name: 'no', node:Cno__has_columns, init:Tno__has_columns}|
		{ name: 'yes', node:Cyes__has_columns, init:Tyes__has_columns}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_columns, parent:Cquery__client_state) => new Cno__has_columns(init, parent);
				case 'yes': return (init:Tyes__has_columns, parent:Cquery__client_state) => new Cyes__has_columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['has columns'], parent:Cquery__client_state) {
			super(data, parent, 'has columns');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has columns'`; }
	}
	export class Dhas_result extends StateGroup<
		{ name: 'no', node:Cno__has_result, init:Tno__has_result}|
		{ name: 'yes', node:Cyes__has_result, init:Tyes__has_result}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_result, parent:Cquery__client_state) => new Cno__has_result(init, parent);
				case 'yes': return (init:Tyes__has_result, parent:Cquery__client_state) => new Cyes__has_result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['has result'], parent:Cquery__client_state) {
			super(data, parent, 'has result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has result'`; }
	}
	export class Dhas_selected_entries extends StateGroup<
		{ name: 'no', node:Cno__has_selected_entries, init:Tno__has_selected_entries}|
		{ name: 'yes', node:Cyes__has_selected_entries, init:Tyes__has_selected_entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_selected_entries, parent:Cquery__client_state) => new Cno__has_selected_entries(init, parent);
				case 'yes': return (init:Tyes__has_selected_entries, parent:Cquery__client_state) => new Cyes__has_selected_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['has selected entries'], parent:Cquery__client_state) {
			super(data, parent, 'has selected entries');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has selected entries'`; }
	}
	export class Dis_busy_querying extends StateGroup<
		{ name: 'no', node:Cno__is_busy_querying, init:Tno__is_busy_querying}|
		{ name: 'yes', node:Cyes__is_busy_querying, init:Tyes__is_busy_querying}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_busy_querying, parent:Cquery__client_state) => new Cno__is_busy_querying(init, parent);
				case 'yes': return (init:Tyes__is_busy_querying, parent:Cquery__client_state) => new Cyes__is_busy_querying(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['is busy querying'], parent:Cquery__client_state) {
			super(data, parent, 'is busy querying');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is busy querying'`; }
	}
	export class Dshow_all extends StateGroup<
		{ name: 'no', node:Cno__show_all, init:Tno__show_all}|
		{ name: 'yes', node:Cyes__show_all, init:Tyes__show_all}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__show_all, parent:Cquery__client_state) => new Cno__show_all(init, parent);
				case 'yes': return (init:Tyes__show_all, parent:Cquery__client_state) => new Cyes__show_all(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['show all'], parent:Cquery__client_state) {
			super(data, parent, 'show all');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'show all'`; }
	}
	export class Duse_for extends StateGroup<
		{ name: 'candidates', node:Ccandidates__use_for__query, init:Tcandidates__use_for__query}|
		{ name: 'query', node:Cquery__use_for__query, init:Tquery__use_for__query}> {
		protected initializer(state: 'candidates'|'query') {
			switch (state) {
				case 'candidates': return (init:Tcandidates__use_for__query, parent:Cquery__client_state) => new Ccandidates__use_for__query(init, parent);
				case 'query': return (init:Tquery__use_for__query, parent:Cquery__client_state) => new Cquery__use_for__query(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery__client_state['use for'], parent:Cquery__client_state) {
			super(data, parent, 'use for');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'use for'`; }
	}
	export class Dview_definition extends Reference<views.Cviews__,string> {

		constructor(data:string, $this:Cquery__client_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.view_definition_context?.ref)
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view definition'`; }
	}
	export class Dview_definition_context extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Cquery__client_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view definition context'`; }
	}
}
export namespace Ccolumns {
	export class Dcolumn_type extends StateGroup<
		{ name: 'content', node:Ccontent__column_type__columns, init:Tcontent__column_type__columns}|
		{ name: 'id', node:Cid__column_type__columns, init:Tid__column_type__columns}> {
		protected initializer(state: 'content'|'id') {
			switch (state) {
				case 'content': return (init:Tcontent__column_type__columns, parent:Ccolumns) => new Ccontent__column_type__columns(init, parent);
				case 'id': return (init:Tid__column_type__columns, parent:Ccolumns) => new Cid__column_type__columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcolumns['column type'], parent:Ccolumns) {
			super(data, parent, 'column type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'column type'`; }
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__columns, init:Tno__has_successor__columns}|
		{ name: 'yes', node:Cyes__has_successor__columns, init:Tyes__has_successor__columns}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__columns, parent:Ccolumns) => new Cno__has_successor__columns(init, parent);
				case 'yes': return (init:Tyes__has_successor__columns, parent:Ccolumns) => new Cyes__has_successor__columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcolumns['has successor'], parent:Ccolumns) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'file', node:Cfile__type__columns, init:Tfile__type__columns}|
		{ name: 'number', node:Cnumber__type__columns, init:Tnumber__type__columns}|
		{ name: 'state group', node:Cstate_group__type__columns, init:Tstate_group__type__columns}|
		{ name: 'text', node:Ctext__type__columns, init:Ttext__type__columns}|
		{ name: 'widget', node:Cwidget__type__columns, init:Twidget__type__columns}> {
		protected initializer(state: 'file'|'number'|'state group'|'text'|'widget') {
			switch (state) {
				case 'file': return (init:Tfile__type__columns, parent:Ccolumns) => new Cfile__type__columns(init, parent);
				case 'number': return (init:Tnumber__type__columns, parent:Ccolumns) => new Cnumber__type__columns(init, parent);
				case 'state group': return (init:Tstate_group__type__columns, parent:Ccolumns) => new Cstate_group__type__columns(init, parent);
				case 'text': return (init:Ttext__type__columns, parent:Ccolumns) => new Ctext__type__columns(init, parent);
				case 'widget': return (init:Twidget__type__columns, parent:Ccolumns) => new Cwidget__type__columns(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcolumns['type'], parent:Ccolumns) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_successor__columns {
	export class Dsuccessor extends Reference<client_state.Ccolumns,string> {

		constructor(data:string, $this:Cyes__has_successor__columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.columns.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Cnumber__type__columns {
	export class Dhas_filter extends StateGroup<
		{ name: 'no', node:Cno__has_filter__number, init:Tno__has_filter__number}|
		{ name: 'yes', node:Cyes__has_filter__number, init:Tyes__has_filter__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_filter__number, parent:Cnumber__type__columns) => new Cno__has_filter__number(init, parent);
				case 'yes': return (init:Tyes__has_filter__number, parent:Cnumber__type__columns) => new Cyes__has_filter__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__columns['has filter'], parent:Cnumber__type__columns) {
			super(data, parent, 'has filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has filter'`; }
	}
}
export namespace Cyes__has_filter__number {
	export class Dcriteria_is_valid extends StateGroup<
		{ name: 'no', node:Cno__criteria_is_valid__yes__has_filter, init:Tno__criteria_is_valid__yes__has_filter}|
		{ name: 'yes', node:Cyes__criteria_is_valid__yes__has_filter, init:Tyes__criteria_is_valid__yes__has_filter}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__criteria_is_valid__yes__has_filter, parent:Cyes__has_filter__number) => new Cno__criteria_is_valid__yes__has_filter(init, parent);
				case 'yes': return (init:Tyes__criteria_is_valid__yes__has_filter, parent:Cyes__has_filter__number) => new Cyes__criteria_is_valid__yes__has_filter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['criteria is valid'], parent:Cyes__has_filter__number) {
			super(data, parent, 'criteria is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criteria is valid'`; }
	}
	export class Dfilter_range extends StateGroup<
		{ name: 'no', node:Cno__filter_range, init:Tno__filter_range}|
		{ name: 'not available', node:Cnot_available, init:Tnot_available}|
		{ name: 'yes', node:Cyes__filter_range, init:Tyes__filter_range}> {
		protected initializer(state: 'no'|'not available'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__filter_range, parent:Cyes__has_filter__number) => new Cno__filter_range(init, parent);
				case 'not available': return (init:Tnot_available, parent:Cyes__has_filter__number) => new Cnot_available(init, parent);
				case 'yes': return (init:Tyes__filter_range, parent:Cyes__has_filter__number) => new Cyes__filter_range(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['filter range'], parent:Cyes__has_filter__number) {
			super(data, parent, 'filter range');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter range'`; }
	}
	export class Dfilter_set extends StateGroup<
		{ name: 'no', node:Cno__filter_set__yes__has_filter__number, init:Tno__filter_set__yes__has_filter__number}|
		{ name: 'yes', node:Cyes__filter_set__yes__has_filter__number, init:Tyes__filter_set__yes__has_filter__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__filter_set__yes__has_filter__number, parent:Cyes__has_filter__number) => new Cno__filter_set__yes__has_filter__number(init, parent);
				case 'yes': return (init:Tyes__filter_set__yes__has_filter__number, parent:Cyes__has_filter__number) => new Cyes__filter_set__yes__has_filter__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['filter set'], parent:Cyes__has_filter__number) {
			super(data, parent, 'filter set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter set'`; }
	}
	export class Dhas_default_filter extends StateGroup<
		{ name: 'no', node:Cno__has_default_filter__yes__has_filter__number, init:Tno__has_default_filter__yes__has_filter__number}|
		{ name: 'yes', node:Cyes__has_default_filter__yes__has_filter__number, init:Tyes__has_default_filter__yes__has_filter__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_default_filter__yes__has_filter__number, parent:Cyes__has_filter__number) => new Cno__has_default_filter__yes__has_filter__number(init, parent);
				case 'yes': return (init:Tyes__has_default_filter__yes__has_filter__number, parent:Cyes__has_filter__number) => new Cyes__has_default_filter__yes__has_filter__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['has default filter'], parent:Cyes__has_filter__number) {
			super(data, parent, 'has default filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has default filter'`; }
	}
	export class Dnumerical_type_properties extends Cnumerical_type_properties {
		constructor(data:Tyes__has_filter__number['numerical type properties'], parent:Cyes__has_filter__number) {
			super(data, parent, 'numerical type properties')
		}
	}
	export class Doperator_selected extends StateGroup<
		{ name: 'no', node:Cno__operator_selected, init:Tno__operator_selected}|
		{ name: 'yes', node:Cyes__operator_selected, init:Tyes__operator_selected}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__operator_selected, parent:Cyes__has_filter__number) => new Cno__operator_selected(init, parent);
				case 'yes': return (init:Tyes__operator_selected, parent:Cyes__has_filter__number) => new Cyes__operator_selected(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__number['operator selected'], parent:Cyes__has_filter__number) {
			super(data, parent, 'operator selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator selected'`; }
	}
}
export namespace Cyes__criteria_is_valid__yes__has_filter {
	export class Dcriteria extends AlanInteger {
		constructor(data:Tyes__criteria_is_valid__yes__has_filter['criteria'], parent:Cyes__criteria_is_valid__yes__has_filter) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criteria'`; }
	}
}
export namespace Cyes__filter_range {
	export class Dcriteria_is_valid extends StateGroup<
		{ name: 'no', node:Cno__criteria_is_valid__yes__filter_range, init:Tno__criteria_is_valid__yes__filter_range}|
		{ name: 'yes', node:Cyes__criteria_is_valid__yes__filter_range, init:Tyes__criteria_is_valid__yes__filter_range}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__criteria_is_valid__yes__filter_range, parent:Cyes__filter_range) => new Cno__criteria_is_valid__yes__filter_range(init, parent);
				case 'yes': return (init:Tyes__criteria_is_valid__yes__filter_range, parent:Cyes__filter_range) => new Cyes__criteria_is_valid__yes__filter_range(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__filter_range['criteria is valid'], parent:Cyes__filter_range) {
			super(data, parent, 'criteria is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criteria is valid'`; }
	}
	export class Doperator extends StateGroup<
		{ name: 'greater than', node:Cgreater_than__operator__yes__filter_range, init:Tgreater_than__operator__yes__filter_range}|
		{ name: 'smaller than', node:Csmaller_than__operator__yes__filter_range, init:Tsmaller_than__operator__yes__filter_range}> {
		protected initializer(state: 'greater than'|'smaller than') {
			switch (state) {
				case 'greater than': return (init:Tgreater_than__operator__yes__filter_range, parent:Cyes__filter_range) => new Cgreater_than__operator__yes__filter_range(init, parent);
				case 'smaller than': return (init:Tsmaller_than__operator__yes__filter_range, parent:Cyes__filter_range) => new Csmaller_than__operator__yes__filter_range(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__filter_range['operator'], parent:Cyes__filter_range) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cyes__criteria_is_valid__yes__filter_range {
	export class Dcriteria extends AlanInteger {
		constructor(data:Tyes__criteria_is_valid__yes__filter_range['criteria'], parent:Cyes__criteria_is_valid__yes__filter_range) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'criteria'`; }
	}
}
export namespace Cgreater_than__operator__yes__filter_range {
	export class Dor_equal_to extends StateGroup<
		{ name: 'no', node:Cno__or_equal_to__greater_than, init:Tno__or_equal_to__greater_than}|
		{ name: 'yes', node:Cyes__or_equal_to__greater_than, init:Tyes__or_equal_to__greater_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__or_equal_to__greater_than, parent:Cgreater_than__operator__yes__filter_range) => new Cno__or_equal_to__greater_than(init, parent);
				case 'yes': return (init:Tyes__or_equal_to__greater_than, parent:Cgreater_than__operator__yes__filter_range) => new Cyes__or_equal_to__greater_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgreater_than__operator__yes__filter_range['or equal to'], parent:Cgreater_than__operator__yes__filter_range) {
			super(data, parent, 'or equal to');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'or equal to'`; }
	}
}
export namespace Csmaller_than__operator__yes__filter_range {
	export class Dor_equal_to extends StateGroup<
		{ name: 'no', node:Cno__or_equal_to__smaller_than, init:Tno__or_equal_to__smaller_than}|
		{ name: 'yes', node:Cyes__or_equal_to__smaller_than, init:Tyes__or_equal_to__smaller_than}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__or_equal_to__smaller_than, parent:Csmaller_than__operator__yes__filter_range) => new Cno__or_equal_to__smaller_than(init, parent);
				case 'yes': return (init:Tyes__or_equal_to__smaller_than, parent:Csmaller_than__operator__yes__filter_range) => new Cyes__or_equal_to__smaller_than(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsmaller_than__operator__yes__filter_range['or equal to'], parent:Csmaller_than__operator__yes__filter_range) {
			super(data, parent, 'or equal to');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'or equal to'`; }
	}
}
export namespace Cyes__operator_selected {
	export class Doperator extends StateGroup<
		{ name: 'equal to', node:Cequal_to, init:Tequal_to}|
		{ name: 'greater than', node:Cgreater_than__operator__yes__operator_selected, init:Tgreater_than__operator__yes__operator_selected}|
		{ name: 'greater than or equal to', node:Cgreater_than_or_equal_to, init:Tgreater_than_or_equal_to}|
		{ name: 'smaller than', node:Csmaller_than__operator__yes__operator_selected, init:Tsmaller_than__operator__yes__operator_selected}|
		{ name: 'smaller than or equal to', node:Csmaller_than_or_equal_to, init:Tsmaller_than_or_equal_to}> {
		protected initializer(state: 'equal to'|'greater than'|'greater than or equal to'|'smaller than'|'smaller than or equal to') {
			switch (state) {
				case 'equal to': return (init:Tequal_to, parent:Cyes__operator_selected) => new Cequal_to(init, parent);
				case 'greater than': return (init:Tgreater_than__operator__yes__operator_selected, parent:Cyes__operator_selected) => new Cgreater_than__operator__yes__operator_selected(init, parent);
				case 'greater than or equal to': return (init:Tgreater_than_or_equal_to, parent:Cyes__operator_selected) => new Cgreater_than_or_equal_to(init, parent);
				case 'smaller than': return (init:Tsmaller_than__operator__yes__operator_selected, parent:Cyes__operator_selected) => new Csmaller_than__operator__yes__operator_selected(init, parent);
				case 'smaller than or equal to': return (init:Tsmaller_than_or_equal_to, parent:Cyes__operator_selected) => new Csmaller_than_or_equal_to(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__operator_selected['operator'], parent:Cyes__operator_selected) {
			super(data, parent, 'operator');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operator'`; }
	}
}
export namespace Cstate_group__type__columns {
	export class Dhas_filter extends StateGroup<
		{ name: 'no', node:Cno__has_filter__state_group, init:Tno__has_filter__state_group}|
		{ name: 'yes', node:Cyes__has_filter__state_group, init:Tyes__has_filter__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_filter__state_group, parent:Cstate_group__type__columns) => new Cno__has_filter__state_group(init, parent);
				case 'yes': return (init:Tyes__has_filter__state_group, parent:Cstate_group__type__columns) => new Cyes__has_filter__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__columns['has filter'], parent:Cstate_group__type__columns) {
			super(data, parent, 'has filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has filter'`; }
	}
}
export namespace Cyes__has_filter__state_group {
	export class Dfilter_enabled extends StateGroup<
		{ name: 'no', node:Cno__filter_enabled, init:Tno__filter_enabled}|
		{ name: 'yes', node:Cyes__filter_enabled, init:Tyes__filter_enabled}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__filter_enabled, parent:Cyes__has_filter__state_group) => new Cno__filter_enabled(init, parent);
				case 'yes': return (init:Tyes__filter_enabled, parent:Cyes__has_filter__state_group) => new Cyes__filter_enabled(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__state_group['filter enabled'], parent:Cyes__has_filter__state_group) {
			super(data, parent, 'filter enabled');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter enabled'`; }
	}
	export class Dhas_default_filter extends StateGroup<
		{ name: 'no', node:Cno__has_default_filter__yes__has_filter__state_group, init:Tno__has_default_filter__yes__has_filter__state_group}|
		{ name: 'yes', node:Cyes__has_default_filter__yes__has_filter__state_group, init:Tyes__has_default_filter__yes__has_filter__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_default_filter__yes__has_filter__state_group, parent:Cyes__has_filter__state_group) => new Cno__has_default_filter__yes__has_filter__state_group(init, parent);
				case 'yes': return (init:Tyes__has_default_filter__yes__has_filter__state_group, parent:Cyes__has_filter__state_group) => new Cyes__has_default_filter__yes__has_filter__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__state_group['has default filter'], parent:Cyes__has_filter__state_group) {
			super(data, parent, 'has default filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has default filter'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__yes, init:Tstates__yes},Cyes__has_filter__state_group> {
		protected initialize(parent:Cyes__has_filter__state_group, key:string, entry_init:Tstates__yes) { return new Cstates__yes(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tyes__has_filter__state_group['states'], parent:Cyes__has_filter__state_group) {
			super(data, parent);
		}
	}
}
export namespace Cstates__yes {
	export class Dis_selected extends StateGroup<
		{ name: 'no', node:Cno__is_selected__states__yes, init:Tno__is_selected__states__yes}|
		{ name: 'yes', node:Cyes__is_selected__states__yes, init:Tyes__is_selected__states__yes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_selected__states__yes, parent:Cstates__yes) => new Cno__is_selected__states__yes(init, parent);
				case 'yes': return (init:Tyes__is_selected__states__yes, parent:Cstates__yes) => new Cyes__is_selected__states__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__yes['is selected'], parent:Cstates__yes) {
			super(data, parent, 'is selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is selected'`; }
	}
}
export namespace Ctext__type__columns {
	export class Dfilter_on_current_id_path extends StateGroup<
		{ name: 'no', node:Cno__filter_on_current_id_path, init:Tno__filter_on_current_id_path}|
		{ name: 'yes', node:Cyes__filter_on_current_id_path, init:Tyes__filter_on_current_id_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__filter_on_current_id_path, parent:Ctext__type__columns) => new Cno__filter_on_current_id_path(init, parent);
				case 'yes': return (init:Tyes__filter_on_current_id_path, parent:Ctext__type__columns) => new Cyes__filter_on_current_id_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__columns['filter on current id path'], parent:Ctext__type__columns) {
			super(data, parent, 'filter on current id path');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter on current id path'`; }
	}
	export class Dhas_filter extends StateGroup<
		{ name: 'no', node:Cno__has_filter__text, init:Tno__has_filter__text}|
		{ name: 'yes', node:Cyes__has_filter__text, init:Tyes__has_filter__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_filter__text, parent:Ctext__type__columns) => new Cno__has_filter__text(init, parent);
				case 'yes': return (init:Tyes__has_filter__text, parent:Ctext__type__columns) => new Cyes__has_filter__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__columns['has filter'], parent:Ctext__type__columns) {
			super(data, parent, 'has filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has filter'`; }
	}
}
export namespace Cyes__has_filter__text {
	export class Dfilter_set extends StateGroup<
		{ name: 'no', node:Cno__filter_set__yes__has_filter__text, init:Tno__filter_set__yes__has_filter__text}|
		{ name: 'yes', node:Cyes__filter_set__yes__has_filter__text, init:Tyes__filter_set__yes__has_filter__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__filter_set__yes__has_filter__text, parent:Cyes__has_filter__text) => new Cno__filter_set__yes__has_filter__text(init, parent);
				case 'yes': return (init:Tyes__filter_set__yes__has_filter__text, parent:Cyes__has_filter__text) => new Cyes__filter_set__yes__has_filter__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__text['filter set'], parent:Cyes__has_filter__text) {
			super(data, parent, 'filter set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'filter set'`; }
	}
	export class Dhas_default_filter extends StateGroup<
		{ name: 'no', node:Cno__has_default_filter__yes__has_filter__text, init:Tno__has_default_filter__yes__has_filter__text}|
		{ name: 'yes', node:Cyes__has_default_filter__yes__has_filter__text, init:Tyes__has_default_filter__yes__has_filter__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_default_filter__yes__has_filter__text, parent:Cyes__has_filter__text) => new Cno__has_default_filter__yes__has_filter__text(init, parent);
				case 'yes': return (init:Tyes__has_default_filter__yes__has_filter__text, parent:Cyes__has_filter__text) => new Cyes__has_default_filter__yes__has_filter__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__text['has default filter'], parent:Cyes__has_filter__text) {
			super(data, parent, 'has default filter');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has default filter'`; }
	}
	export class Doperation extends StateGroup<
		{ name: 'contains', node:Ccontains, init:Tcontains}|
		{ name: 'ends with', node:Cends_with, init:Tends_with}|
		{ name: 'equals', node:Cequals, init:Tequals}|
		{ name: 'starts with', node:Cstarts_with, init:Tstarts_with}> {
		protected initializer(state: 'contains'|'ends with'|'equals'|'starts with') {
			switch (state) {
				case 'contains': return (init:Tcontains, parent:Cyes__has_filter__text) => new Ccontains(init, parent);
				case 'ends with': return (init:Tends_with, parent:Cyes__has_filter__text) => new Cends_with(init, parent);
				case 'equals': return (init:Tequals, parent:Cyes__has_filter__text) => new Cequals(init, parent);
				case 'starts with': return (init:Tstarts_with, parent:Cyes__has_filter__text) => new Cstarts_with(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_filter__text['operation'], parent:Cyes__has_filter__text) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Cyes__entries_are_editable {
	export class Dcan_create_entry extends StateGroup<
		{ name: 'no', node:Cno__can_create_entry__yes, init:Tno__can_create_entry__yes}|
		{ name: 'yes', node:Cyes__can_create_entry__yes, init:Tyes__can_create_entry__yes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_create_entry__yes, parent:Cyes__entries_are_editable) => new Cno__can_create_entry__yes(init, parent);
				case 'yes': return (init:Tyes__can_create_entry__yes, parent:Cyes__entries_are_editable) => new Cyes__can_create_entry__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__entries_are_editable['can create entry'], parent:Cyes__entries_are_editable) {
			super(data, parent, 'can create entry');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can create entry'`; }
	}
	export class Dis_importing extends StateGroup<
		{ name: 'no', node:Cno__is_importing, init:Tno__is_importing}|
		{ name: 'yes', node:Cyes__is_importing, init:Tyes__is_importing}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_importing, parent:Cyes__entries_are_editable) => new Cno__is_importing(init, parent);
				case 'yes': return (init:Tyes__is_importing, parent:Cyes__entries_are_editable) => new Cyes__is_importing(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__entries_are_editable['is importing'], parent:Cyes__entries_are_editable) {
			super(data, parent, 'is importing');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is importing'`; }
	}
}
export namespace Cyes__can_create_entry__yes {
	export class Din_ extends StateGroup<
		{ name: 'candidates', node:Ccandidates__in, init:Tcandidates__in}|
		{ name: 'query', node:Cquery__in, init:Tquery__in}> {
		protected initializer(state: 'candidates'|'query') {
			switch (state) {
				case 'candidates': return (init:Tcandidates__in, parent:Cyes__can_create_entry__yes) => new Ccandidates__in(init, parent);
				case 'query': return (init:Tquery__in, parent:Cyes__can_create_entry__yes) => new Cquery__in(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__can_create_entry__yes['in'], parent:Cyes__can_create_entry__yes) {
			super(data, parent, 'in');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'in'`; }
	}
}
export namespace Cquery__in {
	export class Dcollection extends Reference<client_state.Ccollection,string> {
		public readonly inferences:{
			valid_node: () => application.Cnode__application
		}

		constructor(data:string, $this:Cquery__in) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.query())
				.then(context => context?.inferences.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as client_state.Cproperty;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as client_state.Ccollection;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				valid_node: cache((detach:boolean) => {
					const client_state__query__entries_are_editable__yes__can_create_entry__yes__in__query__collection_nval = $this.properties.collection.ref;
					return resolve($this.properties.collection.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => client_state__query__entries_are_editable__yes__can_create_entry__yes__in__query__collection_nval)
								.then(context => context?.inferences.collection_type())
								.then(context => context?.properties.node).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.query())
								.then(context => context?.inferences.node_context())
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.result_node())
								.then(context => context?.component_root.output.application_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection'`; }
	}
}
export namespace Cfilters {
	export class Dactive extends StateGroup<
		{ name: 'no', node:Cno__active, init:Tno__active}|
		{ name: 'yes', node:Cyes__active, init:Tyes__active}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__active, parent:Cfilters) => new Cno__active(init, parent);
				case 'yes': return (init:Tyes__active, parent:Cfilters) => new Cyes__active(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfilters['active'], parent:Cfilters) {
			super(data, parent, 'active');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'active'`; }
	}
}
export namespace Cyes__has_columns {
	export class Dfirst extends Reference<client_state.Ccolumns,string> {

		constructor(data:string, $this:Cyes__has_columns) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.columns.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cyes__has_result {
	export class Damount_of_entries_visible extends AlanInteger {
		constructor(data:Tyes__has_result['amount of entries visible'], parent:Cyes__has_result) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'amount of entries visible'`; }
	}
	export class Dbackend_updates_are_available extends StateGroup<
		{ name: 'no', node:Cno__backend_updates_are_available, init:Tno__backend_updates_are_available}|
		{ name: 'yes', node:Cyes__backend_updates_are_available, init:Tyes__backend_updates_are_available}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__backend_updates_are_available, parent:Cyes__has_result) => new Cno__backend_updates_are_available(init, parent);
				case 'yes': return (init:Tyes__backend_updates_are_available, parent:Cyes__has_result) => new Cyes__backend_updates_are_available(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_result['backend updates are available'], parent:Cyes__has_result) {
			super(data, parent, 'backend updates are available');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'backend updates are available'`; }
	}
	export class Dentries extends AlanDictionary<{ node:Centries__yes__has_result, init:Tentries__yes__has_result},Cyes__has_result> {
		protected initialize(parent:Cyes__has_result, key:string, entry_init:Tentries__yes__has_result) { return new Centries__yes__has_result(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'entries'`; }
		constructor(data:Tyes__has_result['entries'], parent:Cyes__has_result) {
			super(data, parent);
		}
	}
	export class Dhas_more_entries_than_maximum extends StateGroup<
		{ name: 'no', node:Cno__has_more_entries_than_maximum, init:Tno__has_more_entries_than_maximum}|
		{ name: 'yes', node:Cyes__has_more_entries_than_maximum, init:Tyes__has_more_entries_than_maximum}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_more_entries_than_maximum, parent:Cyes__has_result) => new Cno__has_more_entries_than_maximum(init, parent);
				case 'yes': return (init:Tyes__has_more_entries_than_maximum, parent:Cyes__has_result) => new Cyes__has_more_entries_than_maximum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_result['has more entries than maximum'], parent:Cyes__has_result) {
			super(data, parent, 'has more entries than maximum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has more entries than maximum'`; }
	}
	export class Dis_empty extends StateGroup<
		{ name: 'no', node:Cno__is_empty__yes__has_result, init:Tno__is_empty__yes__has_result}|
		{ name: 'yes', node:Cyes__is_empty__yes__has_result, init:Tyes__is_empty__yes__has_result}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_empty__yes__has_result, parent:Cyes__has_result) => new Cno__is_empty__yes__has_result(init, parent);
				case 'yes': return (init:Tyes__is_empty__yes__has_result, parent:Cyes__has_result) => new Cyes__is_empty__yes__has_result(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_result['is empty'], parent:Cyes__has_result) {
			super(data, parent, 'is empty');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is empty'`; }
	}
	export class Dselected_entries extends StateGroup<
		{ name: 'all', node:Call, init:Tall}|
		{ name: 'none', node:Cnone__selected_entries, init:Tnone__selected_entries}|
		{ name: 'some', node:Csome, init:Tsome}> {
		protected initializer(state: 'all'|'none'|'some') {
			switch (state) {
				case 'all': return (init:Tall, parent:Cyes__has_result) => new Call(init, parent);
				case 'none': return (init:Tnone__selected_entries, parent:Cyes__has_result) => new Cnone__selected_entries(init, parent);
				case 'some': return (init:Tsome, parent:Cyes__has_result) => new Csome(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_result['selected entries'], parent:Cyes__has_result) {
			super(data, parent, 'selected entries');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'selected entries'`; }
	}
	export class Dtotal_amount_of_entries extends AlanInteger {
		constructor(data:Tyes__has_result['total amount of entries'], parent:Cyes__has_result) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'total amount of entries'`; }
	}
}
export namespace Centries__yes__has_result {
	export class Dcells extends AlanTopology<{ node:Ccells, init:Tcells},Centries__yes__has_result,'cell order'> {
		protected graph_iterator(graph:('cell order')):(node:Ccells) => Ccells {
			switch (graph) {
				case 'cell order': return (entry:Ccells) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as client_state.Cyes__has_successor__cells;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Centries__yes__has_result, key:string, entry_init:Tcells) { return new Ccells(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'cells'`; }
		constructor(data:Tentries__yes__has_result['cells'], parent:Centries__yes__has_result) {
			super(data, parent);
		}
	}
	export class Dentry_status extends Cquery_entry_status {
		constructor(data:Tentries__yes__has_result['entry status'], parent:Centries__yes__has_result) {
			super(data, parent, {
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.query_entry_status_position).result!),
				query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'entry status')
		}
	}
	export class Dhas_cells extends StateGroup<
		{ name: 'no', node:Cno__has_cells, init:Tno__has_cells}|
		{ name: 'yes', node:Cyes__has_cells, init:Tyes__has_cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_cells, parent:Centries__yes__has_result) => new Cno__has_cells(init, parent);
				case 'yes': return (init:Tyes__has_cells, parent:Centries__yes__has_result) => new Cyes__has_cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__has_result['has cells'], parent:Centries__yes__has_result) {
			super(data, parent, 'has cells');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has cells'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__entries, init:Tno__is_editable__entries}|
		{ name: 'yes', node:Cyes__is_editable__entries, init:Tyes__is_editable__entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__entries, parent:Centries__yes__has_result) => new Cno__is_editable__entries(init, parent);
				case 'yes': return (init:Tyes__is_editable__entries, parent:Centries__yes__has_result) => new Cyes__is_editable__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__has_result['is editable'], parent:Centries__yes__has_result) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dis_selected extends StateGroup<
		{ name: 'no', node:Cno__is_selected__entries, init:Tno__is_selected__entries}|
		{ name: 'yes', node:Cyes__is_selected__entries, init:Tyes__is_selected__entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_selected__entries, parent:Centries__yes__has_result) => new Cno__is_selected__entries(init, parent);
				case 'yes': return (init:Tyes__is_selected__entries, parent:Centries__yes__has_result) => new Cyes__is_selected__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__has_result['is selected'], parent:Centries__yes__has_result) {
			super(data, parent, 'is selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is selected'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__entries, init:Tno__is_valid__entries}|
		{ name: 'yes', node:Cyes__is_valid__entries, init:Tyes__is_valid__entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__entries, parent:Centries__yes__has_result) => new Cno__is_valid__entries(init, parent);
				case 'yes': return (init:Tyes__is_valid__entries, parent:Centries__yes__has_result) => new Cyes__is_valid__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__has_result['is valid'], parent:Centries__yes__has_result) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dstyle extends Cstyle {
		constructor(data:Tentries__yes__has_result['style'], parent:Centries__yes__has_result) {
			super(data, parent, 'style')
		}
	}
	export class Duse_for extends StateGroup<
		{ name: 'candidates', node:Ccandidates__use_for__entries, init:Tcandidates__use_for__entries}|
		{ name: 'query', node:Cquery__use_for__entries, init:Tquery__use_for__entries}> {
		protected initializer(state: 'candidates'|'query') {
			switch (state) {
				case 'candidates': return (init:Tcandidates__use_for__entries, parent:Centries__yes__has_result) => new Ccandidates__use_for__entries(init, parent);
				case 'query': return (init:Tquery__use_for__entries, parent:Centries__yes__has_result) => new Cquery__use_for__entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentries__yes__has_result['use for'], parent:Centries__yes__has_result) {
			super(data, parent, 'use for');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'use for'`; }
	}
}
export namespace Ccells {
	export class Dcolumn_type extends StateGroup<
		{ name: 'content', node:Ccontent__column_type__cells, init:Tcontent__column_type__cells}|
		{ name: 'id', node:Cid__column_type__cells, init:Tid__column_type__cells}> {
		protected initializer(state: 'content'|'id') {
			switch (state) {
				case 'content': return (init:Tcontent__column_type__cells, parent:Ccells) => new Ccontent__column_type__cells(init, parent);
				case 'id': return (init:Tid__column_type__cells, parent:Ccells) => new Cid__column_type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcells['column type'], parent:Ccells) {
			super(data, parent, 'column type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'column type'`; }
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor__cells, init:Tno__has_successor__cells}|
		{ name: 'yes', node:Cyes__has_successor__cells, init:Tyes__has_successor__cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor__cells, parent:Ccells) => new Cno__has_successor__cells(init, parent);
				case 'yes': return (init:Tyes__has_successor__cells, parent:Ccells) => new Cyes__has_successor__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcells['has successor'], parent:Ccells) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'file', node:Cfile__type__cells, init:Tfile__type__cells}|
		{ name: 'number', node:Cnumber__type__cells, init:Tnumber__type__cells}|
		{ name: 'state group', node:Cstate_group__type__cells, init:Tstate_group__type__cells}|
		{ name: 'text', node:Ctext__type__cells, init:Ttext__type__cells}|
		{ name: 'widget', node:Cwidget__type__cells, init:Twidget__type__cells}> {
		protected initializer(state: 'file'|'number'|'state group'|'text'|'widget') {
			switch (state) {
				case 'file': return (init:Tfile__type__cells, parent:Ccells) => new Cfile__type__cells(init, parent);
				case 'number': return (init:Tnumber__type__cells, parent:Ccells) => new Cnumber__type__cells(init, parent);
				case 'state group': return (init:Tstate_group__type__cells, parent:Ccells) => new Cstate_group__type__cells(init, parent);
				case 'text': return (init:Ttext__type__cells, parent:Ccells) => new Ctext__type__cells(init, parent);
				case 'widget': return (init:Twidget__type__cells, parent:Ccells) => new Cwidget__type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcells['type'], parent:Ccells) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_successor__cells {
	export class Dsuccessor extends Reference<client_state.Ccells,string> {

		constructor(data:string, $this:Cyes__has_successor__cells) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.cells.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Cfile__type__cells {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__file__type__cells, init:Tno__is_set__file__type__cells}|
		{ name: 'yes', node:Cyes__is_set__file__type__cells, init:Tyes__is_set__file__type__cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__file__type__cells, parent:Cfile__type__cells) => new Cno__is_set__file__type__cells(init, parent);
				case 'yes': return (init:Tyes__is_set__file__type__cells, parent:Cfile__type__cells) => new Cyes__is_set__file__type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__cells['is set'], parent:Cfile__type__cells) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
}
export namespace Cyes__is_set__file__type__cells {
}
export namespace Cnumber__type__cells {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__number__type__cells, init:Tno__is_set__number__type__cells}|
		{ name: 'yes', node:Cyes__is_set__number__type__cells, init:Tyes__is_set__number__type__cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__number__type__cells, parent:Cnumber__type__cells) => new Cno__is_set__number__type__cells(init, parent);
				case 'yes': return (init:Tyes__is_set__number__type__cells, parent:Cnumber__type__cells) => new Cyes__is_set__number__type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__cells['is set'], parent:Cnumber__type__cells) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dnumerical_type_properties extends Cnumerical_type_properties {
		constructor(data:Tnumber__type__cells['numerical type properties'], parent:Cnumber__type__cells) {
			super(data, parent, 'numerical type properties')
		}
	}
}
export namespace Cyes__is_set__number__type__cells {
	export class Dnumber extends AlanInteger {
		constructor(data:Tyes__is_set__number__type__cells['number'], parent:Cyes__is_set__number__type__cells) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cstate_group__type__cells {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__state_group__type__cells, init:Tno__is_set__state_group__type__cells}|
		{ name: 'yes', node:Cyes__is_set__state_group__type__cells, init:Tyes__is_set__state_group__type__cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__state_group__type__cells, parent:Cstate_group__type__cells) => new Cno__is_set__state_group__type__cells(init, parent);
				case 'yes': return (init:Tyes__is_set__state_group__type__cells, parent:Cstate_group__type__cells) => new Cyes__is_set__state_group__type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__cells['is set'], parent:Cstate_group__type__cells) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__state_group__type__cells, init:Tstates__state_group__type__cells},Cstate_group__type__cells> {
		protected initialize(parent:Cstate_group__type__cells, key:string, entry_init:Tstates__state_group__type__cells) { return new Cstates__state_group__type__cells(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__cells['states'], parent:Cstate_group__type__cells) {
			super(data, parent);
		}
	}
}
export namespace Cyes__is_set__state_group__type__cells {
	export class Dstyle extends Cstyle {
		constructor(data:Tyes__is_set__state_group__type__cells['style'], parent:Cyes__is_set__state_group__type__cells) {
			super(data, parent, 'style')
		}
	}
}
export namespace Cstates__state_group__type__cells {
	export class Dis_selected extends StateGroup<
		{ name: 'no', node:Cno__is_selected__states__state_group__type__cells, init:Tno__is_selected__states__state_group__type__cells}|
		{ name: 'yes', node:Cyes__is_selected__states__state_group__type__cells, init:Tyes__is_selected__states__state_group__type__cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_selected__states__state_group__type__cells, parent:Cstates__state_group__type__cells) => new Cno__is_selected__states__state_group__type__cells(init, parent);
				case 'yes': return (init:Tyes__is_selected__states__state_group__type__cells, parent:Cstates__state_group__type__cells) => new Cyes__is_selected__states__state_group__type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group__type__cells['is selected'], parent:Cstates__state_group__type__cells) {
			super(data, parent, 'is selected');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is selected'`; }
	}
}
export namespace Ctext__type__cells {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__text__type__cells, init:Tno__is_set__text__type__cells}|
		{ name: 'yes', node:Cyes__is_set__text__type__cells, init:Tyes__is_set__text__type__cells}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__text__type__cells, parent:Ctext__type__cells) => new Cno__is_set__text__type__cells(init, parent);
				case 'yes': return (init:Tyes__is_set__text__type__cells, parent:Ctext__type__cells) => new Cyes__is_set__text__type__cells(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__cells['is set'], parent:Ctext__type__cells) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
}
export namespace Cyes__is_set__text__type__cells {
}
export namespace Cyes__has_cells {
	export class Dfirst extends Reference<client_state.Ccells,string> {

		constructor(data:string, $this:Cyes__has_cells) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.cells.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cyes__is_selected__entries {
	export class Dselected_entry extends Reference<client_state.Cselected_entries,string> {

		constructor(data:string, $this:Cyes__is_selected__entries) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.selection())
				.then(context => {
					const entry = context?.properties.selected_entries.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'selected entry'`; }
	}
}
export namespace Cyes__has_more_entries_than_maximum {
	export class Dcan_show_all extends StateGroup<
		{ name: 'no', node:Cno__can_show_all, init:Tno__can_show_all}|
		{ name: 'yes', node:Cyes__can_show_all, init:Tyes__can_show_all}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_show_all, parent:Cyes__has_more_entries_than_maximum) => new Cno__can_show_all(init, parent);
				case 'yes': return (init:Tyes__can_show_all, parent:Cyes__has_more_entries_than_maximum) => new Cyes__can_show_all(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_more_entries_than_maximum['can show all'], parent:Cyes__has_more_entries_than_maximum) {
			super(data, parent, 'can show all');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can show all'`; }
	}
}
export namespace Cno__is_empty__yes__has_result {
	export class Dresult_size extends StateGroup<
		{ name: 'multiple', node:Cmultiple__result_size, init:Tmultiple__result_size}|
		{ name: 'one', node:Cone, init:Tone}> {
		protected initializer(state: 'multiple'|'one') {
			switch (state) {
				case 'multiple': return (init:Tmultiple__result_size, parent:Cno__is_empty__yes__has_result) => new Cmultiple__result_size(init, parent);
				case 'one': return (init:Tone, parent:Cno__is_empty__yes__has_result) => new Cone(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_empty__yes__has_result['result size'], parent:Cno__is_empty__yes__has_result) {
			super(data, parent, 'result size');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'result size'`; }
	}
}
export namespace Cyes__has_selected_entries {
	export class Dnumber_of_editable_selected_entries extends AlanInteger {
		constructor(data:Tyes__has_selected_entries['number of editable selected entries'], parent:Cyes__has_selected_entries) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number of editable selected entries'`; }
	}
	export class Dnumber_of_selected_entries extends AlanInteger {
		constructor(data:Tyes__has_selected_entries['number of selected entries'], parent:Cyes__has_selected_entries) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number of selected entries'`; }
	}
	export class Dselected_entries extends AlanDictionary<{ node:Cselected_entries, init:Tselected_entries},Cyes__has_selected_entries> {
		protected initialize(parent:Cyes__has_selected_entries, key:string, entry_init:Tselected_entries) { return new Cselected_entries(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'selected entries'`; }
		constructor(data:Tyes__has_selected_entries['selected entries'], parent:Cyes__has_selected_entries) {
			super(data, parent);
		}
	}
}
export namespace Cselected_entries {
	export class Dentry_status extends Cquery_entry_status {
		constructor(data:Tselected_entries['entry status'], parent:Cselected_entries) {
			super(data, parent, {
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.query_entry_status_position).result!),
				query: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'entry status')
		}
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__selected_entries, init:Tno__is_editable__selected_entries}|
		{ name: 'yes', node:Cyes__is_editable__selected_entries, init:Tyes__is_editable__selected_entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__selected_entries, parent:Cselected_entries) => new Cno__is_editable__selected_entries(init, parent);
				case 'yes': return (init:Tyes__is_editable__selected_entries, parent:Cselected_entries) => new Cyes__is_editable__selected_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tselected_entries['is editable'], parent:Cselected_entries) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
}
export namespace Cno__is_busy_querying {
	export class Drequest_status extends StateGroup<
		{ name: 'failure', node:Cfailure__request_status, init:Tfailure__request_status}|
		{ name: 'success', node:Csuccess__request_status, init:Tsuccess__request_status}> {
		protected initializer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__request_status, parent:Cno__is_busy_querying) => new Cfailure__request_status(init, parent);
				case 'success': return (init:Tsuccess__request_status, parent:Cno__is_busy_querying) => new Csuccess__request_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__is_busy_querying['request status'], parent:Cno__is_busy_querying) {
			super(data, parent, 'request status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'request status'`; }
	}
}
export namespace Cyes__is_busy_querying {
	export class Drequery_needed extends StateGroup<
		{ name: 'no', node:Cno__requery_needed, init:Tno__requery_needed}|
		{ name: 'yes', node:Cyes__requery_needed, init:Tyes__requery_needed}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__requery_needed, parent:Cyes__is_busy_querying) => new Cno__requery_needed(init, parent);
				case 'yes': return (init:Tyes__requery_needed, parent:Cyes__is_busy_querying) => new Cyes__requery_needed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__is_busy_querying['requery needed'], parent:Cyes__is_busy_querying) {
			super(data, parent, 'requery needed');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'requery needed'`; }
	}
}
export namespace Cquery_entry_status {
	export class Dinitialized extends StateGroup<
		{ name: 'no', node:Cno__initialized__query_entry_status, init:Tno__initialized__query_entry_status}|
		{ name: 'unknown', node:Cunknown__initialized, init:Tunknown__initialized}|
		{ name: 'yes', node:Cyes__initialized__query_entry_status, init:Tyes__initialized__query_entry_status}> {
		protected initializer(state: 'no'|'unknown'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__initialized__query_entry_status, parent:Cquery_entry_status) => new Cno__initialized__query_entry_status(init, parent);
				case 'unknown': return (init:Tunknown__initialized, parent:Cquery_entry_status) => new Cunknown__initialized(init, parent);
				case 'yes': return (init:Tyes__initialized__query_entry_status, parent:Cquery_entry_status) => new Cyes__initialized__query_entry_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tquery_entry_status['initialized'], parent:Cquery_entry_status) {
			super(data, parent, 'initialized');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initialized'`; }
	}
}
export namespace Cno__initialized__query_entry_status {
	export class Dreferenced_node extends Cid_path {
		constructor(data:Tno__initialized__query_entry_status['referenced node'], parent:Cno__initialized__query_entry_status) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!)
			}, 'referenced node')
		}
	}
}
export namespace Cyes__initialized__query_entry_status {
	export class Dreferenced_node extends Cclient_id_path {
		constructor(data:Tyes__initialized__query_entry_status['referenced node'], parent:Cyes__initialized__query_entry_status) {
			super(data, parent, {
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.id_path_location).result!),
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!)
			}, 'referenced node')
		}
	}
}
export namespace Creport_parameters {
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__report_parameters, init:Tno__is_valid__report_parameters}|
		{ name: 'yes', node:Cyes__is_valid__report_parameters, init:Tyes__is_valid__report_parameters}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__report_parameters, parent:Creport_parameters) => new Cno__is_valid__report_parameters(init, parent);
				case 'yes': return (init:Tyes__is_valid__report_parameters, parent:Creport_parameters) => new Cyes__is_valid__report_parameters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treport_parameters['is valid'], parent:Creport_parameters) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dparameters extends AlanDictionary<{ node:Cparameters, init:Tparameters},Creport_parameters> {
		protected initialize(parent:Creport_parameters, key:string, entry_init:Tparameters) { return new Cparameters(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'parameters'`; }
		constructor(data:Treport_parameters['parameters'], parent:Creport_parameters) {
			super(data, parent);
		}
	}
}
export namespace Cparameters {
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__parameters, init:Tnumber__type__parameters}|
		{ name: 'text', node:Ctext__type__parameters, init:Ttext__type__parameters}> {
		protected initializer(state: 'number'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__parameters, parent:Cparameters) => new Cnumber__type__parameters(init, parent);
				case 'text': return (init:Ttext__type__parameters, parent:Cparameters) => new Ctext__type__parameters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tparameters['type'], parent:Cparameters) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__parameters {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__number__type__parameters, init:Tno__is_set__number__type__parameters}|
		{ name: 'yes', node:Cyes__is_set__number__type__parameters, init:Tyes__is_set__number__type__parameters}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__number__type__parameters, parent:Cnumber__type__parameters) => new Cno__is_set__number__type__parameters(init, parent);
				case 'yes': return (init:Tyes__is_set__number__type__parameters, parent:Cnumber__type__parameters) => new Cyes__is_set__number__type__parameters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__parameters['is set'], parent:Cnumber__type__parameters) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__number__type__parameters, init:Tno__is_valid__number__type__parameters}|
		{ name: 'yes', node:Cyes__is_valid__number__type__parameters, init:Tyes__is_valid__number__type__parameters}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__number__type__parameters, parent:Cnumber__type__parameters) => new Cno__is_valid__number__type__parameters(init, parent);
				case 'yes': return (init:Tyes__is_valid__number__type__parameters, parent:Cnumber__type__parameters) => new Cyes__is_valid__number__type__parameters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__parameters['is valid'], parent:Cnumber__type__parameters) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dnumerical_type_properties extends Cnumerical_type_properties {
		constructor(data:Tnumber__type__parameters['numerical type properties'], parent:Cnumber__type__parameters) {
			super(data, parent, 'numerical type properties')
		}
	}
}
export namespace Cyes__is_set__number__type__parameters {
	export class Dvalue extends AlanInteger {
		constructor(data:Tyes__is_set__number__type__parameters['value'], parent:Cyes__is_set__number__type__parameters) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Ctext__type__parameters {
	export class Dis_set extends StateGroup<
		{ name: 'no', node:Cno__is_set__text__type__parameters, init:Tno__is_set__text__type__parameters}|
		{ name: 'yes', node:Cyes__is_set__text__type__parameters, init:Tyes__is_set__text__type__parameters}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_set__text__type__parameters, parent:Ctext__type__parameters) => new Cno__is_set__text__type__parameters(init, parent);
				case 'yes': return (init:Tyes__is_set__text__type__parameters, parent:Ctext__type__parameters) => new Cyes__is_set__text__type__parameters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__parameters['is set'], parent:Ctext__type__parameters) {
			super(data, parent, 'is set');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is set'`; }
	}
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__text__type__parameters, init:Tno__is_valid__text__type__parameters}|
		{ name: 'yes', node:Cyes__is_valid__text__type__parameters, init:Tyes__is_valid__text__type__parameters}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__text__type__parameters, parent:Ctext__type__parameters) => new Cno__is_valid__text__type__parameters(init, parent);
				case 'yes': return (init:Tyes__is_valid__text__type__parameters, parent:Ctext__type__parameters) => new Cyes__is_valid__text__type__parameters(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__parameters['is valid'], parent:Ctext__type__parameters) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
}
export namespace Cyes__is_set__text__type__parameters {
}
export namespace Cresolve_status {
	export class Dstatus extends StateGroup<
		{ name: 'failed to resolve', node:Cfailed_to_resolve, init:Tfailed_to_resolve}|
		{ name: 'is busy resolving', node:Cis_busy_resolving, init:Tis_busy_resolving}|
		{ name: 'resolved', node:Cresolved__status, init:Tresolved__status}|
		{ name: 'unresolved', node:Cunresolved__status, init:Tunresolved__status}> {
		protected initializer(state: 'failed to resolve'|'is busy resolving'|'resolved'|'unresolved') {
			switch (state) {
				case 'failed to resolve': return (init:Tfailed_to_resolve, parent:Cresolve_status) => new Cfailed_to_resolve(init, parent);
				case 'is busy resolving': return (init:Tis_busy_resolving, parent:Cresolve_status) => new Cis_busy_resolving(init, parent);
				case 'resolved': return (init:Tresolved__status, parent:Cresolve_status) => new Cresolved__status(init, parent);
				case 'unresolved': return (init:Tunresolved__status, parent:Cresolve_status) => new Cunresolved__status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tresolve_status['status'], parent:Cresolve_status) {
			super(data, parent, 'status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'status'`; }
	}
	export class Dvalidation_result extends StateGroup<
		{ name: 'failure', node:Cfailure__validation_result__resolve_status, init:Tfailure__validation_result__resolve_status}|
		{ name: 'success', node:Csuccess__validation_result__resolve_status, init:Tsuccess__validation_result__resolve_status}> {
		protected initializer(state: 'failure'|'success') {
			switch (state) {
				case 'failure': return (init:Tfailure__validation_result__resolve_status, parent:Cresolve_status) => new Cfailure__validation_result__resolve_status(init, parent);
				case 'success': return (init:Tsuccess__validation_result__resolve_status, parent:Cresolve_status) => new Csuccess__validation_result__resolve_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tresolve_status['validation result'], parent:Cresolve_status) {
			super(data, parent, 'validation result');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'validation result'`; }
	}
}
export namespace Cstyle {
	export class Dinitialized extends StateGroup<
		{ name: 'no', node:Cno__initialized__style, init:Tno__initialized__style}|
		{ name: 'yes', node:Cyes__initialized__style, init:Tyes__initialized__style}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__initialized__style, parent:Cstyle) => new Cno__initialized__style(init, parent);
				case 'yes': return (init:Tyes__initialized__style, parent:Cstyle) => new Cyes__initialized__style(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstyle['initialized'], parent:Cstyle) {
			super(data, parent, 'initialized');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'initialized'`; }
	}
}
export namespace Cyes__initialized__style {
	export class Dhas_style extends StateGroup<
		{ name: 'no', node:Cno__has_style, init:Tno__has_style}|
		{ name: 'yes', node:Cyes__has_style, init:Tyes__has_style}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_style, parent:Cyes__initialized__style) => new Cno__has_style(init, parent);
				case 'yes': return (init:Tyes__has_style, parent:Cyes__initialized__style) => new Cyes__has_style(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__initialized__style['has style'], parent:Cyes__initialized__style) {
			super(data, parent, 'has style');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has style'`; }
	}
}
export namespace Cyes__has_style {
	export class Dtype extends StateGroup<
		{ name: 'color', node:Ccolor, init:Tcolor}|
		{ name: 'fixed', node:Cfixed, init:Tfixed}> {
		protected initializer(state: 'color'|'fixed') {
			switch (state) {
				case 'color': return (init:Tcolor, parent:Cyes__has_style) => new Ccolor(init, parent);
				case 'fixed': return (init:Tfixed, parent:Cyes__has_style) => new Cfixed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_style['type'], parent:Cyes__has_style) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccolor {
}
export namespace Cfixed {
	export class Dstyle extends StateGroup<
		{ name: 'accent', node:Caccent, init:Taccent}|
		{ name: 'background', node:Cbackground, init:Tbackground}|
		{ name: 'brand', node:Cbrand, init:Tbrand}|
		{ name: 'error', node:Cerror__style, init:Terror__style}|
		{ name: 'foreground', node:Cforeground, init:Tforeground}|
		{ name: 'link', node:Clink, init:Tlink}|
		{ name: 'success', node:Csuccess__style, init:Tsuccess__style}|
		{ name: 'warning', node:Cwarning__style, init:Twarning__style}> {
		protected initializer(state: 'accent'|'background'|'brand'|'error'|'foreground'|'link'|'success'|'warning') {
			switch (state) {
				case 'accent': return (init:Taccent, parent:Cfixed) => new Caccent(init, parent);
				case 'background': return (init:Tbackground, parent:Cfixed) => new Cbackground(init, parent);
				case 'brand': return (init:Tbrand, parent:Cfixed) => new Cbrand(init, parent);
				case 'error': return (init:Terror__style, parent:Cfixed) => new Cerror__style(init, parent);
				case 'foreground': return (init:Tforeground, parent:Cfixed) => new Cforeground(init, parent);
				case 'link': return (init:Tlink, parent:Cfixed) => new Clink(init, parent);
				case 'success': return (init:Tsuccess__style, parent:Cfixed) => new Csuccess__style(init, parent);
				case 'warning': return (init:Twarning__style, parent:Cfixed) => new Cwarning__style(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfixed['style'], parent:Cfixed) {
			super(data, parent, 'style');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'style'`; }
	}
}
export namespace Cwhere_rules {
	export class Drules extends AlanDictionary<{ node:Crules, init:Trules},Cwhere_rules> {
		protected initialize(parent:Cwhere_rules, key:string, entry_init:Trules) { return new Crules(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rules'`; }
		constructor(data:Twhere_rules['rules'], parent:Cwhere_rules) {
			super(data, parent);
		}
	}
}
export namespace Crules {
	export class Dis_valid extends StateGroup<
		{ name: 'no', node:Cno__is_valid__rules, init:Tno__is_valid__rules}|
		{ name: 'yes', node:Cyes__is_valid__rules, init:Tyes__is_valid__rules}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_valid__rules, parent:Crules) => new Cno__is_valid__rules(init, parent);
				case 'yes': return (init:Tyes__is_valid__rules, parent:Crules) => new Cyes__is_valid__rules(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trules['is valid'], parent:Crules) {
			super(data, parent, 'is valid');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is valid'`; }
	}
	export class Dresolve_status extends Cresolve_status {
		constructor(data:Trules['resolve status'], parent:Crules) {
			super(data, parent, {
				resolvable: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.resolvable).result!)
			}, 'resolve status')
		}
	}
}
export namespace Cclient_state {
	export class Dalt_key_pressed extends StateGroup<
		{ name: 'no', node:Cno__alt_key_pressed, init:Tno__alt_key_pressed}|
		{ name: 'yes', node:Cyes__alt_key_pressed, init:Tyes__alt_key_pressed}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__alt_key_pressed, parent:Cclient_state) => new Cno__alt_key_pressed(init, parent);
				case 'yes': return (init:Tyes__alt_key_pressed, parent:Cclient_state) => new Cyes__alt_key_pressed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['alt key pressed'], parent:Cclient_state) {
			super(data, parent, 'alt key pressed');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'alt key pressed'`; }
	}
	export class Dbrowser_family extends StateGroup<
		{ name: 'Chromium', node:CChromium, init:TChromium}|
		{ name: 'Edge', node:CEdge, init:TEdge}|
		{ name: 'Safari', node:CSafari, init:TSafari}|
		{ name: 'undetermined', node:Cundetermined__browser_family, init:Tundetermined__browser_family}> {
		protected initializer(state: 'Chromium'|'Edge'|'Safari'|'undetermined') {
			switch (state) {
				case 'Chromium': return (init:TChromium, parent:Cclient_state) => new CChromium(init, parent);
				case 'Edge': return (init:TEdge, parent:Cclient_state) => new CEdge(init, parent);
				case 'Safari': return (init:TSafari, parent:Cclient_state) => new CSafari(init, parent);
				case 'undetermined': return (init:Tundetermined__browser_family, parent:Cclient_state) => new Cundetermined__browser_family(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['browser family'], parent:Cclient_state) {
			super(data, parent, 'browser family');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'browser family'`; }
	}
	export class Dctrl_key_pressed extends StateGroup<
		{ name: 'no', node:Cno__ctrl_key_pressed, init:Tno__ctrl_key_pressed}|
		{ name: 'yes', node:Cyes__ctrl_key_pressed, init:Tyes__ctrl_key_pressed}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__ctrl_key_pressed, parent:Cclient_state) => new Cno__ctrl_key_pressed(init, parent);
				case 'yes': return (init:Tyes__ctrl_key_pressed, parent:Cclient_state) => new Cyes__ctrl_key_pressed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['ctrl key pressed'], parent:Cclient_state) {
			super(data, parent, 'ctrl key pressed');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ctrl key pressed'`; }
	}
	export class Dhas_landing_page extends StateGroup<
		{ name: 'no', node:Cno__has_landing_page, init:Tno__has_landing_page}|
		{ name: 'yes', node:Cyes__has_landing_page, init:Tyes__has_landing_page}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_landing_page, parent:Cclient_state) => new Cno__has_landing_page(init, parent);
				case 'yes': return (init:Tyes__has_landing_page, parent:Cclient_state) => new Cyes__has_landing_page(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['has landing page'], parent:Cclient_state) {
			super(data, parent, 'has landing page');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has landing page'`; }
	}
	export class Dis_editable extends StateGroup<
		{ name: 'no', node:Cno__is_editable__client_state, init:Tno__is_editable__client_state}|
		{ name: 'yes', node:Cyes__is_editable__client_state, init:Tyes__is_editable__client_state}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_editable__client_state, parent:Cclient_state) => new Cno__is_editable__client_state(init, parent);
				case 'yes': return (init:Tyes__is_editable__client_state, parent:Cclient_state) => new Cyes__is_editable__client_state(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['is editable'], parent:Cclient_state) {
			super(data, parent, 'is editable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is editable'`; }
	}
	export class Dlanguages extends AlanDictionary<{ node:Clanguages, init:Tlanguages},Cclient_state> {
		protected initialize(parent:Cclient_state, key:string, entry_init:Tlanguages) { return new Clanguages(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'languages'`; }
		constructor(data:Tclient_state['languages'], parent:Cclient_state) {
			super(data, parent);
		}
	}
	export class Dmessages extends AlanDictionary<{ node:Cmessages, init:Tmessages},Cclient_state> {
		protected initialize(parent:Cclient_state, key:string, entry_init:Tmessages) { return new Cmessages(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'messages'`; }
		constructor(data:Tclient_state['messages'], parent:Cclient_state) {
			super(data, parent);
		}
	}
	export class Dmeta_key_pressed extends StateGroup<
		{ name: 'no', node:Cno__meta_key_pressed, init:Tno__meta_key_pressed}|
		{ name: 'yes', node:Cyes__meta_key_pressed, init:Tyes__meta_key_pressed}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__meta_key_pressed, parent:Cclient_state) => new Cno__meta_key_pressed(init, parent);
				case 'yes': return (init:Tyes__meta_key_pressed, parent:Cclient_state) => new Cyes__meta_key_pressed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['meta key pressed'], parent:Cclient_state) {
			super(data, parent, 'meta key pressed');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'meta key pressed'`; }
	}
	export class Dmodules extends AlanDictionary<{ node:Cmodules, init:Tmodules},Cclient_state> {
		protected initialize(parent:Cclient_state, key:string, entry_init:Tmodules) { return new Cmodules(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'modules'`; }
		constructor(data:Tclient_state['modules'], parent:Cclient_state) {
			super(data, parent);
		}
	}
	export class Dnetwork_connection_status extends StateGroup<
		{ name: 'alive', node:Calive__network_connection_status, init:Talive__network_connection_status}|
		{ name: 'dead', node:Cdead__network_connection_status, init:Tdead__network_connection_status}> {
		protected initializer(state: 'alive'|'dead') {
			switch (state) {
				case 'alive': return (init:Talive__network_connection_status, parent:Cclient_state) => new Calive__network_connection_status(init, parent);
				case 'dead': return (init:Tdead__network_connection_status, parent:Cclient_state) => new Cdead__network_connection_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['network connection status'], parent:Cclient_state) {
			super(data, parent, 'network connection status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'network connection status'`; }
	}
	export class Dplatform_family extends StateGroup<
		{ name: 'Android', node:CAndroid, init:TAndroid}|
		{ name: 'Linux', node:CLinux, init:TLinux}|
		{ name: 'Windows', node:CWindows, init:TWindows}|
		{ name: 'iOS', node:CiOS, init:TiOS}|
		{ name: 'macOS', node:CmacOS, init:TmacOS}|
		{ name: 'undetermined', node:Cundetermined__platform_family, init:Tundetermined__platform_family}> {
		protected initializer(state: 'Android'|'Linux'|'Windows'|'iOS'|'macOS'|'undetermined') {
			switch (state) {
				case 'Android': return (init:TAndroid, parent:Cclient_state) => new CAndroid(init, parent);
				case 'Linux': return (init:TLinux, parent:Cclient_state) => new CLinux(init, parent);
				case 'Windows': return (init:TWindows, parent:Cclient_state) => new CWindows(init, parent);
				case 'iOS': return (init:TiOS, parent:Cclient_state) => new CiOS(init, parent);
				case 'macOS': return (init:TmacOS, parent:Cclient_state) => new CmacOS(init, parent);
				case 'undetermined': return (init:Tundetermined__platform_family, parent:Cclient_state) => new Cundetermined__platform_family(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['platform family'], parent:Cclient_state) {
			super(data, parent, 'platform family');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'platform family'`; }
	}
	export class Dreport_types extends AlanDictionary<{ node:Creport_types__client_state, init:Treport_types__client_state},Cclient_state> {
		protected initialize(parent:Cclient_state, key:string) { return new Creport_types__client_state(key, {}, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'report types'`; }
		constructor(data:Tclient_state['report types'], parent:Cclient_state) {
			super(data, parent);
		}
	}
	export class Dreports extends AlanDictionary<{ node:Creports, init:Treports},Cclient_state> {
		protected initialize(parent:Cclient_state, key:string, entry_init:Treports) { return new Creports(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'reports'`; }
		constructor(data:Tclient_state['reports'], parent:Cclient_state) {
			super(data, parent);
		}
	}
	export class Droot extends Cnode {
		constructor(data:Tclient_state['root'], parent:Cclient_state) {
			super(data, parent, {
				entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entity).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(() => client_state.Clocation.Premote).result!),
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!),
				position: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.entity)
					.then(context => context?.definitions.node_position).result!)
			}, 'root')
		}
	}
	export class Dselected_language extends Reference<client_state.Clanguages,string> {

		constructor(data:string, $this:Cclient_state) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => {
					const entry = context?.properties.languages.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'selected language'`; }
	}
	export class Dserver_connection_status extends StateGroup<
		{ name: 'alive', node:Calive__server_connection_status, init:Talive__server_connection_status}|
		{ name: 'dead', node:Cdead__server_connection_status, init:Tdead__server_connection_status}> {
		protected initializer(state: 'alive'|'dead') {
			switch (state) {
				case 'alive': return (init:Talive__server_connection_status, parent:Cclient_state) => new Calive__server_connection_status(init, parent);
				case 'dead': return (init:Tdead__server_connection_status, parent:Cclient_state) => new Cdead__server_connection_status(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['server connection status'], parent:Cclient_state) {
			super(data, parent, 'server connection status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'server connection status'`; }
	}
	export class Dshift_key_pressed extends StateGroup<
		{ name: 'no', node:Cno__shift_key_pressed, init:Tno__shift_key_pressed}|
		{ name: 'yes', node:Cyes__shift_key_pressed, init:Tyes__shift_key_pressed}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__shift_key_pressed, parent:Cclient_state) => new Cno__shift_key_pressed(init, parent);
				case 'yes': return (init:Tyes__shift_key_pressed, parent:Cclient_state) => new Cyes__shift_key_pressed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['shift key pressed'], parent:Cclient_state) {
			super(data, parent, 'shift key pressed');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'shift key pressed'`; }
	}
	export class Dtoggle_key_pressed extends StateGroup<
		{ name: 'no', node:Cno__toggle_key_pressed, init:Tno__toggle_key_pressed}|
		{ name: 'yes', node:Cyes__toggle_key_pressed, init:Tyes__toggle_key_pressed}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__toggle_key_pressed, parent:Cclient_state) => new Cno__toggle_key_pressed(init, parent);
				case 'yes': return (init:Tyes__toggle_key_pressed, parent:Cclient_state) => new Cyes__toggle_key_pressed(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tclient_state['toggle key pressed'], parent:Cclient_state) {
			super(data, parent, 'toggle key pressed');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'toggle key pressed'`; }
	}
	export class Dwindows extends AlanDictionary<{ node:Cwindows, init:Twindows},Cclient_state> {
		protected initialize(parent:Cclient_state, key:string, entry_init:Twindows) { return new Cwindows(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'windows'`; }
		constructor(data:Tclient_state['windows'], parent:Cclient_state) {
			super(data, parent);
		}
	}
}
export namespace Clanguages {
}
export namespace Cmessages {
	export class Dhas_body extends StateGroup<
		{ name: 'no', node:Cno__has_body, init:Tno__has_body}|
		{ name: 'yes', node:Cyes__has_body, init:Tyes__has_body}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_body, parent:Cmessages) => new Cno__has_body(init, parent);
				case 'yes': return (init:Tyes__has_body, parent:Cmessages) => new Cyes__has_body(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmessages['has body'], parent:Cmessages) {
			super(data, parent, 'has body');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has body'`; }
	}
	export class Dtype extends StateGroup<
		{ name: 'error', node:Cerror__type, init:Terror__type}|
		{ name: 'success', node:Csuccess__type, init:Tsuccess__type}|
		{ name: 'waiting', node:Cwaiting, init:Twaiting}|
		{ name: 'warning', node:Cwarning__type, init:Twarning__type}> {
		protected initializer(state: 'error'|'success'|'waiting'|'warning') {
			switch (state) {
				case 'error': return (init:Terror__type, parent:Cmessages) => new Cerror__type(init, parent);
				case 'success': return (init:Tsuccess__type, parent:Cmessages) => new Csuccess__type(init, parent);
				case 'waiting': return (init:Twaiting, parent:Cmessages) => new Cwaiting(init, parent);
				case 'warning': return (init:Twarning__type, parent:Cmessages) => new Cwarning__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmessages['type'], parent:Cmessages) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cyes__has_body {
}
export namespace Cmodules {
	export class Dhas_logo extends StateGroup<
		{ name: 'no', node:Cno__has_logo, init:Tno__has_logo}|
		{ name: 'yes', node:Cyes__has_logo, init:Tyes__has_logo}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_logo, parent:Cmodules) => new Cno__has_logo(init, parent);
				case 'yes': return (init:Tyes__has_logo, parent:Cmodules) => new Cyes__has_logo(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmodules['has logo'], parent:Cmodules) {
			super(data, parent, 'has logo');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has logo'`; }
	}
}
export namespace Cyes__has_logo {
}
export namespace Creports {
	export class Ddefinition extends StateGroup<
		{ name: 'known', node:Cknown, init:Tknown}|
		{ name: 'unknown', node:Cunknown__definition, init:Tunknown__definition}> {
		protected initializer(state: 'known'|'unknown') {
			switch (state) {
				case 'known': return (init:Tknown, parent:Creports) => new Cknown(init, parent);
				case 'unknown': return (init:Tunknown__definition, parent:Creports) => new Cunknown__definition(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treports['definition'], parent:Creports) {
			super(data, parent, 'definition');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'definition'`; }
	}
	export class Dpermissions extends StateGroup<
		{ name: 'read only', node:Cread_only, init:Tread_only}|
		{ name: 'writable', node:Cwritable, init:Twritable}> {
		protected initializer(state: 'read only'|'writable') {
			switch (state) {
				case 'read only': return (init:Tread_only, parent:Creports) => new Cread_only(init, parent);
				case 'writable': return (init:Twritable, parent:Creports) => new Cwritable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Treports['permissions'], parent:Creports) {
			super(data, parent, 'permissions');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'permissions'`; }
	}
}
export namespace Cknown {
	export class Dof_type extends Reference<parameters.Cparameters,string> {

		constructor(data:string, $this:Cknown) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.reports[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'of type'`; }
	}
}
export namespace Csession {
	export class Dstatus extends StateGroup<
		{ name: 'authenticated', node:Cauthenticated, init:Tauthenticated}|
		{ name: 'not authenticated', node:Cnot_authenticated, init:Tnot_authenticated}> {
		protected initializer(state: 'authenticated'|'not authenticated') {
			switch (state) {
				case 'authenticated': return (init:Tauthenticated, parent:Csession) => new Cauthenticated(init, parent);
				case 'not authenticated': return (init:Tnot_authenticated, parent:Csession) => new Cnot_authenticated(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tsession['status'], parent:Csession) {
			super(data, parent, 'status');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'status'`; }
	}
}
export namespace Cauthenticated {
	export class Danonymous_session extends StateGroup<
		{ name: 'no', node:Cno__anonymous_session, init:Tno__anonymous_session}|
		{ name: 'yes', node:Cyes__anonymous_session, init:Tyes__anonymous_session}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__anonymous_session, parent:Cauthenticated) => new Cno__anonymous_session(init, parent);
				case 'yes': return (init:Tyes__anonymous_session, parent:Cauthenticated) => new Cyes__anonymous_session(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tauthenticated['anonymous session'], parent:Cauthenticated) {
			super(data, parent, 'anonymous session');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'anonymous session'`; }
	}
}
export namespace Cno__anonymous_session {
	export class Duser_found extends StateGroup<
		{ name: 'no', node:Cno__user_found, init:Tno__user_found}|
		{ name: 'yes', node:Cyes__user_found, init:Tyes__user_found}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__user_found, parent:Cno__anonymous_session) => new Cno__user_found(init, parent);
				case 'yes': return (init:Tyes__user_found, parent:Cno__anonymous_session) => new Cyes__user_found(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__anonymous_session['user found'], parent:Cno__anonymous_session) {
			super(data, parent, 'user found');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'user found'`; }
	}
}
export namespace Cwindows {
	export class Dhas_active_view extends StateGroup<
		{ name: 'no', node:Cno__has_active_view, init:Tno__has_active_view}|
		{ name: 'yes', node:Cyes__has_active_view, init:Tyes__has_active_view}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_active_view, parent:Cwindows) => new Cno__has_active_view(init, parent);
				case 'yes': return (init:Tyes__has_active_view, parent:Cwindows) => new Cyes__has_active_view(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Twindows['has active view'], parent:Cwindows) {
			super(data, parent, 'has active view');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has active view'`; }
	}
	export class Dopen_views extends AlanDictionary<{ node:Copen_views, init:Topen_views},Cwindows> {
		protected initialize(parent:Cwindows, key:string, entry_init:Topen_views) { return new Copen_views(key, entry_init, parent); }
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'open views'`; }
		constructor(data:Twindows['open views'], parent:Cwindows) {
			super(data, parent);
		}
	}
}
export namespace Cyes__has_active_view {
	export class Dview extends Reference<client_state.Copen_views,string> {
		public readonly inferences:{
			view: () => client_state.Cyes__is_active_view
		}

		constructor(data:string, $this:Cyes__has_active_view) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.open_views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				view: cache((detach:boolean) => {
					const client_state__windows__has_active_view__yes__view_nval = $this.properties.view.ref;
					return resolve($this.properties.view.ref)
						.then(context => client_state__windows__has_active_view__yes__view_nval)
						.then(context => {
							if (context?.properties.is_active_view.state.name === 'yes') {
								return resolve(context.properties.is_active_view.state.node as client_state.Cyes__is_active_view).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view'`; }
	}
}
export namespace Copen_views {
	export class Daction_active extends StateGroup<
		{ name: 'no', node:Cno__action_active, init:Tno__action_active}|
		{ name: 'yes', node:Cyes__action_active, init:Tyes__action_active}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__action_active, parent:Copen_views) => new Cno__action_active(init, parent);
				case 'yes': return (init:Tyes__action_active, parent:Copen_views) => new Cyes__action_active(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Topen_views['action active'], parent:Copen_views) {
			super(data, parent, 'action active');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'action active'`; }
	}
	export class Dis_active_view extends StateGroup<
		{ name: 'no', node:Cno__is_active_view, init:Tno__is_active_view}|
		{ name: 'yes', node:Cyes__is_active_view, init:Tyes__is_active_view}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_active_view, parent:Copen_views) => new Cno__is_active_view(init, parent);
				case 'yes': return (init:Tyes__is_active_view, parent:Copen_views) => new Cyes__is_active_view(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Topen_views['is active view'], parent:Copen_views) {
			super(data, parent, 'is active view');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is active view'`; }
	}
	export class Dis_entry_view extends StateGroup<
		{ name: 'no', node:Cno__is_entry_view, init:Tno__is_entry_view}|
		{ name: 'yes', node:Cyes__is_entry_view, init:Tyes__is_entry_view}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__is_entry_view, parent:Copen_views) => new Cno__is_entry_view(init, parent);
				case 'yes': return (init:Tyes__is_entry_view, parent:Copen_views) => new Cyes__is_entry_view(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Topen_views['is entry view'], parent:Copen_views) {
			super(data, parent, 'is entry view');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'is entry view'`; }
	}
	export class Dsubscribe extends StateGroup<
		{ name: 'no', node:Cno__subscribe, init:Tno__subscribe}|
		{ name: 'yes', node:Cyes__subscribe, init:Tyes__subscribe}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__subscribe, parent:Copen_views) => new Cno__subscribe(init, parent);
				case 'yes': return (init:Tyes__subscribe, parent:Copen_views) => new Cyes__subscribe(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Topen_views['subscribe'], parent:Copen_views) {
			super(data, parent, 'subscribe');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'subscribe'`; }
	}
	export class Dview_context extends Cclient_id_path {
		constructor(data:Topen_views['view context'], parent:Copen_views) {
			super(data, parent, {
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.id_path_location).result!),
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.root).result!)
			}, 'view context')
		}
	}
	export class Dview_definition extends Reference<views.Cviews__,string> {

		constructor(data:string, $this:Copen_views) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.view_definition_context?.ref)
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view definition'`; }
	}
	export class Dview_definition_context extends Reference<views.Cviews,string> {

		constructor(data:string, $this:Copen_views) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this.root.input.views[this.entry]).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'view definition context'`; }
	}
}
export namespace Cyes__action_active {
	export class Dcan_finish_step extends StateGroup<
		{ name: 'no', node:Cno__can_finish_step, init:Tno__can_finish_step}|
		{ name: 'yes', node:Cyes__can_finish_step, init:Tyes__can_finish_step}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__can_finish_step, parent:Cyes__action_active) => new Cno__can_finish_step(init, parent);
				case 'yes': return (init:Tyes__can_finish_step, parent:Cyes__action_active) => new Cyes__can_finish_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__action_active['can finish step'], parent:Cyes__action_active) {
			super(data, parent, 'can finish step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'can finish step'`; }
	}
	export class Dlast_step extends StateGroup<
		{ name: 'no', node:Cno__last_step, init:Tno__last_step}|
		{ name: 'yes', node:Cyes__last_step, init:Tyes__last_step}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__last_step, parent:Cyes__action_active) => new Cno__last_step(init, parent);
				case 'yes': return (init:Tyes__last_step, parent:Cyes__action_active) => new Cyes__last_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__action_active['last step'], parent:Cyes__action_active) {
			super(data, parent, 'last step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'last step'`; }
	}
}
export namespace Cno__is_entry_view {
}
export namespace Cyes__is_entry_view {
}

export namespace Cclient_state {
	export function create(init:Tclient_state, input: {
	'application':application.Capplication
	'client_bindings':client_bindings.Cclient_bindings
	'gui_model':gui_model.Cgui_model
	'languages':{[name:string]:translations.Ctranslations}
	'reports':{[name:string]:parameters.Cparameters}
	'settings':settings.Csettings
	'views':{[name:string]:views.Cviews}
	'widget':{[name:string]:widget.Cwidget}}, symbols_file?: SymbolsFile):Cclient_state {
		return new Cclient_state(init, input as any, symbols_file);
	};
}
